{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024                                      }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGridData;

interface

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF CMNLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF WEBLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}

{$IFDEF FMXLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}
{$IFDEF VCLLIB}
{$DEFINE SUPPORTABSTRACT}
{$ENDIF}

uses
  Classes, SysUtils, WEBLib.TMSFNCTypes, WEBLib.TMSFNCCustomComponent, WEBLib.TMSFNCDataGridCore,
  WEBLib.TMSFNCDataGridCell, WEBLib.TMSFNCPersistence, WEBLib.TMSFNCJSONReader, WEBLib.TMSFNCJSONWriter
  {$IFDEF FMXLIB}
  ,FMX.Types
  {$ENDIF}
  ,Generics.Collections, Rtti, Generics.Defaults, Types
  {$IFNDEF WEBLIB}
  ,UITypes
  {$ENDIF}
  {$IFNDEF WEBLIB}
  ,JSON
  {$ENDIF}
  {$IFDEF WEBLIB}
  ,WEBLIB.JSON
  {$ENDIF}
  {$IFDEF SUPPORTABSTRACT}
  ,TMS.TMSFNCDataSet, Data.DB
  {$ENDIF}
  ;

resourcestring
  sTMSFNCDataGridFilterTypeEmpty = 'Empty';
  sTMSFNCDataGridFilterTypeNotEmpty = 'Not Empty';
  sTMSFNCDataGridFilterTypeStartsWith = 'Starts with';
  sTMSFNCDataGridFilterTypeEndsWith = 'Ends with';
  sTMSFNCDataGridFilterTypeContains = 'Contains';
  sTMSFNCDataGridFilterTypeNotContains = 'Does not contain';
  sTMSFNCDataGridFilterTypeEqual = 'Equals';
  sTMSFNCDataGridFilterTypeNotEqual = 'Does not equal';
  sTMSFNCDataGridFilterTypeLargerThan = 'Larger than';
  sTMSFNCDataGridFilterTypeSmallerThan = 'Smaller than';
  sTMSFNCDataGridFilterTypeLargerOrEqualThan = 'Larger than or equal to';
  sTMSFNCDataGridFilterTypeSmallerOrEqualThan = 'Smaller than or equal to';

type
  TTMSFNCDataGridData = class;

  TTMSFNCDataGridDataDictionary = class(TObjectDictionary<Integer, TTMSFNCDataGridRowItem>);

  TTMSFNCDataGridDataGroupDictionary = class;

  TTMSFNCDataGridDataGroup = class(TPersistent)
  private
    FRow: Integer;
    FSummaryRow: Integer;
    FGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
    function GetGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
  public
    destructor Destroy; override;
  published
    property Row: Integer read FRow write FRow;
    property SummaryRow: Integer read FSummaryRow write FSummaryRow;
    property GroupDictionary: TTMSFNCDataGridDataGroupDictionary read GetGroupDictionary;
  end;

  TTMSFNCDataGridDataFormattingType = (gdftDefault, gdftBoolean, gdftDateTime, gdftDate, gdftTime, gdftFloat, gdftNumber);

  TTMSFNCDataGridDataFormatting = record
    Format: string;
    &Type: TTMSFNCDataGridDataFormattingType;
    BooleanTrueText: string;
    BooleanFalseText: string;
  end;

  TTMSFNCDataGridDataGroupDictionary = class(TObjectDictionary<string, TTMSFNCDataGridDataGroup>);

  TTMSFNCDataGridDataSortOptions = record
    FormatSettings: TFormatSettings;
    Format: TTMSFNCDataGridSortFormat;
    Direction: TTMSFNCDataGridSortDirection;
    CaseSensitive: Boolean;
  end;

  TTMSFNCDataGridDataGroupOptions = record
    Summary: Boolean;
    SummaryInHeader: Boolean;
    MergeSummary: Boolean;
    MergeHeader: Boolean;
    MergeDelimiter: string;
    HideColumns: Boolean;
    AutoSort: Boolean;
    UseFirstRowAsHeader: Boolean;
    HeaderHeight: Double;
    SummaryHeight: Double;
  end;

  TTMSFNCDataGridDataColumnCalculationOptions = record
    MergeSummary: Boolean;
    SummaryInHeader: Boolean;
    CalculationFormat: string;
  end;

  TTMSFNCDataGridDataRowCalculationOptions = record
    CalculationFormat: string;
  end;

  TTMSFNCDataGridDataInputOutputOptions = record
    Delimiter: Char;
    StartColumn: Integer;
    StartRow: Integer;
    AlwaysQuotes: Boolean;
    QuoteEmptyCells: Boolean;
    MaxRows: Integer;
    TrimSpaces: Boolean;
  end;

  TTMSFNCDataGridDataGetCustomGroupCallback = reference to procedure(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; ALevel: Integer; var AGroup: string);
  TTMSFNCDataGridDataGetSortOptionsCallback = reference to procedure(AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataSortOptions);
  TTMSFNCDataGridDataGetGroupOptionsCallback = reference to procedure(AColumns: TArray<Integer>; ALevel: Integer; var AOptions: TTMSFNCDataGridDataGroupOptions);
  TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = reference to procedure(AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataColumnCalculationOptions);
  TTMSFNCDataGridDataGetRowCalculationOptionsCallback = reference to procedure(ARow: Integer; var AOptions: TTMSFNCDataGridDataRowCalculationOptions);
  TTMSFNCDataGridDataCustomColumnCalculationCallback = reference to function(AColumn, ALevel, AFromRow, AToRow: Integer): Double;
  TTMSFNCDataGridDataCustomRowCalculationCallback = reference to function(ARow, AFromColumn, AToColumn: Integer): Double;
  TTMSFNCDataGridDataCustomCompareCallback = reference to function(AColumn, ARow1, ARow2: Integer; AData1, AData2: TTMSFNCDataGridCellValue; ALevel: Integer; var AHandled: Boolean): Integer;
  TTMSFNCDataGridDataCustomCompareEvent = function(Sender: TObject; AColumn, ARow1, ARow2: Integer; AData1, AData2: TTMSFNCDataGridCellValue; ALevel: Integer): Integer of object;
  TTMSFNCDataGridDataCustomColumnCalculationEvent = function(Sender: TObject; AColumn, ALevel: Integer; AFromRow, AToRow: Integer): Double of object;
  TTMSFNCDataGridDataCustomRowCalculationEvent = function(Sender: TObject; ARow: Integer; AFromColumn, AToColumn: Integer): Double of object;
  TTMSFNCDataGridDataGetGroupOptionsEvent = procedure(Sender: TObject; AColumns: TArray<Integer>; ALevel: Integer; var AOptions: TTMSFNCDataGridDataGroupOptions) of object;
  TTMSFNCDataGridDataGetColumnCalculationOptionsEvent = procedure(Sender: TObject; AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataColumnCalculationOptions) of object;
  TTMSFNCDataGridDataGetRowCalculationOptionsEvent = procedure(Sender: TObject; ARow: Integer; var AOptions: TTMSFNCDataGridDataRowCalculationOptions) of object;
  TTMSFNCDataGridDataGetSortOptionsEvent = procedure(Sender: TObject; AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataSortOptions) of object;
  TTMSFNCDataGridDataGetCustomGroupEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; ALevel: Integer; var AGroup: string) of object;
  TTMSFNCDataGridDataGetCellDataEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AData: TTMSFNCDataGridCellValue) of object;
  TTMSFNCDataGridDataGetCellDataPropertiesEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem) of object;
  TTMSFNCDataGridDataGetCellDisplayValueEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AValue: string) of object;
  TTMSFNCDataGridDataGetCellFormattingEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean) of object;
  TTMSFNCDataGridDataRowCollapseEvent = procedure(Sender: TObject; ARow: Integer) of object;
  TTMSFNCDataGridDataRowExpandEvent = procedure(Sender: TObject; ARow: Integer) of object;

  TTMSFNCDataGridDataSwapDirection = (gswdBoth, gswdFromOnly, gswdToOnly);

  TTMSFNCDataGridDataSortSwapMethod<T> = reference to procedure(var Values: TArray<T>; L, R: Integer);

  TTMSFNCDataGridDataSortCompareMethod<T> = reference to function(const V1, V2: T; L, R: Integer): Integer;

  TTMSFNCDataGridDataCellMergeType = (gcmtInvalid, gcmtNormal, gcmtFixed, gcmtFreeze, gcmtFixedRoot, gcmtFreezeRoot);

  TTMSFNCDataGridDataFileStringList = class(TStringList)
  private
    fp: Integer;
    cache: string;
    function GetEOF: boolean;
  public
    procedure Reset;
    procedure ReadLn(var s: string);
    procedure Write(s: string);
    procedure WriteLn(s: string);
    property Eof: boolean read GetEOF;
  end;

  TTMSFNCDataGridDataCalculationMethod = (gcmSum, gcmAverage, gcmMin, gcmMax, gcmCount, gcmCustom, gcmDistinct, gcmStandardDeviation);

  TTMSFNCDataGridDataColumnCalculationType = (gctNormal, gctGroup);

  TTMSFNCDataGridDataColumnCalculation = record
    &Type: TTMSFNCDataGridDataColumnCalculationType;
    Method: TTMSFNCDataGridDataCalculationMethod;
    OptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
    CustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback;
    FromRow, ToRow: Integer;
    OutputRow: Integer;
  end;

  TTMSFNCDataGridDataRowCalculation = record
    Method: TTMSFNCDataGridDataCalculationMethod;
    OptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
    CustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback;
    FromColumn, ToColumn: Integer;
    OutputColumn: Integer;
  end;

  TTMSFNCDataGridDataColumnCalculationArray = array of TTMSFNCDataGridDataColumnCalculation;

  TTMSFNCDataGridDataColumnCalculationItemDictionary = TDictionary<string, TTMSFNCDataGridDataColumnCalculationArray>;
  TTMSFNCDataGridDataColumnCalculationDictionary = class(TObjectDictionary<Integer, TTMSFNCDataGridDataColumnCalculationItemDictionary>);

  TTMSFNCDataGridDataRowCalculationArray = array of TTMSFNCDataGridDataRowCalculation;

  TTMSFNCDataGridDataRowCalculationItemDictionary = TDictionary<string, TTMSFNCDataGridDataRowCalculationArray>;
  TTMSFNCDataGridDataRowCalculationDictionary = class(TObjectDictionary<Integer, TTMSFNCDataGridDataRowCalculationItemDictionary>);

  TTMSFNCDataGridDataSortCellInfo = record
    Data: TTMSFNCDataGridCellValue;
    Column: Integer;
    Options: TTMSFNCDataGridDataSortOptions;
  end;

  TTMSFNCDataGridDataSortInfo = record
    Row: Integer;
    Data: TArray<TTMSFNCDataGridDataSortCellInfo>;
  end;

  TTMSFNCDataGridDataGroupInfo = record
    Columns: TArray<Integer>;
    SubColumns: TArray<TTMSFNCDataGridDataGroupInfo>;
    SortColumns: TArray<Integer>;
    SortDirections: TArray<TTMSFNCDataGridSortDirection>;
  end;

  TTMSFNCDataGridDataFilterCells = (gfcNormal, gfcStripHTML, gfcRow);

  TTMSFNCDataGridDataFilterOperation = (gfoSHORT, gfoNONE, gfoAND, gfoXOR, gfoOR);

  TTMSFNCDataGridDataFilterMethod = (gfmExpression, gfmLiteral);

  TTMSFNCDataGridDataFilterType = (gftStartsWith, gftEndsWith, gftContains, gftNotContains, gftEqual, gftNotEqual, gftEmpty, gftNotEmpty, gftLargerThan,
    gftSmallerThan, gftLargerOrEqualThan, gftSmallerOrEqualThan);

  TTMSFNCDataGridDataFilterTextType = gftStartsWith..gftNotEmpty;

  TTMSFNCDataGridDataFilterNumberType = gftEqual..gftSmallerOrEqualThan;

  TTMSFNCDataGridDataSearchType = (gstStartsWith, gstEndsWith, gstContains, gstEqual, gstNotEqual, gstNotContains);

  TTMSFNCDataGridDataStringArray = array of string;

  TTMSFNCDataGridDataFilterData = class(TCollectionItem)
  private
    FColumn: Integer;
    FCondition: string;
    FCaseSensitive: Boolean;
    FSuffix: string;
    FPrefix: string;
    FOperation: TTMSFNCDataGridDataFilterOperation;
    FData: TTMSFNCDataGridDataFilterCells;
    FMethod: TTMSFNCDataGridDataFilterMethod;
    FType: TTMSFNCDataGridDataFilterType;
    FEnabled: Boolean;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
  public
    constructor Create(ACollection: TCollection); override;
    procedure Assign(Source: TPersistent); override;
    procedure BuildCondition(s: string);
    function StripCondition: string;
    function &And(AColumn: Integer; ACondition: string): TTMSFNCDataGridDataFilterData; overload;
    function &And(AColumn: Integer; AType: TTMSFNCDataGridDataFilterType; ACondition: string): TTMSFNCDataGridDataFilterData; overload;
    function &Or(AColumn: Integer; ACondition: string): TTMSFNCDataGridDataFilterData; overload;
    function &Or(AColumn: Integer; AType: TTMSFNCDataGridDataFilterType; ACondition: string): TTMSFNCDataGridDataFilterData; overload;
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
  published
    property CaseSensitive: Boolean read FCaseSensitive write FCaseSensitive default False;
    property Column: Integer read FColumn write FColumn;
    property Condition: string read FCondition write FCondition;
    property &Type: TTMSFNCDataGridDataFilterType read FType write FType default gftEqual;
    property Data: TTMSFNCDataGridDataFilterCells read FData write FData default gfcNormal;
    property Enabled: Boolean read FEnabled write FEnabled default True;
    property Method: TTMSFNCDataGridDataFilterMethod read FMethod write FMethod;
    property Operation: TTMSFNCDataGridDataFilterOperation read FOperation write FOperation;
    property Prefix: string read FPrefix write FPrefix;
    property Suffix: string read FSuffix write FSuffix;
  end;

  TTMSFNCDataGridDataFilterItem = class
  private
    FDoubleQuotes: Boolean;
    FPartOfCheckAll: Boolean;
  public
    constructor Create; virtual;
    property DoubleQuotes: Boolean read FDoubleQuotes;
    property PartOfCheckAll: Boolean read FPartOfCheckAll;
  end;

  TTMSFNCDataGridDataFilterCommonItem = class(TTMSFNCDataGridDataFilterItem)
  public
    constructor Create; override;
  end;

  TTMSFNCDataGridDataFilterUniqueItem = class(TTMSFNCDataGridDataFilterItem)
  public
    constructor Create; override;
  end;

  TTMSFNCDataGridDataFilterCommonDoubleQuotedItem = class(TTMSFNCDataGridDataFilterItem)
  public
    constructor Create; override;
  end;

  TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem = class(TTMSFNCDataGridDataFilterItem)
  public
    constructor Create; override;
  end;

  TTMSFNCDataGridDataFilter = class(TTMSFNCOwnedCollection{$IFNDEF WEBLIB}<TTMSFNCDataGridDataFilterData>{$ENDIF})
  private
    FUniqueItem: TTMSFNCDataGridDataFilterUniqueItem;
    FCommonItem: TTMSFNCDataGridDataFilterCommonItem;
    FUniqueDoubleQuotedItem: TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem;
    FCommonDoubleQuotedItem: TTMSFNCDataGridDataFilterCommonDoubleQuotedItem;
    function GetItem(Index: Integer): TTMSFNCDataGridDataFilterData;
    procedure SetItem(Index: Integer; const Value: TTMSFNCDataGridDataFilterData);
    function GetColumnFilter(AColumn: Integer): TTMSFNCDataGridDataFilterData;
    function GetCommonItem: TTMSFNCDataGridDataFilterCommonItem;
    function GetUniqueItem: TTMSFNCDataGridDataFilterUniqueItem;
    function GetCommonDoubleQuotedItem: TTMSFNCDataGridDataFilterCommonDoubleQuotedItem;
    function GetUniqueDoubleQuotedItem: TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem;
  public
    destructor Destroy; override;
    function First: TTMSFNCDataGridDataFilterData;
    function Last: TTMSFNCDataGridDataFilterData;
    function Add(AColumn: Integer; ACondition: string; AOperation: TTMSFNCDataGridDataFilterOperation = gfoNone): TTMSFNCDataGridDataFilterData; overload;
    function Add(AColumn: Integer; AType: TTMSFNCDataGridDataFilterType; ACondition: string; AOperation: TTMSFNCDataGridDataFilterOperation = gfoNone): TTMSFNCDataGridDataFilterData; overload;
    function Add: TTMSFNCDataGridDataFilterData; overload;
    function HasFilter(AColumn: integer): Boolean;
    function Insert(AIndex: Integer): TTMSFNCDataGridDataFilterData;
    procedure Parse(AColumn: Integer; AFilterText: string);
    procedure RemoveColumnFilter(AColumn: integer);
    property ColumnFilter[AColumn: Integer]: TTMSFNCDataGridDataFilterData read GetColumnFilter;
    property Items[Index: Integer]: TTMSFNCDataGridDataFilterData read GetItem write SetItem; default;
    property CommonItem: TTMSFNCDataGridDataFilterCommonItem read GetCommonItem;
    property UniqueItem: TTMSFNCDataGridDataFilterUniqueItem read GetUniqueItem;
    property CommonDoubleQuotedItem: TTMSFNCDataGridDataFilterCommonDoubleQuotedItem read GetCommonDoubleQuotedItem;
    property UniqueDoubleQuotedItem: TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem read GetUniqueDoubleQuotedItem;
  end;

  TTMSFNCDataGridDataFindParameter = (gfnMatchCase, gfnMatchFull, gfnMatchRegular, gfnDirectionLeftRight,
    gfnMatchStart, gfnFindInCurrentRow, gfnFindInCurrentColumn, gfnIncludeFixed, gfnAutoGoto,
    gfnIgnoreHTMLTags, gfnBackward, gfnIncludeHiddenColumns, gfnFindInPresetColumn, gfnFindInPresetRow,
    gfnSelectedCells, gfnIncludeHiddenRows);

  TTMSFNCDataGridDataFindParameters = set of TTMSFNCDataGridDataFindParameter;

  TTMSFNCDataGridDataIOOptions = class(TPersistent)
  private
    FDelimiter: Char;
    FMaxRows: Integer;
    FStartRow: Integer;
    FStartColumn: Integer;
    FQuoteEmptyCells: Boolean;
    FAlwaysQuotes: Boolean;
    FOpenAfterCreation: Boolean;
    FTrimSpaces: Boolean;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
  published
    property AlwaysQuotes: Boolean read FAlwaysQuotes write FAlwaysQuotes default False;
    property Delimiter: Char read FDelimiter write FDelimiter default #0;
    property MaxRows: Integer read FMaxRows write FMaxRows default -1;
    property OpenAfterCreation: Boolean read FOpenAfterCreation write FOpenAfterCreation default True;
    property QuoteEmptyCells: Boolean read FQuoteEmptyCells write FQuoteEmptyCells default True;
    property StartColumn: Integer read FStartColumn write FStartColumn default 0;
    property StartRow: Integer read FStartRow write FStartRow default 0;
    property TrimSpaces: Boolean read FTrimSpaces write FTrimSpaces default False;
  end;

  TTMSFNCDataGridDataURLOptions = class(TPersistent)
  private
    FAutoDetect: Boolean;
    FAutoOpen: Boolean;
    FOnChangeRebuild: TNotifyEvent;
    procedure SetAutoDetect(const Value: Boolean);
  protected
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
  published
    property AutoOpen: Boolean read FAutoOpen write FAutoOpen default True;
    property AutoDetect: Boolean read FAutoDetect write SetAutoDetect default False;
  end;

  TTMSFNCDataGridDataIOOptionsClass = class of TTMSFNCDataGridDataIOOptions;

  TTMSFNCDataGridDataSortingOptions = class(TPersistent)
  private
    FDirection: TTMSFNCDataGridSortDirection;
    FCaseSensitive: Boolean;
    FFormat: TTMSFNCDataGridSortFormat;
    FRow: Integer;
    FEnabled: Boolean;
    FOnChangeRebuild: TNotifyEvent;
    procedure SetEnabled(const Value: Boolean);
  protected
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property CaseSensitive: Boolean read FCaseSensitive write FCaseSensitive default True;
    property Direction: TTMSFNCDataGridSortDirection read FDirection write FDirection default gsdAscending;
    property Enabled: Boolean read FEnabled write SetEnabled default False;
    property Format: TTMSFNCDataGridSortFormat read FFormat write FFormat default gsfAutomatic;
    property Row: Integer read FRow write FRow default 0;
  end;

  TTMSFNCDataGridDataGroupingOptions = class(TPersistent)
  private
    FSummary: Boolean;
    FMergeHeader: Boolean;
    FMergeSummary: Boolean;
    FSummaryInHeader: Boolean;
    FHideColumns: Boolean;
    FCalculationFormat: string;
    FAutoSort: Boolean;
    FUseFirstRowAsHeader: Boolean;
    FMergeDelimiter: string;
    FHeaderHeight: Double;
    FSummaryHeight: Double;
    function IsHeaderHeightStored: Boolean;
    function IsSummaryHeightStored: Boolean;
    function IsCalculationFormatStored: Boolean;
    function IsMergeDelimiterStored: Boolean;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property AutoSort: Boolean read FAutoSort write FAutoSort default True;
    property CalculationFormat: string read FCalculationFormat write FCalculationFormat stored IsCalculationFormatStored nodefault;
    property HeaderHeight: Double read FHeaderHeight write FHeaderHeight stored IsHeaderHeightStored nodefault;
    property HideColumns: Boolean read FHideColumns write FHideColumns default False;
    property MergeDelimiter: string read FMergeDelimiter write FMergeDelimiter stored IsMergeDelimiterStored nodefault;
    property MergeHeader: Boolean read FMergeHeader write FMergeHeader default True;
    property MergeSummary: Boolean read FMergeSummary write FMergeSummary default False;
    property Summary: Boolean read FSummary write FSummary default True;
    property SummaryHeight: Double read FSummaryHeight write FSummaryHeight stored IsSummaryHeightStored nodefault;
    property SummaryInHeader: Boolean read FSummaryInHeader write FSummaryInHeader default False;
    property UseFirstRowAsHeader: Boolean read FUseFirstRowAsHeader write FUseFirstRowAsHeader default True;
  end;

  TTMSFNCDataGridDataCalculationOptions = class(TPersistent)
  private
    FIncludeHiddenRows: Boolean;
    FIncludeHiddenColumns: Boolean;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property IncludeHiddenRows: Boolean read FIncludeHiddenRows write FIncludeHiddenRows default True;
    property IncludeHiddenColumns: Boolean read FIncludeHiddenColumns write FIncludeHiddenColumns default True;
  end;

  TTMSFNCDataGridDataFilterRows = (gfrNormal, gfrAll);

  TTMSFNCDataGridDataFilterControl = (gfcButton, gfcEditor);

  TTMSFNCDataGridDataFilterControls = set of TTMSFNCDataGridDataFilterControl;

  TTMSFNCDataGridDataFilteringOptions = class(TPersistent)
  private
    FMultiColumn: Boolean;
    FEnabled: Boolean;
    FDropDownHeight: Integer;
    FRows: TTMSFNCDataGridDataFilterRows;
    FOnChangeRebuild: TNotifyEvent;
    FRow: Integer;
    FDropDownWidth: Integer;
    FHideEmptyGroups: Boolean;
    FControls: TTMSFNCDataGridDataFilterControls;
    procedure SetEnabled(const Value: Boolean);
    procedure SetRow(const Value: Integer);
    procedure SetControls(const Value: TTMSFNCDataGridDataFilterControls);
  protected
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property DropDownHeight: Integer read FDropDownHeight write FDropDownHeight default 280;
    property DropDownWidth: Integer read FDropDownWidth write FDropDownWidth default 200;
    property Enabled: Boolean read FEnabled write SetEnabled default False;
    property Controls: TTMSFNCDataGridDataFilterControls read FControls write SetControls default [gfcButton];
    property MultiColumn: Boolean read FMultiColumn write FMultiColumn default false;
    property Row: Integer read FRow write SetRow default 0;
    property Rows: TTMSFNCDataGridDataFilterRows read FRows write FRows default gfrNormal;
    property HideEmptyGroups: Boolean read FHideEmptyGroups write FHideEmptyGroups default False;
  end;

  TTMSFNCDataGridDataFindOptions = class(TPersistent)
  private
    FRow: Integer;
    FColumn: Integer;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Column: Integer read FColumn write FColumn default 0;
    property Row: Integer read FRow write FRow default 0;
  end;

  TTMSFNCDataGridDataOptions = class(TPersistent)
  private
    FSorting: TTMSFNCDataGridDataSortingOptions;
    FIO: TTMSFNCDataGridDataIOOptions;
    FGrouping: TTMSFNCDataGridDataGroupingOptions;
    FFiltering: TTMSFNCDataGridDataFilteringOptions;
    FFind: TTMSFNCDataGridDataFindOptions;
    FOnChangeRebuild: TNotifyEvent;
    FOnChange: TNotifyEvent;
    FCalculations: TTMSFNCDataGridDataCalculationOptions;
    FURL: TTMSFNCDataGridDataURLOptions;
    procedure SetIO(const Value: TTMSFNCDataGridDataIOOptions);
    procedure SetSorting(const Value: TTMSFNCDataGridDataSortingOptions);
    procedure SetGrouping(const Value: TTMSFNCDataGridDataGroupingOptions);
    procedure SetFiltering(const Value: TTMSFNCDataGridDataFilteringOptions);
    procedure SetFind(const Value: TTMSFNCDataGridDataFindOptions);
    procedure SetCalculations(
      const Value: TTMSFNCDataGridDataCalculationOptions);
    procedure SetURL(const Value: TTMSFNCDataGridDataURLOptions);
  protected
    function GetIOOptionsClass: TTMSFNCDataGridDataIOOptionsClass; virtual;
    procedure DoFilteringOptionsChange(Sender: TObject);
    procedure DoSortingOptionsChange(Sender: TObject);
    procedure DoURLOptionsChange(Sender: TObject);
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property Filtering: TTMSFNCDataGridDataFilteringOptions read FFiltering write SetFiltering;
    property Find: TTMSFNCDataGridDataFindOptions read FFind write SetFind;
    property Grouping: TTMSFNCDataGridDataGroupingOptions read FGrouping write SetGrouping;
    property Calculations: TTMSFNCDataGridDataCalculationOptions read FCalculations write SetCalculations;
    property IO: TTMSFNCDataGridDataIOOptions read FIO write SetIO;
    property Sorting: TTMSFNCDataGridDataSortingOptions read FSorting write SetSorting;
    property URL: TTMSFNCDataGridDataURLOptions read FURL write SetURL;
  end;

  TTMSFNCDataGridDataOptionsClass = class of TTMSFNCDataGridDataOptions;

  TTMSFNCDataGridDataAdapterGroupMode = (agmNone, agmNormal, agmAdapter);

  TTMSFNCDataGridDataAdapterSortMode = (asmNone, asmNormal, asmAdapter);

  TTMSFNCDataGridDataAdapterFilterMode = (afmNone, afmNormal, afmAdapter);

  TTMSFNCDataGridDataAdapter = class(TTMSFNCCustomComponent)
  private
    FDataInstance: TTMSFNCDataGridData;
    FActive: Boolean;
    FBlockAdd: Boolean;
    procedure SetActive(const Value: Boolean);
    function GetDataInstance: TTMSFNCDataGridData;
    procedure SetDataInstance(const Value: TTMSFNCDataGridData);
  protected
    function GetInstance: NativeUInt; override;
    function NeedsItemExtended(ACell: TTMSFNCDataGridCellCoord): Boolean; virtual; abstract;
    function GroupMode: TTMSFNCDataGridDataAdapterGroupMode; virtual; abstract;
    function SortMode: TTMSFNCDataGridDataAdapterSortMode; virtual; abstract;
    function FilterMode: TTMSFNCDataGridDataAdapterFilterMode; virtual; abstract;
    procedure SortData; virtual; abstract;
    procedure ApplyFilter; virtual; abstract;
    procedure ClearFilter; virtual; abstract;
    procedure RowCollapse(ARow: Integer); virtual; abstract;
    procedure RowExpand(ARow: Integer); virtual; abstract;
    procedure GetCellFormatting(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean); virtual; abstract;
    procedure GetCellDataProperties(ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem); virtual; abstract;
    procedure GetCellData(ACell: TTMSFNCDataGridCellCoord; var ACellData: TTMSFNCDataGridCellValue); virtual; abstract;
    procedure SetCellData(ACell: TTMSFNCDataGridCellCoord; ACellData: TTMSFNCDataGridCellValue); virtual; abstract;
    procedure Initialize; virtual; abstract;
  public
    constructor Create(AOwner: TComponent); overload; override;
    constructor Create; reintroduce; overload;
    destructor Destroy; override;
    property DataInstance: TTMSFNCDataGridData read GetDataInstance write SetDataInstance;
  published
    property Active: Boolean read FActive write SetActive default True;
  end;

  TTMSFNCDataGridDataCellDataEvent = procedure(Sender: TObject; AColumn, ARow: Integer; var AValue: TTMSFNCDataGridCellValue) of object;

  TTMSFNCDataGridSortIndexDictionary = class(TDictionary<Integer, TTMSFNCDataGridSortDirection>);

  {$IFDEF SUPPORTABSTRACT}
  TTMSFNCDataGridDataObjectIndexDictionary = class(TDictionary<string, Integer>);
  {$ENDIF}

  TTMSFNCDataGridDataJSONNamesArray = array of string;

  TTMSFNCDataGridDataTempData = class
  private
    FCell: TTMSFNCDataGridCellCoord;
    FData: TTMSFNCDataGridCellItem;
  public
    constructor Create(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellItem);
    destructor Destroy; override;
    property Data: TTMSFNCDataGridCellItem read FData;
    property Cell: TTMSFNCDataGridCellCoord read FCell;
  end;

  TTMSFNCDataGridDataTempDataList = class(TObjectList<TTMSFNCDataGridDataTempData>)
  public
    function Find(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellItem;
  end;

  TTMSFNCDataGridData = class(TTMSFNCDataGridCore, ITMSFNCCustomReadWriteIO{$IFDEF SUPPORTABSTRACT}, ITMSFNCDataObject{$ENDIF})
  private
    FTempDataList: TTMSFNCDataGridDataTempDataList;
    FFilterApplied: Boolean;
    {$IFDEF SUPPORTABSTRACT}
    FPosting: Boolean;
    FNotifyObject: ITMSFNCDataObjectNotification;
    FFieldIndexDictionary: TTMSFNCDataGridDataObjectIndexDictionary;
    {$ENDIF}
    FSortIndexDictionary: TTMSFNCDataGridSortIndexDictionary;
    FCellDataDictionary: TTMSFNCDataGridDataDictionary;
    FColumnCalculationDictionary: TTMSFNCDataGridDataColumnCalculationDictionary;
    FRowCalculationDictionary: TTMSFNCDataGridDataRowCalculationDictionary;
    FExpandAll: Integer;
    FFilter: TTMSFNCDataGridDataFilter;
    FFindCache: string;
    FFindCell: TTMSFNCDataGridCellCoord;
    FFindParameters: TTMSFNCDataGridDataFindParameters;
    FGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
    FOnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent;
    FOnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent;
    FOnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent;
    FOnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent;
    FOnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent;
    FOnGetCellData: TTMSFNCDataGridDataGetCellDataEvent;
    FOnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent;
    FOnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent;
    FOnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent;
    FOnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent;
    FOptions: TTMSFNCDataGridDataOptions;
    FUpdateGrouping: Integer;
    FAdapter: TTMSFNCDataGridDataAdapter;
    FBlockAdd: Boolean;
    FOnLoadCellData: TTMSFNCDataGridDataCellDataEvent;
    FOnSaveCellData: TTMSFNCDataGridDataCellDataEvent;
    FOnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent;
    FOnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent;
    FOnRowExpand: TTMSFNCDataGridDataRowExpandEvent;
    FOnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent;
    FGroupInfo: TTMSFNCDataGridDataGroupInfo;
    function GetCellData(AColumn, ARow: Integer): TTMSFNCDataGridCellItem;
    function GetCellDataDictionary: TTMSFNCDataGridDataDictionary;
    function GetCellDataExtended(AColumn, ARow: Integer): TTMSFNCDataGridCellItemExtended;
    function GetCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetColumnCalculationDictionary: TTMSFNCDataGridDataColumnCalculationDictionary;
    function GetColumnCalculation(AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
    function GetRowCalculationDictionary: TTMSFNCDataGridDataRowCalculationDictionary;
    function GetRowCalculation(ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
    function GetControls(AColumn, ARow: Integer): TTMSFNCDataGridCellControl;
    function GetFilter: TTMSFNCDataGridDataFilter;
    function GetFloats(AColumn, ARow: Integer): Double;
    function GetGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
    function GetHasCellData(AColumn, ARow: Integer): Boolean;
    function GetHasMergeInfo(AColumn, ARow: Integer): Boolean;
    function GetInts(AColumn, ARow: Integer): Int64;
    function GetManagedObjects(AColumn, ARow: Integer): TObject;
    function GetMergeInfo(AColumn, ARow: Integer): TTMSFNCDataGridCellMergeInfo;
    function GetObjects(AColumn, ARow: Integer): TObject;
    function GetRowNodeChildCount(ARow: Integer): Integer;
    function GetRowNodeLevel(ARow: Integer): Integer;
    function GetRowNodeState(ARow: Integer): TTMSFNCDataGridRowNodeState;
    function GetRowText(ARow: Integer): string;
    function GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
    function GetSortIndexDictionary: TTMSFNCDataGridSortIndexDictionary;
    function GetStoredCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
    function GetStrings(AColumn, ARow: Integer): string;
    function GetStrippedCells(AColumn, ARow: Integer): string;
    {$IFDEF SUPPORTABSTRACT}
    function GetFieldIndexDictionary: TTMSFNCDataGridDataObjectIndexDictionary;
    {$ENDIF}
    procedure SetAdapter(const Value: TTMSFNCDataGridDataAdapter);
    procedure SetCells(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellValue);
    procedure SetRowCalculation(ARow: Integer; AName: string; const Value: TTMSFNCDataGridDataRowCalculationArray);
    procedure SetColumnCalculation(AColumn: Integer; AName: string; const Value: TTMSFNCDataGridDataColumnCalculationArray);
    procedure SetControls(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellControl);
    procedure SetManagedObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetMergeInfo(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellMergeInfo);
    procedure SetObjects(AColumn, ARow: Integer; const Value: TObject);
    procedure SetOptions(const Value: TTMSFNCDataGridDataOptions);
    procedure SetRowNodeChildCount(ARow: Integer; const Value: Integer);
    procedure SetRowNodeLevel(ARow: Integer; const Value: Integer);
    procedure SetRowNodeState(ARow: Integer; const Value: TTMSFNCDataGridRowNodeState);
    procedure SetRowText(ARow: Integer; const Value: string);
    procedure SetRowTypes(ARow: Integer; const Value: TTMSFNCDataGridRowType);
    procedure SetFloats(AColumn, ARow: Integer; const Value: Double);
    procedure SetInts(AColumn, ARow: Integer; const Value: Int64);
    function GetHasColumnCalculation(AColumn: Integer; AName: string): Boolean;
    function GetHasRowCalculation(ARow: Integer; AName: string): Boolean;
    procedure SetStrings(AColumn, ARow: Integer; const Value: string);
    function GetTempDataList: TTMSFNCDataGridDataTempDataList;
  protected
    function CompareRows(AColumn: Integer; ALevel: Integer; V1, V2: TTMSFNCDataGridDataSortInfo; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback): Integer;
    function CreateCellDataForRow(ARowData: TTMSFNCDataGridRowItem; AColumn, ARow: Integer; AExtended: Boolean): TTMSFNCDataGridCellItem;
    function CreateRowData(ARow: Integer): TTMSFNCDataGridRowItem;
    function DoCustomColumnCalculation(AColumn: Integer; ALevel: Integer; AFromRow, AToRow: Integer): Double; virtual;
    function DoCustomRowCalculation(ARow: Integer; AFromColumn, AToColumn: Integer): Double; virtual;
    function DoCustomCompare(AColumn: Integer; ARow1, ARow2: Integer; AData1, AData2: TTMSFNCDataGridCellValue; ALevel: Integer): Integer; virtual;
    function FetchData(ACellData: TTMSFNCDataGridCellItem; ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellValue;
    function GetBooleans(AColumn, ARow: Integer): Boolean; virtual;
    function GetColumnCalculationOptions: TTMSFNCDataGridDataColumnCalculationOptions;
    function GetRowCalculationOptions: TTMSFNCDataGridDataRowCalculationOptions;
    function GetCellDataForRow(ARowData: TTMSFNCDataGridRowItem; AColumn: Integer; ARow: Integer; AExtended: Boolean): TTMSFNCDataGridCellItem;
    function GetGroupOptions: TTMSFNCDataGridDataGroupOptions;
    function GetIOOptions: TTMSFNCDataGridDataInputOutputOptions;
    function GetOptionsClass: TTMSFNCDataGridDataOptionsClass; virtual;
    function GetRowData(ARow: Integer): TTMSFNCDataGridRowItem;
    function GetSelection: TTMSFNCDataGridCellCoordRange; virtual;
    function GetSortOptions: TTMSFNCDataGridDataSortOptions;
    function HasMergedCells(AColumns: TArray<Integer>; AFromRow, AToRow: Integer): Boolean;
    function InternalFind(AStartCell: TTMSFNCDataGridCellCoord; AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
    function InternalGroup(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary; AGroupInfo: TTMSFNCDataGridDataGroupInfo; ALevel, AFromRow, AToRow: Integer; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback): Integer;
    function InternalColumnCustomCalculation(AColumn, ALevel: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function MatchCell(AColumn, ARow: Integer): Boolean;
    function NeedsItemExtended(ACell: TTMSFNCDataGridCellCoord): Boolean; virtual;
    function FormatDisplayValue(ACell: TTMSFNCDataGridCellCoord; AValue: TTMSFNCDataGridCellValue): string;
    procedure BeginUsingCellData(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellItem);
    procedure EndUsingCellData;
    procedure CustomWriteProperty(AObject: TObject; APropertyName: string; APropertyKind: TTypeKind; AWriter: TTMSFNCJSONWriter; var ACanWrite: Boolean); virtual;
    procedure CustomReadProperty(AObject: TObject; APropertyName: string; APropertyKind: TTypeKind; AReader: TTMSFNCJSONReader; var ACanRead: Boolean); virtual;
    procedure CreateDisplayValue(ACellData: TTMSFNCDataGridCellItem; ACell: TTMSFNCDataGridCellCoord; AValue: TTMSFNCDataGridCellValue);
    procedure DeleteCellData(ACell: TTMSFNCDataGridCellCoord);
    procedure DeleteColumnData(AColumn: Integer); virtual;
    procedure DeleteRowData(ARow: Integer); virtual;
    procedure DoGetColumnCalculationOptions(AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataColumnCalculationOptions); virtual;
    procedure DoGetRowCalculationOptions(ARow: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataRowCalculationOptions); virtual;
    procedure DoGetCellData(ACell: TTMSFNCDataGridCellCoord; var AData: TTMSFNCDataGridCellValue); virtual;
    procedure DoGetCellDataProperties(ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem); virtual;
    procedure DoGetCellDisplayValue(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AValue: string); virtual;
    procedure DoGetCellFormatting(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean); virtual;
    procedure DoGetCustomGroup(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; ALevel: Integer; var AGroup: string); virtual;
    procedure DoGetGroupOptions(AColumns: TArray<Integer>; ALevel: Integer; var AOptions: TTMSFNCDataGridDataGroupOptions); virtual;
    procedure DoGetSortOptions(AColumn: Integer; ALevel: Integer; var AOptions: TTMSFNCDataGridDataSortOptions); virtual;
    procedure DoLoadCellData(AColumn, ARow: Integer; var AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoSaveCellData(AColumn, ARow: Integer; var AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoRowExpand(ARow: Integer); virtual;
    procedure DoRowCollapse(ARow: Integer); virtual;
    procedure FillGridData(ADoFixed: Boolean; ARandom: Boolean; ARandomNumber: Integer = 100);
    procedure InternalClear; override;
    procedure InternalFilterGroups(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary);
    procedure InternalDeleteRows(ARow, ACount: Integer);
    procedure InternalRowCalculation(ARows: TArray<Integer>; AOutputColumn, AFromColumn, AToColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure InternalRowCalculation(ACalculations: TTMSFNCDataGridDataRowCalculationDictionary); overload;
    procedure InternalColumnCalculation(AColumns: TArray<Integer>; AOutputRow, AFromRow, AToRow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure InternalColumnCalculation(ACalculations: TTMSFNCDataGridDataColumnCalculationDictionary; AGroup: TTMSFNCDataGridDataGroup; ALevel: Integer); overload;
    procedure InternalGroupCalculation(AColumns: TArray<Integer>; AOutputRow, AFromRow, AToRow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure InternalGroupCalculation(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary; ACalculations: TTMSFNCDataGridDataColumnCalculationDictionary; ALevel: Integer); overload;

    procedure InternalLoadFromJSON(AFileName: string; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); virtual;
    procedure InternalLoadFromJSONStream(AStream: TStream; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); virtual;
    procedure InternalLoadFromJSONText(AText: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); virtual;

    procedure InternalLoadFromCSV(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalLoadFromCSVStream(AStream: TStream; AEncoding: TEncoding); virtual;
    procedure InternalLoadFromFile(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalLoadFromFileStream(AStream: TStream; AEncoding: TEncoding); virtual;
    procedure InternalLoadFromXML(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalLoadFromXMLStream(AStream: TStream; AEncoding: TEncoding); virtual;
    procedure InternalSaveToCSV(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalSaveToCSVStream(AStream: TStream; AEncoding: TEncoding); virtual;
    procedure InternalSaveToFile(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalSaveToFileStream(AStream: TStream; AEncoding: TEncoding); virtual;
    procedure InternalSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ALevel, AFromRow, AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
    procedure MoveCellData(AFromCell, AToCell: TTMSFNCDataGridCellCoord);
    procedure MoveColumnData(AFromColumn, AToColumn: Integer); virtual;
    procedure MoveRowData(AFromRow, AToRow: Integer); virtual;
    procedure QuickSort<T>(var Values: TArray<T>; const Comparer: IComparer<T>; L, R: Integer; const SwapMethod: TTMSFNCDataGridDataSortSwapMethod<T>);
    procedure RegisterRuntimeClasses; override;
    procedure RemoveCellControlFromCache(AColumn, ARow: Integer); virtual;
    procedure SetBooleans(AColumn, ARow: Integer; const Value: Boolean); virtual;
    procedure ShiftColumnOrder(AStartColumn: Integer; AShift: Integer);
    procedure ShiftRowOrder(AStartRow: Integer; AShift: Integer);
    procedure SwapCellData(AFromCell, AToCell: TTMSFNCDataGridCellCoord; ASwapDirection: TTMSFNCDataGridDataSwapDirection);
    procedure SwapColumnData(AFromColumn, AToColumn: Integer); virtual;
    procedure SwapRowData(AFromRow, AToRow: Integer); virtual;

    {$IFDEF SUPPORTABSTRACT}
    procedure Activate(AValue: Boolean);
    procedure AddRecord(ARecord: TValue);
    procedure DeleteRecord(AIndex: Integer);
    procedure InsertRecord(AIndex: Integer; ARecord: TValue);
    function CreateDefaultRecord: TValue;
    procedure InitializeFieldDefs(AFieldDefs: TFieldDefs);
    function GetRecordCount: Integer;
    function GetRecordIndex(ARecord: TValue): Integer;
    function GetRecord(AIndex: Integer): TValue;
    function GetRecordValue(ARecord: TValue; AField: string; AFieldType: TFieldType): ValueType;
    procedure SetRecordValue(ARecordIndex: Integer; AField: string; AValue: ValueType);
    function CompareRecords(AField: string; ARecord1, ARecord2: TValue): Integer;
    procedure SetNotification(ANotifyObject: ITMSFNCDataObjectNotification);
    procedure NotifyObjectDataRefresh;
    function Clone: TObject;
    //Helpers
    function DataSetStartRow: Integer;
    function DataSetStopRow: Integer;
    function DataSetStartColumn: Integer;
    function DataSetStopColumn: Integer;
    function DataSetRecordCount: Integer;
    function DataSetColumnCount: Integer;
    {$ENDIF}

    property ColumnCalculationDictionary: TTMSFNCDataGridDataColumnCalculationDictionary read GetColumnCalculationDictionary;
    property RowCalculationDictionary: TTMSFNCDataGridDataRowCalculationDictionary read GetRowCalculationDictionary;
    property HasCellData[AColumn, ARow: Integer]: Boolean read GetHasCellData;
    property HasMergeInfo[AColumn, ARow: Integer]: Boolean read GetHasMergeInfo;
    property MergeInfo[AColumn, ARow: Integer]: TTMSFNCDataGridCellMergeInfo read GetMergeInfo write SetMergeInfo;
    property TempDataList: TTMSFNCDataGridDataTempDataList read GetTempDataList;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create; override;
    destructor Destroy; override;

    function BaseCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function CanDeleteColumn(AColumn: Integer): Boolean;
    function CanDeleteRow(ARow: Integer): Boolean;
    function CanInsertColumn(AColumn: Integer): Boolean;
    function CanInsertRow(ARow: Integer): Boolean;
    function CanMergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer): Boolean;
    function CanMoveColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanMoveRow(AFromRow, AToRow: Integer): Boolean;
    function CanSwapColumn(AFromColumn, AToColumn: Integer): Boolean;
    function CanSwapRow(AFromRow, AToRow: Integer): Boolean;
    function ColumnAverage(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnCounting(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnDistinct(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnMax(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnMin(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function ColumnStandardDeviation(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function ColumnSum(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
    function Find(AText: string): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord; overload;
    function Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord; overload;
    function FindAll(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; var AResults: Integer): TTMSFNCDataGridCellCoord;
    function FindFirst(AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
    function FindNext(ADown: Boolean): TTMSFNCDataGridCellCoord; overload;
    function FindNext: TTMSFNCDataGridCellCoord; overload;
    function GetCellKind(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
    function GetCellMergeType(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
    function GetCellSize(ACell: TTMSFNCDataGridCellCoord): TSizeF;
    function GetMaxLevel: Integer;
    function GetParentRow(ARow: Integer): Integer;
    function GetRootRow(ARow: Integer): Integer;
    function HasRowData(ARow: Integer): Boolean;
    function IsCellBase(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellHidden(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellMerged(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellVisible(ACell: TTMSFNCDataGridCellCoord): Boolean; override;
    function IsGrouped: Boolean;
    function IsRowBand(ARow: Integer): Boolean;
    function IsRowNode(ARow: Integer): Boolean;
    function IsRowNormal(ARow: Integer): Boolean;
    function IsRowSummary(ARow: Integer): Boolean;
    function IsRowFilter(ARow: Integer): Boolean;
    function MatchFilter(ARow: Integer): Boolean; virtual;
    function Replace(AOriginalValue, ANewValue: string): Integer; overload;
    function Replace(AOriginalValue, ANewValue: string; AParameters: TTMSFNCDataGridDataFindParameters): Integer; overload;
    function RowAverage(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowCounting(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowDistinct(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMax(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowMin(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
    function RowStandardDeviation(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    function RowSum(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
    class function TypeOfValue(AValue: TTMSFNCDataGridCellValue): TTypeKind;
    class function ValueIsEmpty(AValue: TTMSFNCDataGridCellValue): Boolean;
    class function ValueIsFloat(AValue: TTMSFNCDataGridCellValue): Boolean;
    class function ValueIsInteger(AValue: TTMSFNCDataGridCellValue): Boolean;
    class function ValueIsString(AValue: TTMSFNCDataGridCellValue): Boolean;
    class function ValueToBoolean(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Boolean;
    class function ValueToDateTime(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): TDateTime;
    class function ValueToFloat(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Double;
    class function ValueToInt64(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Int64;
    class function ValueToInteger(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Integer;
    class function ValueToString(AValue: TTMSFNCDataGridCellValue): string;
    procedure AddColumn;
    procedure AddNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure AddRow;
    procedure ApplyFilter;
    procedure AutoNumberColumn(AColumn: Integer; AStartRow: Integer = 0; AStartValue: Integer = 0);
    procedure AutoNumberRow(ARow: Integer; AStartColumn: Integer = 0; AStartValue: Integer = 0);
    procedure ClearCells(ACellRange: TTMSFNCDataGridCellCoordRange); virtual;
    procedure ClearData; virtual;
    procedure ClearDataForColumn(AColumn: Integer); virtual;
    procedure ClearDataForRow(ARow: Integer); virtual;
    procedure ClearFilter; virtual;
    procedure ClearFind;
    procedure ClearNodeRow(ARow: Integer);
    procedure RowCalculation(ARow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure RowCalculation(ARows: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil); overload;
    procedure ColumnCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure ColumnCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure CollapseAllNodes;
    procedure CollapseNode(ARow: Integer);
    procedure DeleteColumn(AColumn: Integer);
    procedure DeleteColumns(AColumn, ACount: Integer);
    procedure DeleteRow(ARow: Integer);
    procedure DeleteRows(ARow, ACount: Integer); virtual;
    procedure ExpandAllNodes;
    procedure ExpandNode(ARow: Integer);
    procedure GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True); virtual;
    procedure Group(AColumn: Integer); overload;
    procedure Group(AColumns: TArray<Integer>; ASortColumns: TArray<Integer> = nil; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo; ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); overload;
    procedure GroupAverage(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupAverage(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCalculation(AColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCalculation(AColumns: TArray<Integer>; AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCount(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCount(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupCustomCalculation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupCustomCalculation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil); overload;
    procedure GroupDistinct(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupDistinct(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMax(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupMin(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); overload;
    procedure GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure GroupSortIndexed;
    procedure GroupStandardDeviation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupStandardDeviation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure GroupSum(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil); overload;
    procedure InsertColumn(AColumn: Integer);
    procedure InsertColumns(AColumn, ACount: Integer);
    procedure InsertRow(ARow: Integer);
    procedure InsertRows(ARow, ACount: Integer); virtual;
    procedure LinearFill(ADoFixed: Boolean = False);
    procedure LoadFromJSONData(AFileName: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONData(AFileName: string; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil); overload; virtual;
    procedure LoadFromJSONStreamData(AStream: TStream; AEncoding: TEncoding; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); overload; virtual;
    procedure LoadFromJSONTextData(AText: string; ARowDataName: string = ''; AColumnDataName: string = ''; AColumnNames: TTMSFNCDataGridDataJSONNamesArray = nil ); virtual;
    procedure LoadFromCSVData(AFileName: string); overload; virtual;
    procedure LoadFromCSVData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileData(AFileName: string); overload; virtual;
    procedure LoadFromFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromXMLData(AFileName: string); overload; virtual;
    procedure LoadFromXMLData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream); overload; virtual;
    procedure LoadFromXMLStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure LoadSampleData;
    procedure MergeCells(AColumn, ARow, AColumnCount, ARowCount: Integer);
    procedure MergeColumn(AColumn: Integer);
    procedure MergeColumns(AColumn, ACount: Integer);
    procedure MergeRange(ARange: TTMSFNCDataGridCellCoordRange);
    procedure MergeRow(ARow: Integer);
    procedure MergeRows(ARow, ACount: Integer);
    procedure MoveColumn(AFromColumn, AToColumn: Integer);
    procedure MoveRow(AFromRow, AToRow: Integer);
    procedure RandomFill(ADoFixed: Boolean = False; ARandomNumber: Integer = 100);
    procedure RefreshData(ACell: TTMSFNCDataGridCellCoord; AUpdate: Boolean = True); overload; virtual;
    procedure RefreshData(ACellRange: TTMSFNCDataGridCellCoordRange; AUpdate: Boolean = True); overload; virtual;
    procedure RefreshData(AUpdate: Boolean = True); overload; virtual;
    procedure RemoveFilter;
    procedure RemoveNodeRow(ARow: Integer);
    procedure SaveToCSVData(AFileName: string); overload; virtual;
    procedure SaveToCSVData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure SaveToCSVStreamData(AStream: TStream); overload; virtual;
    procedure SaveToCSVStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure SaveToFileData(AFileName: string); overload; virtual;
    procedure SaveToFileData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure SaveToFileStreamData(AStream: TStream); overload; virtual;
    procedure SaveToFileStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure SetNodeRow(ARow: Integer; AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection); reintroduce; overload;
    procedure Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow, AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil); reintroduce; overload;
    procedure SortIndexed;
    procedure SplitCell(ACell: TTMSFNCDataGridCellCoord);
    procedure SwapCells(AFromCell, AToCell: TTMSFNCDataGridCellCoord);
    procedure SwapColumn(AFromColumn, AToColumn: Integer);
    procedure SwapRow(AFromRow, AToRow: Integer);
    procedure ToggleNode(ARow: Integer);
    procedure Ungroup;
    procedure UpdateColumnCalculation(AColumn: Integer; AName: string);
    procedure UpdateColumnCalculations;
    procedure UpdateRowCalculation(ARow: Integer; AName: string);
    procedure UpdateRowCalculations;
    procedure UpdateCalculations;
    property Adapter: TTMSFNCDataGridDataAdapter read FAdapter write SetAdapter;
    property Booleans[AColumn, ARow: Integer]: Boolean read GetBooleans write SetBooleans;
    property CellDataExtended[AColumn, ARow: Integer]: TTMSFNCDataGridCellItemExtended read GetCellDataExtended;
    property CellData[AColumn, ARow: Integer]: TTMSFNCDataGridCellItem read GetCellData;
    property Cells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetCells write SetCells;
    property ColumnCalculations[AColumn: Integer; AName: string]: TTMSFNCDataGridDataColumnCalculationArray read GetColumnCalculation write SetColumnCalculation;
    property GroupInfo: TTMSFNCDataGridDataGroupInfo read FGroupInfo;
    property RowCalculations[ARow: Integer; AName: string]: TTMSFNCDataGridDataRowCalculationArray read GetRowCalculation write SetRowCalculation;
    property Controls[AColumn, ARow: Integer]: TTMSFNCDataGridCellControl read GetControls write SetControls;
    property Floats[AColumn, ARow: Integer]: Double read GetFloats write SetFloats;
    property HasColumnCalculations[AColumn: Integer; AName: string]: Boolean read GetHasColumnCalculation;
    property HasRowCalculations[ARow: Integer; AName: string]: Boolean read GetHasRowCalculation;
    property Ints[AColumn, ARow: Integer]: Int64 read GetInts write SetInts;
    property ManagedObjects[AColumn, ARow: Integer]: TObject read GetManagedObjects write SetManagedObjects;
    property Objects[AColumn, ARow: Integer]: TObject read GetObjects write SetObjects;
    property OnCustomColumnCalculation: TTMSFNCDataGridDataCustomColumnCalculationEvent read FOnCustomColumnCalculation write FOnCustomColumnCalculation;
    property OnCustomRowCalculation: TTMSFNCDataGridDataCustomRowCalculationEvent read FOnCustomRowCalculation write FOnCustomRowCalculation;
    property OnCustomCompare: TTMSFNCDataGridDataCustomCompareEvent read FOnCustomCompare write FOnCustomCompare;
    property OnGetColumnCalculationOptions: TTMSFNCDataGridDataGetColumnCalculationOptionsEvent read FOnGetColumnCalculationOptions write FOnGetColumnCalculationOptions;
    property OnGetRowCalculationOptions: TTMSFNCDataGridDataGetRowCalculationOptionsEvent read FOnGetRowCalculationOptions write FOnGetRowCalculationOptions;
    property OnGetCellData: TTMSFNCDataGridDataGetCellDataEvent read FOnGetCellData write FOnGetCellData;
    property OnGetCellDataProperties: TTMSFNCDataGridDataGetCellDataPropertiesEvent read FOnGetCellDataProperties write FOnGetCellDataProperties;
    property OnGetCellDisplayValue: TTMSFNCDataGridDataGetCellDisplayValueEvent read FOnGetCellDisplayValue write FOnGetCellDisplayValue;
    property OnGetCellFormatting: TTMSFNCDataGridDataGetCellFormattingEvent read FOnGetCellFormatting write FOnGetCellFormatting;
    property OnGetCustomGroup: TTMSFNCDataGridDataGetCustomGroupEvent read FOnGetCustomGroup write FOnGetCustomGroup;
    property OnGetGroupOptions: TTMSFNCDataGridDataGetGroupOptionsEvent read FOnGetGroupOptions write FOnGetGroupOptions;
    property OnGetSortOptions: TTMSFNCDataGridDataGetSortOptionsEvent read FOnGetSortOptions write FOnGetSortOptions;
    property OnRowCollapse: TTMSFNCDataGridDataRowCollapseEvent read FOnRowCollapse write FOnRowCollapse;
    property OnRowExpand: TTMSFNCDataGridDataRowExpandEvent read FOnRowExpand write FOnRowExpand;
    property OnLoadCellData: TTMSFNCDataGridDataCellDataEvent read FOnLoadCellData write FOnLoadCellData;
    property OnSaveCellData: TTMSFNCDataGridDataCellDataEvent read FOnSaveCellData write FOnSaveCellData;
    property RowNodeChildCount[ARow: Integer]: Integer read GetRowNodeChildCount write SetRowNodeChildCount;
    property RowNodeLevel[ARow: Integer]: Integer read GetRowNodeLevel write SetRowNodeLevel;
    property RowNodeState[ARow: Integer]: TTMSFNCDataGridRowNodeState read GetRowNodeState write SetRowNodeState;
    property RowText[ARow: Integer]: string read GetRowText write SetRowText;
    property RowTypes[ARow: Integer]: TTMSFNCDataGridRowType read GetRowTypes write SetRowTypes;
    property SortIndexDictionary: TTMSFNCDataGridSortIndexDictionary read GetSortIndexDictionary;
    property StoredCells[AColumn, ARow: Integer]: TTMSFNCDataGridCellValue read GetStoredCells;
    property Strings[AColumn, ARow: Integer]: string read GetStrings write SetStrings;
    property StrippedCells[AColumn, ARow: Integer]: string read GetStrippedCells;
  published
    property CellDataDictionary: TTMSFNCDataGridDataDictionary read GetCellDataDictionary;
    property Filter: TTMSFNCDataGridDataFilter read GetFilter;
    property GroupDictionary: TTMSFNCDataGridDataGroupDictionary read GetGroupDictionary;
    property Options: TTMSFNCDataGridDataOptions read FOptions write SetOptions;
  end;

function CellValueToTValue(AValue: TTMSFNCDataGridCellValue): TValue;
function CreateRowCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1; AOutputColumn: Integer = -1): TTMSFNCDataGridDataRowCalculation;
function CreateColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; AType: TTMSFNCDataGridDataColumnCalculationType = gctNormal; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
function CreateGroupColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
function CreateNormalColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
function CreateCustomRowCalculation(ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1; AOutputColumn: Integer = -1): TTMSFNCDataGridDataRowCalculation;
function CreateCustomColumnCalculation(ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; ASortColumnsLevel1: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; ASortColumnsLevel1: TArray<Integer>; AColumnsLevel2: TArray<Integer>; ASortColumnsLevel2: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function CreateGroupInfo(AColumnsLevel0: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function CreateGroupInfo(AColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function CreateGroupInfo(AColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; AColumnsLevel2: TArray<Integer>): TTMSFNCDataGridDataGroupInfo; overload;
function DefaultColumnCalculationOptions: TTMSFNCDataGridDataColumnCalculationOptions;
function DefaultRowCalculationOptions: TTMSFNCDataGridDataRowCalculationOptions;
function DefaultFormatting: TTMSFNCDataGridDataFormatting;
function DefaultGroupOptions: TTMSFNCDataGridDataGroupOptions;
function DefaultIOOptions: TTMSFNCDataGridDataInputOutputOptions;
function DefaultSortOptions: TTMSFNCDataGridDataSortOptions;
function DefaultGroupInfo: TTMSFNCDataGridDataGroupInfo;
function TranslateTextEx(AText: String): String;
function TValueToCellValue(AValue: TValue): TTMSFNCDataGridCellValue;
function GetFilterTypeName(AType: TTMSFNCDataGridDataFilterType): string;
procedure StripFilterCondition(ACondition: string; var AValue: string; var AType: TTMSFNCDataGridDataFilterType);

implementation

uses
  Math, Variants, DateUtils, WEBLib.TMSFNCUtils, WEBLib.TMSFNCDataGridUtil
  {$IFNDEF WEBLIB}
  ,XmlIntf, XMLDom, XMLDoc
  {$ENDIF}
  {$IFDEF WEBLIB}
  ,Web,JS
  {$ENDIF}
  ,StrUtils
  ;

type
  TTMSFNCDataGridInternalSortFormat = (gisfAlphabetic, gisfNumericFloat, gisfNumericInteger, gisfDateTime, gisfCustom);

  {$IFDEF WEBLIB}
  TDomNode = TJSNode;
  TXMLDocument = TJSDocument;

  TJSNodeListHelper = class helper for TJSNodeList
    function FindNode(ANodeName: string): TJSNode;
    function IndexOf(const Name: string): NativeInt; overload;
    function GetCount: NativeInt;
    property Count: NativeInt read GetCount;
  end;
  {$ENDIF}

  {$IFNDEF WEBLIB}
  TTMSFNCDataGridDataXMLNode = IXMLNode;
  TTMSFNCDataGridDataXMLDocument = IXMLDocument;
  {$ELSE}
  TTMSFNCDataGridDataXMLNode = TDomNode;
  TTMSFNCDataGridDataXMLDocument = TXMLDocument;
  {$ENDIF}

function DefaultGroupInfo: TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := nil;
  Result.SubColumns := nil;
  Result.SortColumns := nil;
  Result.SortDirections := nil;
end;

function DefaultFormatting: TTMSFNCDataGridDataFormatting;
begin
  Result.Format := '';
  Result.&Type := gdftDefault;
  Result.BooleanTrueText := '';
  Result.BooleanFalseText := '';
end;

function DefaultSortOptions: TTMSFNCDataGridDataSortOptions;
begin
  Result.Direction := gsdAscending;
  Result.CaseSensitive := True;
  Result.Format := gsfAutomatic;
  Result.FormatSettings := FormatSettings;
end;

function DefaultColumnCalculationOptions: TTMSFNCDataGridDataColumnCalculationOptions;
begin
  Result.MergeSummary := False;
  Result.SummaryInHeader := False;
  Result.CalculationFormat := '%g';
end;

function DefaultRowCalculationOptions: TTMSFNCDataGridDataRowCalculationOptions;
begin
  Result.CalculationFormat := '%g';
end;

function DefaultGroupOptions: TTMSFNCDataGridDataGroupOptions;
begin
  Result.Summary := True;
  Result.SummaryInHeader := False;
  Result.MergeSummary := False;
  Result.MergeHeader := True;
  Result.MergeDelimiter := ' ; ';
  Result.HideColumns := False;
  Result.UseFirstRowAsHeader := True;
  Result.HeaderHeight := 30;
  Result.SummaryHeight := 30;
  Result.AutoSort := True;
end;

function DefaultIOOptions: TTMSFNCDataGridDataInputOutputOptions;
begin
  Result.Delimiter := #0;
  Result.StartColumn := 0;
  Result.StartRow := 0;
  Result.MaxRows := -1;
  Result.AlwaysQuotes := False;
  Result.QuoteEmptyCells := True;
  Result.TrimSpaces := False;
end;

function CreateGroupInfo(AColumnsLevel0: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
end;

function CreateGroupInfo(AColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
  SetLength(Result.SubColumns, 1);
  Result.SubColumns[0].Columns := AColumnsLevel1;
end;

function CreateGroupInfo(AColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; AColumnsLevel2: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
  SetLength(Result.SubColumns, 1);
  Result.SubColumns[0].Columns := AColumnsLevel1;
  SetLength(Result.SubColumns[0].SubColumns, 1);
  Result.SubColumns[0].SubColumns[0].Columns := AColumnsLevel2;
end;

function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
  Result.SortColumns := ASortColumnsLevel0;
end;

function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; ASortColumnsLevel1: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
  Result.SortColumns := ASortColumnsLevel0;
  SetLength(Result.SubColumns, 1);
  Result.SubColumns[0].Columns := AColumnsLevel1;
  Result.SubColumns[0].SortColumns := ASortColumnsLevel1;
end;

function CreateGroupAndSortInfo(AColumnsLevel0: TArray<Integer>; ASortColumnsLevel0: TArray<Integer>; AColumnsLevel1: TArray<Integer>; ASortColumnsLevel1: TArray<Integer>; AColumnsLevel2: TArray<Integer>; ASortColumnsLevel2: TArray<Integer>): TTMSFNCDataGridDataGroupInfo;
begin
  Result.Columns := AColumnsLevel0;
  Result.SortColumns := ASortColumnsLevel0;
  SetLength(Result.SubColumns, 1);
  Result.SubColumns[0].Columns := AColumnsLevel1;
  Result.SubColumns[0].SortColumns := ASortColumnsLevel1;
  SetLength(Result.SubColumns[0].SubColumns, 1);
  Result.SubColumns[0].SubColumns[0].Columns := AColumnsLevel2;
  Result.SubColumns[0].SubColumns[0].SortColumns := ASortColumnsLevel2;
end;

function TValueToCellValue(AValue: TValue): TTMSFNCDataGridCellValue;
begin
  {$IFDEF WEBLIB}
  Result := AValue.AsJSValue;
  {$ELSE}
  Result := AValue;
  {$ENDIF}
end;

function CellValueToTValue(AValue: TTMSFNCDataGridCellValue): TValue;
begin
  {$IFDEF WEBLIB}
  if Assigned(AValue) then
    Result := TValue.FromJSValue(AValue)
  else
    Result := TValue.Empty;
  {$ELSE}
  Result := AValue;
  {$ENDIF}
end;

function CreateNormalColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
begin
  Result := CreateColumnCalculation(AMethod, gctNormal, ACalculationOptionsCallback, ACustomCalculationCallback, AFromRow, AToRow, AOutputRow);
end;

function CreateGroupColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
begin
  Result := CreateColumnCalculation(AMethod, gctGroup, ACalculationOptionsCallback, ACustomCalculationCallback, AFromRow, AToRow, AOutputRow);
end;

function CreateCustomRowCalculation(ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1; AOutputColumn: Integer = -1): TTMSFNCDataGridDataRowCalculation;
begin
  Result := CreateRowCalculation(gcmCustom, nil, ACustomCalculationCallback, AFromColumn, AToColumn, AOutputColumn);
end;

function CreateCustomColumnCalculation(ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
begin
  Result := CreateColumnCalculation(gcmCustom, gctNormal, nil, ACustomCalculationCallback, AFromRow, AToRow, AOutputRow);
end;

function CreateColumnCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; AType: TTMSFNCDataGridDataColumnCalculationType = gctNormal; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1; AOutputRow: Integer = -1): TTMSFNCDataGridDataColumnCalculation;
begin
  Result.&Type := AType;
  Result.OutputRow := AOutputRow;
  Result.Method := AMethod;
  Result.OptionsCallback := ACalculationOptionsCallback;
  Result.CustomCalculationCallback := ACustomCalculationCallback;
  Result.FromRow := AFromRow;
  Result.ToRow := AToRow;
end;

function CreateRowCalculation(AMethod: TTMSFNCDataGridDataCalculationMethod; ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1; AOutputColumn: Integer = -1): TTMSFNCDataGridDataRowCalculation;
begin
  Result.OutputColumn := AOutputColumn;
  Result.Method := AMethod;
  Result.OptionsCallback := ACalculationOptionsCallback;
  Result.CustomCalculationCallback := ACustomCalculationCallback;
  Result.FromColumn := AFromColumn;
  Result.ToColumn := AToColumn;
end;

function CleanupSep(value: string): string;
var
  i: integer;
  s: string;
begin
  s := '';

  {$IFDEF ZEROSTRINGINDEX}
  for i := 0 to Length(value) - 1 do
  {$ENDIF}
  {$IFNDEF ZEROSTRINGINDEX}
  for i := 1 to Length(value) do
  {$ENDIF}
  begin
    if (Value[i] = FormatSettings.ThousandSeparator) then
      Continue;

    if (Value[i] = FormatSettings.DecimalSeparator) then
      s := s + '.'
    else
      s := s + Value[i];
  end;
  Result := s;
end;

function TranslateTextEx(AText: String): String;
begin
  {$IFDEF FMXLIB}
  Result := TranslateText(AText);
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  Result := AText;
  {$ENDIF}
end;

{ TTMSFNCDataGridData }

procedure TTMSFNCDataGridData.AddColumn;
var
  cc: Integer;
begin
  BeginUpdate;
  try
    cc := ColumnCount;
    ColumnCount := ColumnCount + 1;
    SetColumnOrder(cc, ColumnCount - 1);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.AddNodeRow(ARow, AChildCount, ALevel: Integer;
  AMerge: Boolean);
begin
  SetNodeRow(ARow, AChildCount, ALevel, AMerge);
end;

procedure TTMSFNCDataGridData.AddRow;
var
  rr: Integer;
begin
  BeginUpdate;
  try
    rr := RowCount;
    RowCount := RowCount + 1;
    SetRowOrder(rr, RowCount - 1);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ApplyFilter;
var
  i: Integer;
begin
  if Assigned(Adapter) then
  begin
    case Adapter.FilterMode of
      afmNone: Exit;
      afmAdapter:
      begin
        Adapter.ApplyFilter;
        Exit;
      end;
    end;
  end;

  BeginUpdate;
  try
    if IsGrouped then
      InternalFilterGroups(GroupDictionary)
    else
    begin
      for i := FixedRowCount to RowCount - FixedBottomRowCount - 1 do
      begin
        if not MatchFilter(i) and IsRowNormal(i) then
          FilteredRows[i] := True;
      end;
    end;

  finally
    FFilterApplied := True;
    EndUpdate;
  end;
end;

{$IFDEF SUPPORTABSTRACT}

function TTMSFNCDataGridData.GetFieldIndexDictionary: TTMSFNCDataGridDataObjectIndexDictionary;
begin
  if not Assigned(FFieldIndexDictionary) then
    FFieldIndexDictionary := TTMSFNCDataGridDataObjectIndexDictionary.Create;

  Result := FFieldIndexDictionary;
end;

function TTMSFNCDataGridData.GetRecord(AIndex: Integer): TValue;
begin
  Result := GetRowData(DataSetStartRow + AIndex);
end;

function TTMSFNCDataGridData.GetRecordCount: Integer;
begin
  Result := DataSetRecordCount
end;

function TTMSFNCDataGridData.GetRecordIndex(ARecord: TValue): Integer;
var
  I: Integer;
  val: TTMSFNCDataGridRowItem;
begin
  Result := -1;

  for I in CellDataDictionary.Keys do
  begin
    if CellDataDictionary.TryGetValue(I, val) then
    begin
      if val = ARecord.AsType<TTMSFNCDataGridRowItem> then
      begin
        Result := I - DataSetStartRow;
        Exit;
      end;
    end;
  end;
end;

function TTMSFNCDataGridData.GetRecordValue(ARecord: TValue;
  AField: string; AFieldType: TFieldType): ValueType;
var
  I, R: Integer;
  ri: TTMSFNCDataGridRowItem;
begin
  Result := Null;

  if GetFieldIndexDictionary.TryGetValue(AField, I) then
  begin
    ri := ARecord.AsType<TTMSFNCDataGridRowItem>;
    r := GetRecordIndex(ri);
    Result := Cells[I, DataSetStartRow + r].AsVariant;
  end;
end;

procedure TTMSFNCDataGridData.NotifyObjectDataRefresh;
begin
  if Assigned(FNotifyObject) and not FPosting then
    FNotifyObject.DataRefresh;
end;

function TTMSFNCDataGridData.Clone: TObject;
begin
  Result := TTMSFNCDataGridData.Create;
  TTMSFNCDataGridData(Result).Assign(Self);
  TTMSFNCDataGridData(Result).OnGetCellData := OnGetCellData;
end;

procedure TTMSFNCDataGridData.SetRecordValue(ARecordIndex: Integer;
  AField: string; AValue: ValueType);
var
  I: Integer;
begin
  if GetFieldIndexDictionary.TryGetValue(AField, I) then
  begin
    FPosting := True;
    Cells[I, DataSetStartRow + ARecordIndex] := TValue.FromVariant(AValue);
    FPosting := False;
  end;
end;

procedure TTMSFNCDataGridData.SetNotification(
  ANotifyObject: ITMSFNCDataObjectNotification);
begin
  FNotifyObject := ANotifyObject;
end;

procedure TTMSFNCDataGridData.InitializeFieldDefs(AFieldDefs: TFieldDefs);
var
  I: Integer;
  tk: TTypeKind;
  ft: TFieldType;
  v: TTMSFNCDataGridCellValue;
begin
  for I := DataSetStartColumn to DataSetStartColumn + DataSetColumnCount - 1 do
  begin
    v := Cells[I, 0];
    tk := TTMSFNCDataGridData.TypeOfValue(v);
    case tk of
      tkUnknown: ft := ftUnknown;
      tkInteger: ft := ftInteger;
      tkChar: ft := ftFixedChar;
      tkEnumeration: ft := ftUnknown;
      tkFloat: ft := ftFloat;
      tkString: ft := ftString;
      tkSet: ft := ftUnknown;
      tkClass: ft := ftObject;
      tkMethod: ft := ftUnknown;
      tkWChar: ft := ftFixedWideChar;
      tkLString: ft := ftString;
      tkWString: ft := ftWideString;
      tkVariant: ft := ftVariant;
      tkArray: ft := ftArray;
      tkRecord: ft := ftUnknown;
      tkInterface: ft := ftInterface;
      tkInt64: ft := ftLargeint;
      tkDynArray: ft := ftArray;
      tkUString: ft := ftString;
      tkClassRef: ft := ftUnknown;
      tkPointer: ft := ftUnknown;
      tkProcedure: ft := ftUnknown;
    else
      ft := ftUnknown;
    end;

    if (FixedRowCount > 0) and ((ft = ftString) or (ft = ftWideString)) then
    begin
      AFieldDefs.Add(TTMSFNCDataGridData.ValueToString(v), ft, 255);
      GetFieldIndexDictionary.AddOrSetValue(TTMSFNCDataGridData.ValueToString(v), I);
    end
    else if (FixedRowCount > 0) then
    begin
      AFieldDefs.Add(TTMSFNCDataGridData.ValueToString(v), ft);
      GetFieldIndexDictionary.AddOrSetValue(TTMSFNCDataGridData.ValueToString(v), I);
    end
    else if ((ft = ftString) or (ft = ftWideString)) then
    begin
      AFieldDefs.Add(IntToStr(I + 1), ft, 255);
      GetFieldIndexDictionary.AddOrSetValue(IntToStr(I + 1), I);
    end
    else
    begin
      AFieldDefs.Add(IntToStr(I + 1), ft);
      GetFieldIndexDictionary.AddOrSetValue(IntToStr(I + 1), I);
    end;
  end;
end;

procedure TTMSFNCDataGridData.Activate(AValue: Boolean);
begin
  //Skip for now, only used when TTMSFNCDataSet is activated
end;

procedure TTMSFNCDataGridData.InsertRecord(AIndex: Integer; ARecord: TValue);
var
  I: Integer;
  r: TPair<Integer, TTMSFNCDataGridRowItem>;
begin
  for I := RowCount - 1 downto (DataSetStartRow + AIndex) do
  begin
    r := CellDataDictionary.ExtractPair(I);
    CellDataDictionary.AddOrSetValue(I + 1, r.Value);
  end;

  CellDataDictionary.AddOrSetValue(DataSetStartRow + AIndex, ARecord.AsType<TTMSFNCDataGridRowItem>);
end;

procedure TTMSFNCDataGridData.AddRecord(ARecord: TValue);
begin
  CellDataDictionary.AddOrSetValue(RowCount - DataSetStopRow, ARecord.AsType<TTMSFNCDataGridRowItem>);
end;

procedure TTMSFNCDataGridData.DeleteRecord(AIndex: Integer);
begin
  DeleteRow(DataSetStartRow + AIndex);
end;

function TTMSFNCDataGridData.CompareRecords(AField: string; ARecord1,
  ARecord2: TValue): Integer;
var
  v1, v2: Variant;
begin
  //TODO?
  v1 := GetRecordValue(ARecord1, AField, ftUnknown);
  v2 := GetRecordValue(ARecord2, AField, ftUnknown);

  case VarCompareValue(v1, v2) of
    vrEqual: Result := 0;
    vrLessThan: Result := -1;
    vrGreaterThan: Result := 1;
    vrNotEqual: Result := -1;
  else
    Result := 0;
  end;
end;

function TTMSFNCDataGridData.CreateDefaultRecord: TValue;
begin
  Result := TTMSFNCDataGridRowItem.Create;
  Result.AsType<TTMSFNCDataGridRowItem>.Init;
end;

function TTMSFNCDataGridData.DataSetStartRow: Integer;
begin
//  if Options.Binding.StartRowIndex <> -1 then
//    Result := Options.Binding.StartRowIndex
//  else
    Result := FixedRowCount;
end;

function TTMSFNCDataGridData.DataSetStopRow: Integer;
begin
//  if Options.Binding.StopRowIndex <> -1 then
//    Result := Options.Binding.StopRowIndex
//  else
    Result := FixedBottomRowCount;
end;

function TTMSFNCDataGridData.DataSetStartColumn: Integer;
begin
//  if Options.Binding.StartColumnIndex <> -1 then
//    Result := Options.Binding.StartColumnIndex
//  else
    Result := FixedColumnCount;
end;

function TTMSFNCDataGridData.DataSetStopColumn: Integer;
begin
//  if Options.Binding.StopColumnIndex <> -1 then
//    Result := Options.Binding.StopColumnIndex
//  else
    Result := FixedRightColumnCount;
end;

function TTMSFNCDataGridData.DataSetRecordCount: Integer;
begin
//  if (Options.Binding.StartRowIndex <> -1) and (Options.Binding.StopRowIndex <> -1) then
//    Result := DataSetStopRow - DataSetStartRow + 1
//  else if (Options.Binding.StartRowIndex <> -1) then
//    Result := RowCount - DataSetStartRow
//  else
    Result := RowCount - DataSetStopRow - DataSetStartRow;
end;

function TTMSFNCDataGridData.DataSetColumnCount: Integer;
begin
//  if (Options.Binding.StartColumnIndex <> -1) and (Options.Binding.StopColumnIndex <> -1) then
//    Result := DataSetStopColumn - DataSetStartColumn + 1
//  else if (Options.Binding.StartColumnIndex <> -1) then
//    Result := ColumnCount - DataSetStartColumn
//  else
    Result := ColumnCount - DataSetStopColumn - DataSetStartColumn;
end;
{$ENDIF}

procedure TTMSFNCDataGridData.Assign(Source: TPersistent);
var
  r, ri: TTMSFNCDataGridRowItem;
  k: Integer;
begin
  BeginUpdate;
  try
    inherited;
    if Source is TTMSFNCDataGridData then
    begin
      FAdapter := (Source as TTMSFNCDataGridData).Adapter;
      FreeAndNil(FCellDataDictionary);
      for k in (Source as TTMSFNCDataGridData).CellDataDictionary.Keys do
      begin
        r := (Source as TTMSFNCDataGridData).CellDataDictionary[k];
        if Assigned(r) then
        begin
          ri := TTMSFNCDataGridRowItem.Create;
          ri.Assign(r, Self);
          CellDataDictionary.AddOrSetValue(k, ri);
        end;
      end;

      FreeAndNil(FColumnCalculationDictionary);
      FColumnCalculationDictionary := TTMSFNCDataGridDataColumnCalculationDictionary.Create((Source as TTMSFNCDataGridData).ColumnCalculationDictionary);
      FreeAndNil(FRowCalculationDictionary);
      FRowCalculationDictionary := TTMSFNCDataGridDataRowCalculationDictionary.Create((Source as TTMSFNCDataGridData).RowCalculationDictionary);
      Filter.Assign((Source as TTMSFNCDataGridData).Filter);
      FOptions.Assign((Source as TTMSFNCDataGridData).Options);
      FreeAndNil(FSortIndexDictionary);
      FSortIndexDictionary := TTMSFNCDataGridSortIndexDictionary.Create((Source as TTMSFNCDataGridData).SortIndexDictionary);
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.AutoNumberColumn(AColumn, AStartRow,
  AStartValue: Integer);
var
  i: Integer;
begin
  for i := AStartRow to RowCount - 1 do
  begin
    Cells[AColumn, i] := AStartValue;
    Inc(AStartValue);
  end;
end;

procedure TTMSFNCDataGridData.AutoNumberRow(ARow, AStartColumn,
  AStartValue: Integer);
var
  i: Integer;
begin
  for i := AStartColumn to ColumnCount - 1 do
  begin
    Cells[i, ARow] := AStartValue;
    Inc(AStartValue);
  end;
end;

function TTMSFNCDataGridData.BaseCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
var
  mi: TTMSFNCDataGridCellMergeInfo;
begin
  Result := ACell;

  if HasMergeInfo[ACell.Column, ACell.Row] then
  begin
    mi := MergeInfo[ACell.Column, ACell.Row];
    Result.Column := ACell.Column - mi.ColumnOffset;
    Result.Row := ACell.Row - mi.RowOffset;
  end;
end;

procedure TTMSFNCDataGridData.BeginUsingCellData(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellItem);
begin
  TempDataList.Add(TTMSFNCDataGridDataTempData.Create(ACell, AData));
end;

function TTMSFNCDataGridData.CanSwapColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := not IsColumnMerged(AFromColumn) and not IsColumnMerged(AToColumn) and (AFromColumn <> AToColumn)
    and IsColumnValid(AFromColumn) and IsColumnValid(AToColumn);
end;

function TTMSFNCDataGridData.CanSwapRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := not IsRowMerged(AFromRow) and not IsRowMerged(AToRow) and (AFromRow <> AToRow)
    and IsRowValid(AFromRow) and IsRowValid(AToRow) and IsRowNormal(AFromRow) and IsRowNormal(AToRow);
end;

function TTMSFNCDataGridData.CanDeleteColumn(AColumn: Integer): Boolean;
begin
  Result := not IsColumnMerged(AColumn) and IsColumnValid(AColumn);
end;

function TTMSFNCDataGridData.CanDeleteRow(ARow: Integer): Boolean;
begin
  Result := not IsRowMerged(ARow) and IsRowValid(ARow) and IsRowNormal(ARow);
end;

function TTMSFNCDataGridData.CanInsertColumn(AColumn: Integer): Boolean;
begin
  Result := not IsColumnMerged(AColumn) and IsColumnValid(AColumn);
end;

function TTMSFNCDataGridData.CanInsertRow(ARow: Integer): Boolean;
begin
  Result := not IsRowMerged(ARow) and IsRowValid(ARow);
end;

function TTMSFNCDataGridData.CanMergeCells(AColumn, ARow, AColumnCount,
  ARowCount: Integer): Boolean;
var
  c: Integer;
  r: Integer;
  t: TTMSFNCDataGridDataCellMergeType;
begin
  Result := True;
  t := GetCellMergeType(MakeCell(AColumn, ARow));

  if t = gcmtInvalid then
  begin
    Result := False;
    Exit;
  end;

  for c := AColumn to AColumn + AColumnCount - 1 do
  begin
    for r := ARow to ARow + ARowCount - 1 do
    begin
      if not IsCellValid(c, r) or (t <> GetCellMergeType(MakeCell(c, r))) then
      begin
        Result := False;
        Exit;
      end;
    end;
  end;
end;

function TTMSFNCDataGridData.CanMoveColumn(AFromColumn,
  AToColumn: Integer): Boolean;
begin
  Result := not IsColumnMerged(AFromColumn) and not IsColumnMerged(AToColumn) and (AFromColumn <> AToColumn)
    and IsColumnValid(AFromColumn) and IsColumnValid(AToColumn);
end;

function TTMSFNCDataGridData.CanMoveRow(AFromRow, AToRow: Integer): Boolean;
begin
  Result := not IsRowMerged(AFromRow) and not IsRowMerged(AToRow) and (AFromRow <> AToRow)
    and IsRowValid(AFromRow) and IsRowValid(AToRow) and IsRowNormal(AFromRow) and IsRowNormal(AToRow);
end;

procedure TTMSFNCDataGridData.InternalClear;
begin
  inherited;
  RowCalculationDictionary.Clear;
  ColumnCalculationDictionary.Clear;
  CellDataDictionary.Clear;
  GroupDictionary.Clear;
end;

procedure TTMSFNCDataGridData.InternalColumnCalculation(
  ACalculations: TTMSFNCDataGridDataColumnCalculationDictionary; AGroup: TTMSFNCDataGridDataGroup; ALevel: Integer);
var
  j, k: Integer;
  I, str, s: string;
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
  cm: TTMSFNCDataGridDataColumnCalculation;
  cma: TTMSFNCDataGridDataColumnCalculationArray;
  opt: TTMSFNCDataGridDataColumnCalculationOptions;
  fr, frto, outr, cr: Integer;
begin
  for k in ACalculations.Keys do
  begin
    l := ACalculations[k];
    for I in l.Keys do
    begin
      s := '';
      outr := -1;
      cr := K;

      cma := l[I];
      for j := 0 to Length(cma) - 1 do
      begin
        cm := cma[J];
        opt := GetColumnCalculationOptions;

        if Assigned(cm.OptionsCallback) then
          cm.OptionsCallback(k, ALevel, opt)
        else
          DoGetColumnCalculationOptions(k, ALevel, opt);

        if (cm.&Type = gctGroup) and opt.MergeSummary and not opt.SummaryInHeader then
          cr := FixedColumnCount;

        if (cm.FromRow <> - 1) and (cm.ToRow <> -1) then
        begin
          fr := cm.FromRow;
          frto := cm.ToRow;
        end
        else
        begin
          fr := FixedRowCount;
          frto := RowCount - FixedBottomRowCount - 1;
        end;

        case cm.Method of
          gcmSum: str := Format(opt.CalculationFormat, [ColumnSum(k, fr, frto)]);
          gcmAverage: str := Format(opt.CalculationFormat, [ColumnAverage(k, fr, frto)]);
          gcmCount: str := Format(opt.CalculationFormat, [ColumnCounting(k, fr, frto)]);
          gcmMin: str := Format(opt.CalculationFormat, [ColumnMin(k, fr, frto)]);
          gcmMax: str := Format(opt.CalculationFormat, [ColumnMax(k, fr, frto)]);
          gcmCustom: str := Format(opt.CalculationFormat, [InternalColumnCustomCalculation(k, ALevel, cm.CustomCalculationCallback, fr, frto)]);
          gcmDistinct: str := Format(opt.CalculationFormat, [ColumnDistinct(k, fr, frto)]);
          gcmStandardDeviation: str := Format(opt.CalculationFormat, [ColumnStandardDeviation(k, fr, frto)]);
        end;

        s := s + str;
        outr := cm.OutputRow;
        if (cm.&Type = gctGroup) and (outr = -1) and Assigned(AGroup) then
        begin
          if opt.SummaryInHeader then
            outr := AGroup.Row
          else
            outr := AGroup.SummaryRow;
        end;
      end;

      if outr <> -1 then
        Cells[cr, outr] := s
      else if (FixedBottomRowCount > 0) then
        Cells[cr, RowCount - FixedBottomRowCount] := s;
    end;
  end;
end;

function TTMSFNCDataGridData.InternalColumnCustomCalculation(AColumn,
  ALevel: Integer;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback;
  AFromRow, AToRow: Integer): Double;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  if Assigned(ACustomCalculationCallback) then
    Result := ACustomCalculationCallback(AColumn, ALevel, AFromRow, AToRow)
  else
    Result := DoCustomColumnCalculation(AColumn, ALevel, AFromRow, AToRow);
end;

procedure TTMSFNCDataGridData.InternalRowCalculation(
  ACalculations: TTMSFNCDataGridDataRowCalculationDictionary);
var
  k: Integer;
  I, str, s: string;
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
  cm: TTMSFNCDataGridDataRowCalculation;
  cma: TTMSFNCDataGridDataRowCalculationArray;
  opt: TTMSFNCDataGridDataRowCalculationOptions;
  fr, frto, outc: Integer;
  J: Integer;
begin
  for k in ACalculations.Keys do
  begin
    l := ACalculations[k];
    for I in l.Keys do
    begin
      s := '';
      outc := -1;

      cma := l[I];
      for J := 0 to Length(cma) - 1 do
      begin
        cm := cma[J];
        opt := GetRowCalculationOptions;

        if Assigned(cm.OptionsCallback) then
          cm.OptionsCallback(k, opt)
        else
          DoGetRowCalculationOptions(k, 0, opt);

        if (cm.FromColumn <> - 1) and (cm.ToColumn <> -1) then
        begin
          fr := cm.FromColumn;
          frto := cm.ToColumn;
        end
        else
        begin
          fr := FixedColumnCount;
          frto := ColumnCount - FixedRightColumnCount - 1;
        end;

        case cm.Method of
          gcmSum: str := Format(opt.CalculationFormat, [RowSum(k, fr, frto)]);
          gcmAverage: str := Format(opt.CalculationFormat, [RowAverage(k, fr, frto)]);
          gcmCount: str := Format(opt.CalculationFormat, [RowCounting(k, fr, frto)]);
          gcmMin: str := Format(opt.CalculationFormat, [RowMin(k, fr, frto)]);
          gcmMax: str := Format(opt.CalculationFormat, [RowMax(k, fr, frto)]);
          gcmCustom: str := Format(opt.CalculationFormat, [RowCustomCalculation(k, cm.CustomCalculationCallback, fr, frto)]);
          gcmDistinct: str := Format(opt.CalculationFormat, [RowDistinct(k, fr, frto)]);
          gcmStandardDeviation: str := Format(opt.CalculationFormat, [RowStandardDeviation(k, fr, frto)]);
        end;

        s := s + str;
        outc := cm.OutputColumn;
      end;

      if outc <> -1 then
        Cells[outc, K] := str
      else if (FixedRightColumnCount > 0) then
        Cells[ColumnCount - FixedRightColumnCount, K] := str;
    end;
  end;
end;

function GetColumnCalculations(AColumns: TArray<Integer>; AOutputRow, AFromRow, AToRow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod; AType: TTMSFNCDataGridDataColumnCalculationType;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil): TTMSFNCDataGridDataColumnCalculationDictionary;
var
  I: Integer;
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
begin
  Result := TTMSFNCDataGridDataColumnCalculationDictionary.Create([doOwnsValues]);
  for I := 0 to Length(AColumns) - 1 do
  begin
    l := TTMSFNCDataGridDataColumnCalculationItemDictionary.Create;
    l.AddOrSetValue('ROOT', [CreateColumnCalculation(AMethod, AType, ACalculationOptionsCallback, ACustomCalculationCallback, AFromRow, AToRow, AOutputRow)]);
    Result.AddOrSetValue(AColumns[I], l);
  end;
end;

function GetRowCalculations(ARows: TArray<Integer>; AOutputColumn, AFromColumn, AToColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback = nil; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil): TTMSFNCDataGridDataRowCalculationDictionary;
var
  I: Integer;
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
begin
  Result := TTMSFNCDataGridDataRowCalculationDictionary.Create([doOwnsValues]);
  for I := 0 to Length(ARows) - 1 do
  begin
    l := TTMSFNCDataGridDataRowCalculationItemDictionary.Create;
    l.AddOrSetValue('ROOT', [CreateRowCalculation(AMethod, ACalculationOptionsCallback, ACustomCalculationCallback, AFromColumn, AToColumn, AOutputColumn)]);
    Result.AddOrSetValue(ARows[I], l);
  end;
end;

procedure TTMSFNCDataGridData.InternalRowCalculation(
  ARows: TArray<Integer>; AOutputColumn, AFromColumn, AToColumn: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
var
  d: TTMSFNCDataGridDataRowCalculationDictionary;
begin
  d := GetRowCalculations(ARows, AOutputColumn, AFromColumn, AToColumn, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
  try
    InternalRowCalculation(d);
  finally
    d.Free;
  end;
end;

procedure TTMSFNCDataGridData.InternalColumnCalculation(
  AColumns: TArray<Integer>; AOutputRow, AFromRow, AToRow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
var
  d: TTMSFNCDataGridDataColumnCalculationDictionary;
begin
  d := GetColumnCalculations(AColumns, AOutputRow, AFromRow, AToRow, AMethod, gctNormal, ACalculationOptionsCallback, ACustomCalculationCallback);
  try
    InternalColumnCalculation(d, nil, 0);
  finally
    d.Free;
  end;
end;

procedure TTMSFNCDataGridData.ClearFilter;
begin
  if Assigned(Adapter) then
  begin
    case Adapter.FilterMode of
      afmNone: Exit;
      afmAdapter:
      begin
        Filter.Clear;
        Adapter.ClearFilter;
        Exit;
      end;
    end;
  end;

  FFilterApplied := False;
  Filter.Clear;
  RemoveFilter;
end;

procedure TTMSFNCDataGridData.ClearFind;
begin
  FFindCache := '';
  FFindCell := MakeCell(-1, -1);
  FFindParameters := [];
end;

procedure TTMSFNCDataGridData.CollapseNode(ARow: Integer);
var
  I: Integer;
begin
  if not IsRowNode(ARow) then
  begin
    LogMessage('Row ' + ARow.ToString + ' cannot be collapsed.');
    Exit;
  end;

  RowNodeState[ARow] := grnsCollapsed;

  BeginUpdate;
  try
    for I := ARow + 1 to ARow + RowNodeChildCount[ARow] do
      HideRow(I);

    DoRowCollapse(ARow);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.CollapseAllNodes;
var
  k: Integer;
begin
  BeginUpdate;
  try
    for k in CellDataDictionary.Keys do
    begin
      if Assigned(CellDataDictionary[k]) and (CellDataDictionary[k].&Type = grtNode) and IsRowValid(k) then
        CollapseNode(k);
    end;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridData.ColumnSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := 0;
  if HasMergeInfo[ACell.Column, ACell.Row] then
    Result := MergeInfo[ACell.Column, ACell.Row].ColumnSpan;
end;

procedure TTMSFNCDataGridData.SetRowNodeLevel(ARow: Integer; const Value: Integer);
var
  ri: TTMSFNCDataGridRowItem;
begin
  if not IsRowValid(ARow) then
    Exit;

  ri := GetRowData(ARow);
  if Assigned(ri) then
    ri.NodeLevel := Value;
end;

procedure TTMSFNCDataGridData.SetRowNodeChildCount(ARow: Integer; const Value: Integer);
var
  ri: TTMSFNCDataGridRowItem;
begin
  if not IsRowValid(ARow) then
    Exit;

  ri := GetRowData(ARow);
  if Assigned(ri) then
    ri.NodeChildCount := Value;
end;

procedure TTMSFNCDataGridData.SetRowNodeState(ARow: Integer;
  const Value: TTMSFNCDataGridRowNodeState);
var
  ri: TTMSFNCDataGridRowItem;
begin
  if not IsRowValid(ARow) then
    Exit;

  ri := GetRowData(ARow);
  if Assigned(ri) then
    ri.NodeState := Value;
end;

procedure TTMSFNCDataGridData.SetRowText(ARow: Integer; const Value: string);
var
  str: TStringList;
  I, sc: Integer;
  o: TTMSFNCDataGridDataInputOutputOptions;
begin
  if not IsRowValid(ARow) then
    Exit;

  str := TStringList.Create;
  try
    o := GetIOOptions;
    TTMSFNCUtils.Split(o.Delimiter, Value, str);
    sc := o.StartColumn;

    for I := sc to sc + str.Count - 1 do
      Cells[I, ARow] := str[i - sc];

  finally
    str.Free;
  end;
end;

procedure TTMSFNCDataGridData.SetRowTypes(ARow: Integer;
  const Value: TTMSFNCDataGridRowType);
var
  ri: TTMSFNCDataGridRowItem;
begin
  if not IsRowValid(ARow) then
    Exit;

  ri := GetRowData(ARow);
  if Assigned(ri) then
    ri.&Type := Value;
end;

procedure TTMSFNCDataGridData.SetStrings(AColumn, ARow: Integer;
  const Value: string);
begin
  Cells[AColumn, ARow] := Value;
end;

function TTMSFNCDataGridData.IsRowBand(ARow: Integer): Boolean;
begin
  Result := Odd(ARow);
end;

function TTMSFNCDataGridData.IsRowNode(ARow: Integer): Boolean;
begin
  Result := False;
  if HasRowData(ARow) then
    Result := GetRowData(ARow).&Type = grtNode;
end;

function TTMSFNCDataGridData.HasMergedCells(AColumns: TArray<Integer>; AFromRow,
  AToRow: Integer): Boolean;
var
  J, M: Integer;
begin
  Result := False;
  for J := 0 to Length(AColumns) - 1 do
  begin
    for M := AFromRow to AToRow do
    begin
      if IsCellMerged(MakeCell(AColumns[J], M)) then
      begin
        Result := True;
        Exit;
      end;
    end;
  end;
end;

function TTMSFNCDataGridData.HasRowData(ARow: Integer): Boolean;
var
  r: TTMSFNCDataGridRowItem;
begin
  Result := False;
  if IsRowValid(ARow) then
  begin
    if CellDataDictionary.TryGetValue(ARow, r) then
      Result := Assigned(r);
  end;
end;

function TTMSFNCDataGridData.IsRowNormal(ARow: Integer): Boolean;
begin
  Result := True;
  if HasRowData(ARow) then
    Result := GetRowData(ARow).&Type = grtDefault;
end;

function TTMSFNCDataGridData.IsRowSummary(ARow: Integer): Boolean;
begin
  Result := False;
  if HasRowData(ARow) then
    Result := GetRowData(ARow).&Type = grtSummary;
end;

function TTMSFNCDataGridData.IsRowFilter(ARow: Integer): Boolean;
begin
  Result := False;
  if HasRowData(ARow) then
    Result := GetRowData(ARow).&Type = grtFilter;
end;

function TTMSFNCDataGridData.GetRowNodeState(
  ARow: Integer): TTMSFNCDataGridRowNodeState;
var
  ri: TTMSFNCDataGridRowItem;
begin
  Result := grnsExpanded;
  ri := GetRowData(ARow);
  if Assigned(ri) then
    Result := ri.NodeState;
end;

function TTMSFNCDataGridData.GetRowText(ARow: Integer): string;
var
  I: Integer;
  sl: TStringList;
  o: TTMSFNCDataGridDataInputOutputOptions;
begin
  Result := '';

  if not IsRowValid(ARow) then
    Exit;

  sl := TStringList.Create;
  try
    o := GetIOOptions;
    sl.Delimiter := o.Delimiter;
    for I := 0 to ColumnCount - 1 do
      sl.Add(Strings[I, ARow]);

    Result := sl.DelimitedText;
  finally
    sl.Free;
  end;
end;

function TTMSFNCDataGridData.GetRowTypes(ARow: Integer): TTMSFNCDataGridRowType;
var
  ri: TTMSFNCDataGridRowItem;
begin
  Result := grtDefault;
  ri := GetRowData(ARow);
  if Assigned(ri) then
    Result := ri.&Type;
end;

function TTMSFNCDataGridData.CreateCellDataForRow(ARowData: TTMSFNCDataGridRowItem; AColumn, ARow: Integer; AExtended: Boolean): TTMSFNCDataGridCellItem;
begin
  Result := nil;
  if Assigned(ARowData) and IsColumnValid(AColumn) then
  begin
    if AExtended or NeedsItemExtended(MakeCell(AColumn, ARow)) then
      Result := TTMSFNCDataGridCellItemExtended.Create
    else
      Result := TTMSFNCDataGridCellItem.Create;

    ARowData.Data.AddOrSetValue(AColumn, Result);
  end;
end;

function TTMSFNCDataGridData.FormatDisplayValue(ACell: TTMSFNCDataGridCellCoord; AValue: TTMSFNCDataGridCellValue): string;
var
  s: string;
  fmt: TFormatSettings;
  f: TTMSFNCDataGridDataFormatting;
  dt: TDateTime;
  d: Double;
  a, b: Boolean;
begin
  Result := '';
  if IsCellValid(ACell) then
  begin
    f := DefaultFormatting;
    fmt := FormatSettings;
    a := not IsCellFixed(ACell);
    DoGetCellFormatting(ACell, AValue, f, fmt, a);

    s := '';
    if not ValueIsEmpty(AValue) then
    begin
      if a then
      begin
        try
          case f.&Type of
            gdftDefault:
            begin
              if f.Format <> '' then
                s := Format(f.Format, [ValueToString(AValue)])
              else
                s := ValueToString(AValue);
            end;
            gdftBoolean:
            begin
              b := ValueToBoolean(AValue, fmt);
              if (f.BooleanTrueText <> '') and (f.BooleanFalseText <> '') then
                s := TTMSFNCUtils.IfElse<string>(b, f.BooleanTrueText, f.BooleanFalseText)
              else
                s := BoolToStr(b, True);
            end;

            gdftDateTime, gdftDate, gdftTime:
            begin
              dt := ValueToDateTime(AValue, fmt);
              if f.Format <> '' then
                s := FormatDateTime(f.Format, dt, fmt)
              else
              begin
                case f.&Type of
                  gdftDateTime: s := DateTimeToStr(dt, fmt);
                  gdftDate: s := DateToStr(dt, fmt);
                  gdftTime: s := TimeToStr(dt, fmt);
                end;
              end;
            end;
            gdftFloat:
            begin
              d := ValueToFloat(AValue, fmt);
              if f.Format <> '' then
                s := FormatFloat(f.Format, d)
              else
                s := FloatToStr(d, fmt);
            end;
            gdftNumber:
            begin
              d := ValueToFloat(AValue, fmt);
              if f.Format <> '' then
                s := Format(f.Format, [d])
              else
                s := FloatToStr(d, fmt);
            end;
          end;
        finally

        end;
      end
      else
        s := ValueToString(AValue);
    end;

    Result := s;
  end;
end;

procedure TTMSFNCDataGridData.CreateDisplayValue(
  ACellData: TTMSFNCDataGridCellItem; ACell: TTMSFNCDataGridCellCoord; AValue: TTMSFNCDataGridCellValue);
var
  s: string;

  function MakeHTMLHyperlink(const Value: string): string;
  var
    lstart, lend: Integer;
    i, k: Integer;
    link: string;
    HTMLValue: string;
  const
    LinkTypes: array[0..5] of string = ('HTTP://', 'HTTPS://', 'WWW.', 'MAILTO:', 'FILE://', 'FTP://');
  begin
    Result := Value;

    if TTMSFNCUtils.IsHTML(Result) then
      Exit;

    for k := 0 to Length(LinkTypes) - 1 do
    begin
      lstart := Pos(LinkTypes[k], Uppercase(Result));

      if lstart > 0 then
      begin
        HTMLValue := StringReplace(Result, '<', '&lt;', [rfReplaceAll]);
        HTMLValue := StringReplace(HTMLValue, '>', '&gt;', [rfReplaceAll]);
        Result := HTMLValue;

        lstart := Pos(LinkTypes[k], Uppercase(HTMLValue));
        lend := Length(HTMLValue);

        for i := lstart to lend do
        begin
          if not TTMSFNCUtils.CharInSet(HTMLValue[i], TTMSFNCUtils.AlphaCharSet) and not
            TTMSFNCUtils.CharInSet(HTMLValue[i], TTMSFNCUtils.NumericCharSet) and not
            TTMSFNCUtils.CharInSet(HTMLValue[i], ['/', '\', '_', '-', ':', '?', '!', '.', '=', '&', '%',
              '(', ')', '{', '}', '@']) then
          begin
            lend := i - 1;
            break;
          end;
        end;

        link := Copy(HTMLValue, lstart, 1 + lend - lstart); // this is the URL

        Insert('<a href="' + link + '">', Result, lstart);
        Insert('</a>', Result, 11 + 2 * Length(link) + lstart);

        Break;
      end;
    end;
  end;
begin
  if Assigned(ACellData) and IsCellValid(ACell) then
  begin
    s := FormatDisplayValue(ACell, AValue);

    if Options.URL.AutoDetect then
      s := MakeHTMLHyperlink(s);

    DoGetCellDisplayValue(ACell, AValue, s);
    ACellData.DisplayValue := s;
  end;
end;

function TTMSFNCDataGridData.GetOptionsClass: TTMSFNCDataGridDataOptionsClass;
begin
  Result := TTMSFNCDataGridDataOptions;
end;

function TTMSFNCDataGridData.CreateRowData(ARow: Integer): TTMSFNCDataGridRowItem;
begin
  Result := nil;
  if IsRowValid(ARow) then
  begin
    Result := TTMSFNCDataGridRowItem.Create;
    Result.Init;
    CellDataDictionary.AddOrSetValue(ARow, Result);
  end;
end;

procedure TTMSFNCDataGridData.CustomReadProperty(AObject: TObject;
  APropertyName: string; APropertyKind: TTypeKind; AReader: TTMSFNCJSONReader;
  var ACanRead: Boolean);
var
  r, c: Integer;
  ri: TTMSFNCDataGridRowItem;
  ci: TTMSFNCDataGridCellItem;
  rd: TTMSFNCReader;
  vl: TTMSFNCDataGridCellValue;
  vr: TTMSFNCDataGridCellItemVariant;
begin
  if ACanRead and (APropertyName = 'CellDataDictionary') then
  begin
    rd := TTMSFNCReader.Create(AReader);
    try
      ACanRead := False;
      AReader.ReadBeginArray;
      if not AReader.HasNext then
        CellDataDictionary.Clear
      else
      begin
        while AReader.HasNext do
        begin
          if not AReader.IsNull then
          begin
            AReader.ReadBeginObject;

            AReader.ReadName; //Row
            r := AReader.ReadInteger;
            if r > RowCount - 1 then
              RowCount := RowCount + 1;

            ri := GetRowData(r);

            AReader.ReadName; //RowInfo
            rd.Read(ri);

            AReader.ReadName; //CellInfo
            AReader.ReadBeginArray;

            while AReader.HasNext do
            begin
              if not AReader.IsNull then
              begin
                AReader.ReadBeginObject;
                AReader.ReadName; //Column
                c := AReader.ReadInteger;

                if c > ColumnCount - 1 then
                  ColumnCount := ColumnCount + 1;

                ci := GetCellDataExtended(c, r);
                AReader.ReadName; //Properties
                rd.Read(ci);

                AReader.ReadName; //Data

                {$IFDEF WEBLIB}
                vr := nil;
                {$ELSE}
                vr := varNull;
                {$ENDIF}

                if AReader.HasDouble then
                  vr := AReader.ReadDouble
                else if AReader.HasInt64 then
                  vr := AReader.ReadInt64
                else if AReader.HasBoolean then
                  vr := AReader.ReadBoolean
                else if AReader.HasString then
                  vr := AReader.ReadString
                else
                  AReader.ReadNull;

                {$IFDEF WEBLIB}
                vl := vr;
                {$ELSE}
                vl := TValue.FromVariant(vr);
                {$ENDIF}

                Cells[c, r] := vl;

                AReader.ReadEndObject;
              end
              else
                AReader.SkipValue;
            end;

            AReader.ReadEndArray;

            AReader.ReadEndObject;
          end
          else
            AReader.SkipValue;
        end;
      end;
      AReader.ReadEndArray;

    finally
      rd.Free;
    end;
  end;
end;

procedure TTMSFNCDataGridData.CustomWriteProperty(AObject: TObject;
  APropertyName: string; APropertyKind: TTypeKind; AWriter: TTMSFNCJSONWriter;
  var ACanWrite: Boolean);
var
  r, c: Integer;
  ri: TTMSFNCDataGridRowItem;
  ci: TTMSFNCDataGridCellItem;
  v: TTMSFNCDataGridCellValue;
  vr: TTMSFNCDataGridCellItemVariant;
  wr: TTMSFNCWriter;
begin
  if ACanWrite and (APropertyName = 'CellDataDictionary') then
  begin
    ACanWrite := False;

    ExportNotification(gesExportStart, -1);
    wr := TTMSFNCWriter.Create(AWriter);
    try
      AWriter.WriteName(APropertyName);
      AWriter.WriteBeginArray;
      for r := 0 to RowCount - 1 do
      begin
        ExportNotification(gesExportNewRow, r);
        ri := GetRowData(r);
        AWriter.WriteBeginObject;
        AWriter.WriteName('Row');
        AWriter.WriteInteger(r);

        AWriter.WriteName('RowInfo');
        wr.Write(ri);

        AWriter.WriteName('CellInfo');
        AWriter.WriteBeginArray;
        for c := 0 to ColumnCount - 1 do
        begin
          ci := GetCellData(c, r);
          AWriter.WriteBeginObject;
          AWriter.WriteName('Column');
          AWriter.WriteInteger(c);

          AWriter.WriteName('Properties');
          wr.Write(ci);

          AWriter.WriteName('Data');
          v := Cells[c, r];
          {$IFDEF WEBLIB}
          vr := v;
          if isUndefined(vr) or isNull(vr) then
            AWriter.WriteNull
          else if IsNumber(vr) then
          begin
            if Frac(Double(vr))=0 then
              AWriter.WriteInteger(NativeInt(vr))
            else
              AWriter.WriteDouble(Double(vr))
          end
          else if isBoolean(vr) then
            AWriter.WriteBoolean(Boolean(vr))
          else if isString(vr) then
            AWriter.WriteString(string(vr))
          else
            AWriter.WriteNull;

          {$ELSE}
          vr := v.AsVariant;
          case VarType(vr) of
            varEmpty, varNull: AWriter.WriteNull;
            varBoolean: AWriter.WriteBoolean(TVarData(vr).VBoolean);
            varShortInt: AWriter.WriteInteger(TVarData(vr).VShortInt);
            varSmallint: AWriter.WriteInteger(TVarData(vr).VSmallInt);
            varInteger:  AWriter.WriteInteger(TVarData(vr).VInteger);
            varSingle:  AWriter.WriteDouble(TVarData(vr).VSingle);
            varDouble: AWriter.WriteDouble(TVarData(vr).VDouble);
            varCurrency: AWriter.WriteDouble(TVarData(vr).VCurrency);
            varDate: AWriter.WriteDouble(TVarData(vr).VDate);
            varOleStr: AWriter.WriteString(string(TVarData(vr).VOleStr));
            varByte: AWriter.WriteInteger(TVarData(vr).VByte);
            varWord: AWriter.WriteInteger(TVarData(vr).VWord);
            varLongWord: AWriter.WriteInteger(TVarData(vr).VLongWord);
            varInt64: AWriter.WriteInteger(TVarData(vr).VInt64);
            varUInt64: AWriter.WriteInteger(TVarData(vr).VUInt64);
            varString: AWriter.WriteString(string(TVarData(vr).VString));
            varUString: AWriter.WriteString(UnicodeString(TVarData(vr).VUString));
            else
              AWriter.WriteNull;
          end;
          {$ENDIF}

          AWriter.WriteEndObject;
        end;
        AWriter.WriteEndArray;
        AWriter.WriteEndObject;
      end;
      AWriter.WriteEndArray;
    finally
      wr.Free;
      ExportNotification(gesExportDone, -1)
    end;
  end;
end;

function TTMSFNCDataGridData.GetRowNodeLevel(ARow: Integer): Integer;
var
  ri: TTMSFNCDataGridRowItem;
begin
  Result := -1;
  ri := GetRowData(ARow);
  if Assigned(ri) then
    Result := ri.NodeLevel;
end;

function TTMSFNCDataGridData.GetRowNodeChildCount(ARow: Integer): Integer;
var
  ri: TTMSFNCDataGridRowItem;
begin
  Result := -1;
  ri := GetRowData(ARow);
  if Assigned(ri) then
    Result := ri.NodeChildCount;
end;

procedure TTMSFNCDataGridData.QuickSort<T>(var Values: TArray<T>; const Comparer: IComparer<T>;
  L, R: Integer; const SwapMethod: TTMSFNCDataGridDataSortSwapMethod<T>);
var
  I, J, K: Integer;
  pivot: T;
begin
  if L < R then
  begin
    repeat
      if (R - L) = 1 then
      begin
        if Comparer.Compare(Values[L], Values[R]) > 0 then
          SwapMethod(Values, L, R);

        Break;
      end;
      I := L;
      J := R;
      K := L + (R - L) shr 1;
      pivot := Values[K];
      repeat
        while Comparer.Compare(Values[I], pivot) < 0 do
          Inc(I);
        while Comparer.Compare(Values[J], pivot) > 0 do
          Dec(J);
        if I <= J then
        begin
          if I <> J then
            SwapMethod(Values, I, J);

          Inc(I);
          Dec(J);
        end;
      until I > J;
      if (J - L) > (R - I) then
      begin
        if I < R then
          QuickSort<T>(Values, Comparer, I, R, SwapMethod);
        R := J;
      end
      else
      begin
        if L < J then
          QuickSort<T>(Values, Comparer, L, J, SwapMethod);
        L := I;
      end;
    until L >= R;
  end;
end;

function TTMSFNCDataGridData.GetIOOptions: TTMSFNCDataGridDataInputOutputOptions;
begin
  Result.Delimiter := Options.IO.Delimiter;
  Result.StartColumn := Options.IO.StartColumn;
  Result.StartRow := Options.IO.StartRow;
  Result.AlwaysQuotes := Options.IO.AlwaysQuotes;
  Result.QuoteEmptyCells := Options.IO.QuoteEmptyCells;
  Result.MaxRows := Options.IO.MaxRows;
  Result.TrimSpaces := Options.IO.TrimSpaces;
end;

function TTMSFNCDataGridData.GetGroupOptions: TTMSFNCDataGridDataGroupOptions;
begin
  Result.Summary := Options.Grouping.Summary;
  Result.SummaryInHeader := Options.Grouping.SummaryInHeader;
  Result.MergeSummary := Options.Grouping.MergeSummary;
  Result.MergeHeader := Options.Grouping.MergeHeader;
  Result.MergeDelimiter := Options.Grouping.MergeDelimiter;
  Result.HideColumns := Options.Grouping.HideColumns;
  Result.UseFirstRowAsHeader := Options.Grouping.UseFirstRowAsHeader;
  Result.HeaderHeight := Options.Grouping.HeaderHeight;
  Result.SummaryHeight := Options.Grouping.SummaryHeight;
  Result.AutoSort := Options.Grouping.AutoSort;
end;

function TTMSFNCDataGridData.GetColumnCalculationOptions: TTMSFNCDataGridDataColumnCalculationOptions;
begin
  Result.MergeSummary := Options.Grouping.MergeSummary;
  Result.SummaryInHeader := Options.Grouping.SummaryInHeader;
  Result.CalculationFormat := Options.Grouping.CalculationFormat;
end;

function TTMSFNCDataGridData.GetRowCalculationOptions: TTMSFNCDataGridDataRowCalculationOptions;
begin
  Result.CalculationFormat := Options.Grouping.CalculationFormat;
end;

function TTMSFNCDataGridData.GetSelection: TTMSFNCDataGridCellCoordRange;
begin
  Result := MakeCellRange(-1, -1, -1, -1);
end;

function TTMSFNCDataGridData.GetSortIndexDictionary: TTMSFNCDataGridSortIndexDictionary;
begin
  if not Assigned(FSortIndexDictionary) then
    FSortIndexDictionary := TTMSFNCDataGridSortIndexDictionary.Create;

  Result := FSortIndexDictionary;
end;

function TTMSFNCDataGridData.GetSortOptions: TTMSFNCDataGridDataSortOptions;
begin
  Result.Format := Options.Sorting.Format;
  Result.Direction := Options.Sorting.Direction;
  Result.CaseSensitive := Options.Sorting.CaseSensitive;
  Result.FormatSettings := FormatSettings;
end;

procedure TTMSFNCDataGridData.DeleteCellData(ACell: TTMSFNCDataGridCellCoord);
var
  r: TTMSFNCDataGridRowItem;
begin
  if CellDataDictionary.TryGetValue(ACell.Row, r) then
  begin
    if Assigned(r) and r.HasData then
      r.Data.Remove(ACell.Column);
  end;
end;

procedure TTMSFNCDataGridData.DeleteColumn(AColumn: Integer);
begin
  DeleteColumns(AColumn, 1)
end;

procedure TTMSFNCDataGridData.DeleteColumnData(AColumn: Integer);
var
  co: Integer;
begin
  co := ColumnReverseOrders[AColumn];
  SetColumnOrder(co, -1);

  ColumnWidthDictionary.Remove(AColumn);
  ColumnPositionDictionary.Remove(AColumn);

  HiddenColumnDictionary.Remove(AColumn);
  ColumnCalculationDictionary.Remove(AColumn);
  MergedColumnDictionary.Remove(AColumn);
end;

procedure TTMSFNCDataGridData.DeleteColumns(AColumn, ACount: Integer);
var
  c, r: Integer;
  b: Boolean;
begin
  b := True;
  for c := AColumn to AColumn + ACount - 1 do
  begin
    if not CanDeleteColumn(c) then
    begin
      b := False;
      Break;
    end;
  end;

  if b then
  begin
    BeginUpdate;
    try
      for c := AColumn to AColumn + ACount - 1 do
      begin
        for r := 0 to RowCount - 1 do
          DeleteCellData(MakeCell(c, r));

        DeleteColumnData(c);
      end;

      for C := AColumn + ACount to ColumnCount - 1 do
        MoveColumnData(c, c - ACount);

      ShiftColumnOrder(AColumn + ACount, -ACount);

      ColumnCount := ColumnCount - ACount;
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Columns [' + FormatNumberRange(AColumn, ACount) + '] cannot be deleted.', glmWarning);
end;

procedure TTMSFNCDataGridData.DeleteRow(ARow: Integer);
begin
  DeleteRows(ARow, 1);
end;

procedure TTMSFNCDataGridData.DeleteRowData(ARow: Integer);
var
  ro: Integer;
begin
  ro := RowReverseOrders[ARow];
  SetRowOrder(ro, -1);

  RowHeightDictionary.Remove(ARow);
  RowPositionDictionary.Remove(ARow);

  HiddenRowDictionary.Remove(ARow);
  MergedRowDictionary.Remove(ARow);
  FilteredRowDictionary.Remove(ARow);

  CellDataDictionary.Remove(ARow);
end;

procedure TTMSFNCDataGridData.InternalDeleteRows(ARow, ACount: Integer);
var
  r, pr: Integer;
begin
  BeginUpdate;
  try
    pr := -1;
    if FUpdateGrouping = 0 then
      pr := GetParentRow(ARow);

    for r := ARow to ARow + ACount - 1 do
      DeleteRowData(r);

    for r := ARow + ACount to RowCount - 1 do
      MoveRowData(r, r - ACount);

    ShiftRowOrder(ARow + ACount, -ACount);

    RowCount := RowCount - ACount;

    if (FUpdateGrouping = 0) then
    begin
      while pr <> -1 do
      begin
        RowNodeChildCount[pr] := RowNodeChildCount[pr] - ACount;
        pr := GetParentRow(pr);
      end;
    end;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalFilterGroups(
  AGroupDictionary: TTMSFNCDataGridDataGroupDictionary);
var
  ks: string;
  grp: TTMSFNCDataGridDataGroup;
  cc, K: Integer;
  r: Integer;
begin
  if Assigned(AGroupDictionary) then
  begin
    for ks in AGroupDictionary.Keys do
    begin
      grp := AGroupDictionary[ks];
      if Assigned(grp) then
      begin
        cc := RowNodeChildCount[grp.Row];
        r := 0;
        for K := grp.Row + 1 to grp.Row + cc do
        begin
          if IsRowNormal(K) then
          begin
            if not MatchFilter(k) then
              FilteredRows[K] := True
            else
              Inc(r);
          end;
        end;

        if (r = 0) and Options.Filtering.HideEmptyGroups then
        begin
          FilteredRows[grp.Row] := True;
          if grp.SummaryRow <> -1 then
            FilteredRows[grp.SummaryRow] := True;
        end;

        InternalFilterGroups(grp.GroupDictionary);
      end;
    end;
  end;
end;

function TTMSFNCDataGridData.InternalFind(AStartCell: TTMSFNCDataGridCellCoord;
  AText: string; AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
var
  MaxCol, MinCol: Integer;
  MaxRow, MinRow: Integer;
  i, j: Integer;
  sel: TTMSFNCDataGridCellCoordRange;

  function GetNodeFromRow(r: integer): integer;
  var
    k,l: integer;
  begin
    k := FixedRowCount;
    l := FixedRowCount;

    while (k < RowCount - 1) do
    begin
      if IsRowNode(k) then
        l := l + RowSpan(MakeCell(FixedColumnCount, k));

      if l >= r then
        Break
      else
        Inc(k);
    end;
    Result := k;
  end;

begin
  Result.Column := -1;
  Result.Row := -1;

  if not (gfnMatchCase in AParameters) then
    FFindCache := UpperCase(AText)
  else  
    FFindCache := AText;

  FFindParameters := AParameters;

  if (ColumnCount = FixedColumnCount) or (ColumnCount = 0) then
    Exit;

  if (RowCount = FixedRowCount) or (RowCount = 0) then
    Exit;

  if (gfnIncludeFixed in AParameters) then
  begin
    MaxCol := ColumnCount - 1;
    MaxRow := RowCount - 1;
    MinCol := 0;
    MinRow := 0;
  end
  else
  begin
    MaxCol := ColumnCount - 1 - FixedRightColumnCount;
    MaxRow := RowCount - 1 - FixedBottomRowCount;
    MinCol := FixedColumnCount;
    MinRow := FixedRowCount;
  end;

  if gfnSelectedCells in AParameters then
  begin
    sel := GetSelection;
    MaxCol := sel.StopCell.Column;
    MaxRow := sel.StopCell.Row;
    MinCol := sel.StartCell.Column;
    MinRow := sel.StartCell.Row;
  end;

  if (AStartCell.Column = -1) and (AStartCell.Row = -1) then
  begin
    if gfnBackward in AParameters then
    begin
      AStartCell.Column := MaxCol;
      AStartCell.Row := MaxRow;
    end
    else
    begin
      AStartCell.Column := MinCol;
      AStartCell.Row := MinRow;
    end;
  end
  else
  begin
    if gfnDirectionLeftRight in AParameters then
    begin
      if gfnBackward in AParameters then
      begin
        if AStartCell.Column >= MinCol then
          AStartCell.Column := AStartCell.Column - 1
        else
        begin
          if AStartCell.Row >= MinRow then
            AStartCell.Row := AStartCell.Row - 1
          else
          begin
            AStartCell.Column := MaxCol;
            AStartCell.Row := MaxRow;
          end;
        end;
      end
      else
      begin
        if AStartCell.Column <= MaxCol then
          AStartCell.Column := AStartCell.Column + 1
        else
        begin
          if AStartCell.Row <= MaxRow then
            AStartCell.Row := AStartCell.Row + 1
          else
          begin
            AStartCell.Column := MinCol;
            AStartCell.Row := MinRow;
          end;
        end;
      end;
    end
    else
    begin
      if gfnBackward in AParameters then
      begin
        if AStartCell.Row >= MinRow then
          AStartCell.Row := AStartCell.Row - 1
        else
        begin
          if AStartCell.Column >= MinCol then
            AStartCell.Column := AStartCell.Column - 1
          else
          begin
            AStartCell.Column := MaxCol;
            AStartCell.Row := MaxRow;
          end;
        end;
      end
      else
      begin
        if AStartCell.Row <= MaxRow then
          AStartCell.Row := AStartCell.Row + 1
        else
        begin
          if AStartCell.Column <= MaxCol then
            AStartCell.Column := AStartCell.Column + 1
          else
          begin
            AStartCell.Column := MinCol;
            AStartCell.Row := MinRow;
          end;
        end;
      end;
    end;
  end;

  i := AStartCell.Column;
  j := AStartCell.Row;

  if gfnFindInCurrentRow in AParameters then
  begin
    j := StartRow;
    MaxRow := j;
    MinRow := j;
  end;

  if gfnFindInPresetRow in AParameters then
  begin
    j := Options.Find.Row;
    MaxRow := j;
    MinRow := j;
  end;

  if gfnFindInCurrentColumn in AParameters then
  begin
    i := StartColumn;
    MaxCol := i;
    MinCol := i;
  end;

  if gfnFindInPresetColumn in AParameters then
  begin
    i := Options.Find.Column;
    MaxCol := i;
    MinCol := i;
  end;

  AStartCell.Column := i;
  AStartCell.Row := j;

  if gfnDirectionLeftRight in AParameters then
  begin
    while (j <= MaxRow) and (j >= MinRow) do
    begin
      while (i <= MaxCol) and (i >= MinCol) do
      begin
        if MatchCell(i, j) then
        begin
          FFindCell.Column := i;
          FFindCell.Row := j;
          Result := FFindCell;
            
          if gfnAutoGoto in AParameters then
            GoToCell(Result);

          Exit;
        end;

        if gfnBackward in AParameters then
          Dec(i)
        else
          Inc(i);
      end;      

      if gfnFindInCurrentColumn in AParameters then
        i := StartColumn
      else
      begin
        if gfnBackward in AParameters then
          i := MaxCol
        else
          i := MinCol;
      end;

      if gfnBackward in AParameters then
        Dec(j)
      else
        Inc(j);
    end;
  end
  else
  begin
    while (i <= MaxCol) and (i >= MinCol) do
    begin
      while (j <= MaxRow) and (j >= MinRow) do
      begin
        if MatchCell(i, j) then
        begin
          FFindCell.Column := i;
          FFindCell.Row := j;
          Result := FFindCell;
          
          if gfnAutoGoto in AParameters then
            GoToCell(Result);
          
          Exit;
        end;

        if gfnBackward in AParameters then
          Dec(j)
        else
          Inc(j);
      end;

    if gfnFindInCurrentRow in AParameters then
      j := StartRow
    else
    begin
      if gfnBackward in AParameters then
        j := MaxRow
      else
        j := MinRow;
    end;

    if gfnBackward in AParameters then
      Dec(i)
    else
      Inc(i);
    end;
  end;
end;

procedure TTMSFNCDataGridData.DeleteRows(ARow, ACount: Integer);
var
  r: Integer;
  b: Boolean;
begin
  b := True;
  for r := ARow to ARow + ACount - 1 do
  begin
    if not CanDeleteRow(r) then
    begin
      b := False;
      Break;
    end;
  end;

  if b then
    InternalDeleteRows(ARow, ACount)
  else
    LogMessage('Rows [' + FormatNumberRange(ARow, ACount) + '] cannot be deleted.', glmWarning);
end;

constructor TTMSFNCDataGridData.Create;
begin
  inherited;
  FGroupInfo := DefaultGroupInfo;
  FFindCache := '';
  FFindParameters := [];
  FFindCell := MakeCell(-1, -1);
  FOptions := GetOptionsClass.Create;
end;

destructor TTMSFNCDataGridData.Destroy;
begin
  FAdapter := nil;
  FreeAndNil(FTempDataList);
  FreeAndNil(FColumnCalculationDictionary);
  FreeAndNil(FRowCalculationDictionary);
  FreeAndNil(FFilter);
  FreeAndNil(FCellDataDictionary);
  FreeAndNil(FGroupDictionary);
  FOptions.Free;
  FreeAndNil(FSortIndexDictionary);
  {$IFDEF SUPPORTABSTRACT}
  FreeAndNil(FFieldIndexDictionary);
  {$ENDIF}
  inherited;
end;

procedure TTMSFNCDataGridData.DoGetCellDisplayValue(ACell: TTMSFNCDataGridCellCoord;
  AData: TTMSFNCDataGridCellValue; var AValue: string);
begin
  if Assigned(OnGetCellDisplayValue) then
    OnGetCellDisplayValue(Self, ACell, AData, AValue);
end;

procedure TTMSFNCDataGridData.DoGetCellFormatting(
  ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue;
  var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean);
begin
  if Assigned(Adapter) then
    Adapter.GetCellFormatting(ACell, AData, AFormatting, AConvertSettings, ACanFormat);

  if Assigned(OnGetCellFormatting) then
    OnGetCellFormatting(Self, ACell, AData, AFormatting, AConvertSettings, ACanFormat);
end;

procedure TTMSFNCDataGridData.DoGetCustomGroup(ACell: TTMSFNCDataGridCellCoord;
  AData: TTMSFNCDataGridCellValue; ALevel: Integer; var AGroup: string);
begin
  if Assigned(OnGetCustomGroup) then
    OnGetCustomGroup(Self, ACell, AData, ALevel, AGroup);
end;

procedure TTMSFNCDataGridData.DoGetGroupOptions(AColumns: TArray<Integer>; ALevel: Integer;
  var AOptions: TTMSFNCDataGridDataGroupOptions);
begin
  if Assigned(OnGetGroupOptions) then
    OnGetGroupOptions(Self, AColumns, ALevel, AOptions);
end;

procedure TTMSFNCDataGridData.DoGetSortOptions(AColumn: Integer;
  ALevel: Integer; var AOptions: TTMSFNCDataGridDataSortOptions);
begin
  if Assigned(OnGetSortOptions) then
    OnGetSortOptions(Self, AColumn, ALevel, AOptions);
end;

procedure TTMSFNCDataGridData.DoLoadCellData(AColumn, ARow: Integer;
  var AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(OnLoadCellData) then
    OnLoadCellData(Self, AColumn, ARow, AValue);
end;

procedure TTMSFNCDataGridData.DoSaveCellData(AColumn, ARow: Integer;
  var AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(OnSaveCellData) then
    OnSaveCellData(Self, AColumn, ARow, AValue);
end;

procedure TTMSFNCDataGridData.ExpandNode(ARow: Integer);
var
  I, p: Integer;
  lvl: Integer;
begin
  if not IsRowNode(ARow) then
  begin
    LogMessage('Row ' + ARow.ToString + ' cannot be expanded.');
    Exit;
  end;

  if FExpandAll = 0 then
  begin
    p := GetParentRow(ARow);
    if p <> -1 then
    begin
      if RowNodeState[p] = grnsCollapsed then
      begin
        LogMessage('Expand parent node first');
        Exit;
      end;
    end;
  end;

  RowNodeState[ARow] := grnsExpanded;
  lvl := RowNodeLevel[ARow];

  BeginUpdate;
  try
    for I := ARow + 1 to ARow + RowNodeChildCount[ARow] do
    begin
      if RowNodeLevel[I] = lvl + 1 then
        UnhideRow(I);

      if IsRowNode(I) and (RowNodeState[I] = grnsExpanded) then
        ExpandNode(I);
    end;

    DoRowExpand(ARow);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.EndUsingCellData;
var
  d: TTMSFNCDataGridDataTempData;
begin
  d := TempDataList.Last;
  if Assigned(d) then
    TempDataList.Remove(d);
end;

procedure TTMSFNCDataGridData.ExpandAllNodes;
var
  k: Integer;
begin
  BeginUpdate;
  Inc(FExpandAll);
  try
    for k in CellDataDictionary.Keys do
    begin
      if Assigned(CellDataDictionary[k]) and (CellDataDictionary[k].&Type = grtNode) and IsRowValid(k) then
        ExpandNode(k);
    end;
  finally
    Dec(FExpandAll);
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.DoRowCollapse(ARow: Integer);
begin
  if Assigned(Adapter) then
    Adapter.RowCollapse(ARow);

  if Assigned(OnRowCollapse) then
    OnRowCollapse(Self, ARow);
end;

procedure TTMSFNCDataGridData.DoRowExpand(ARow: Integer);
begin
  if Assigned(Adapter) then
    Adapter.RowExpand(ARow);

  if Assigned(OnRowExpand) then
    OnRowExpand(Self, ARow);
end;

function TTMSFNCDataGridData.DoCustomColumnCalculation(AColumn, ALevel, AFromRow,
  AToRow: Integer): Double;
begin
  Result := 0;
  if Assigned(OnCustomColumnCalculation) then
    Result := OnCustomColumnCalculation(Self, AColumn, ALevel, AFromRow, AToRow);
end;

function TTMSFNCDataGridData.DoCustomRowCalculation(ARow, AFromColumn,
  AToColumn: Integer): Double;
begin
  Result := 0;
  if Assigned(OnCustomRowCalculation) then
    Result := OnCustomRowCalculation(Self, ARow, AFromColumn, AToColumn);
end;

function TTMSFNCDataGridData.DoCustomCompare(AColumn, ARow1, ARow2: Integer;
  AData1, AData2: TTMSFNCDataGridCellValue; ALevel: Integer): Integer;
begin
  Result := 0;
  if Assigned(OnCustomCompare) then
    Result := OnCustomCompare(Self, AColumn, ARow1, ARow2, AData1, AData2, ALevel);
end;

procedure TTMSFNCDataGridData.DoGetColumnCalculationOptions(AColumn, ALevel: Integer;
  var AOptions: TTMSFNCDataGridDataColumnCalculationOptions);
begin
  if Assigned(OnGetColumnCalculationOptions) then
    OnGetColumnCalculationOptions(Self, AColumn, ALevel, AOptions);
end;

procedure TTMSFNCDataGridData.DoGetRowCalculationOptions(ARow, ALevel: Integer;
  var AOptions: TTMSFNCDataGridDataRowCalculationOptions);
begin
  if Assigned(OnGetRowCalculationOptions) then
    OnGetRowCalculationOptions(Self, ARow, AOptions);
end;

procedure TTMSFNCDataGridData.DoGetCellData(ACell: TTMSFNCDataGridCellCoord;
  var AData: TTMSFNCDataGridCellValue);
begin
  if Assigned(Adapter) then
    Adapter.GetCellData(ACell, AData);

  if Assigned(OnGetCellData) then
    OnGetCellData(Self, ACell, AData);
end;

procedure TTMSFNCDataGridData.DoGetCellDataProperties(
  ACell: TTMSFNCDataGridCellCoord; ACellItem: TTMSFNCDataGridCellItem);
begin
  if Assigned(Adapter) then
    Adapter.GetCellDataProperties(ACell, ACellItem);

  if Assigned(OnGetCellDataProperties) then
    OnGetCellDataProperties(Self, ACell, ACellItem);
end;

function TTMSFNCDataGridData.FetchData(ACellData: TTMSFNCDataGridCellItem;
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellValue;
var
  v: TTMSFNCDataGridCellValue;
begin
  {$IFDEF WEBLIB}
  Result := nil;
  {$ELSE}
  Result := TTMSFNCDataGridCellValue.Empty;
  {$ENDIF}

  if Assigned(ACellData) and IsCellValid(ACell) then
  begin
    v := ACellData.Value;
    DoGetCellData(ACell, v);
    Result := v;
    DoGetCellDataProperties(ACell, ACellData);
  end;
end;

procedure TTMSFNCDataGridData.FillGridData(ADoFixed, ARandom: Boolean;
  ARandomNumber: Integer = 100);
var
  i, j: Integer;
  ro, co, re, ce: Integer;
  v: Integer;
begin
  BeginUpdate;
  try
    if ADoFixed then
    begin
      ro := 0;
      co := 0;
      re := RowCount - 1;
      ce := ColumnCount - 1;
    end
    else
    begin
      ro := FixedRowCount;
      co := FixedColumnCount;
      re := RowCount - 1 - FixedBottomRowCount;
      ce := ColumnCount - 1 - FixedRightColumnCount;
    end;

    for i := ro to re do
    begin
      for j := co to ce do
      begin
        if ARandom then
        begin
          v := Random(ARandomNumber);
          Cells[j, i] := v;
        end
        else
          Cells[j, i] := IntToStr(j) + ':' + IntToStr(i);
      end;
    end;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridData.Find(AText: string; AParameters: TTMSFNCDataGridDataFindParameters; AStartCell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := InternalFind(AStartCell, AText, AParameters);
end;

function TTMSFNCDataGridData.Find(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  Result := Find(AText, AParameters, MakeCell(-1, -1));
end;

function TTMSFNCDataGridData.Find(AText: string): TTMSFNCDataGridCellCoord;
begin
  Result := Find(AText, []);
end;

function TTMSFNCDataGridData.FindAll(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters;
  var AResults: Integer): TTMSFNCDataGridCellCoord;
var
  StartCell, pt: TTMSFNCDataGridCellCoord;
begin
  StartCell := MakeCell(-1,-1);
  AResults := 0;

  Result := InternalFind(StartCell, AText, AParameters);

  if (gfnAutoGoto in FFindParameters) then
    FFindParameters := FFindParameters - [gfnAutoGoto];

  if (Result.Column <> -1) and (Result.Row <> -1) then
  begin
    Inc(AResults);

    repeat
      pt := FindNext;
      if (pt.Column <> -1) and (pt.Row <> -1) then
        Inc(AResults);
    until (pt.Column = -1);
  end;
end;

function TTMSFNCDataGridData.FindFirst(AText: string;
  AParameters: TTMSFNCDataGridDataFindParameters): TTMSFNCDataGridCellCoord;
begin
  FFindCell := Find(AText, AParameters, MakeCell(-1,-1));
  Result := FFindCell;
end;

function TTMSFNCDataGridData.FindNext(ADown: Boolean): TTMSFNCDataGridCellCoord;
begin
  if ADown then
    FFindParameters := FFindParameters - [gfnBackward]
  else
    FFindParameters := FFindParameters + [gfnBackward];

  Result := FindNext;
end;

function TTMSFNCDataGridData.FindNext: TTMSFNCDataGridCellCoord;
begin
  FFindCell := Find(FFindCache, FFindParameters, FFindCell);
  Result := FFindCell;
end;

function TTMSFNCDataGridData.GetRootRow(ARow: Integer): Integer;
var
  R: Integer;
begin
  Result := -1;
  if not IsRowValid(ARow) then
    Exit;

  for R := ARow - 1 downto FixedRowCount do
  begin
    if IsRowNode(R) and (RowNodeLevel[R] = 0) then
    begin
      Result := R;
      Exit;
    end;
  end;
end;

function TTMSFNCDataGridData.GetRowData(ARow: Integer): TTMSFNCDataGridRowItem;
begin
  Result := nil;
  if IsRowValid(ARow) then
  begin
    CellDataDictionary.TryGetValue(ARow, Result);
    if not Assigned(Result) then
      Result := CreateRowData(ARow);
  end;
end;

function TTMSFNCDataGridData.GetBooleans(AColumn, ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := False;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).BooleanValue;
end;

function TTMSFNCDataGridData.GetCellDataExtended(AColumn,
  ARow: Integer): TTMSFNCDataGridCellItemExtended;
var
  c: TTMSFNCDataGridCellCoord;
  cd: TTMSFNCDataGridCellItem;
begin
  c.Column := AColumn;
  c.Row := ARow;

  Result := nil;

  if IsCellValid(c) then
  begin
    cd := GetCellDataForRow(GetRowData(c.Row), c.Column, c.Row, True);
    if IsExtendedCell(cd) then
      Result := AsExtendedCell(cd);
  end;
end;

function TTMSFNCDataGridData.GetCellData(AColumn,
  ARow: Integer): TTMSFNCDataGridCellItem;
var
  c: TTMSFNCDataGridCellCoord;
  d: TTMSFNCDataGridCellItem;
begin
  c.Column := AColumn;
  c.Row := ARow;

  Result := nil;

  d := TempDataList.Find(MakeCell(AColumn, ARow));

  if Assigned(d) then
    Result := d
  else
  begin
    if IsCellValid(c) then
      Result := GetCellDataForRow(GetRowData(c.Row), c.Column, c.Row, False);
  end;
end;

function TTMSFNCDataGridData.GetCellDataDictionary: TTMSFNCDataGridDataDictionary;
begin
  if not Assigned(FCellDataDictionary) then
    FCellDataDictionary := TTMSFNCDataGridDataDictionary.Create([doOwnsValues]);

  Result := FCellDataDictionary;
end;

function TTMSFNCDataGridData.GetCellDataForRow(ARowData: TTMSFNCDataGridRowItem; AColumn, ARow: Integer; AExtended: Boolean): TTMSFNCDataGridCellItem;
var
  c, cc: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  if Assigned(ARowData) and IsCellValid(MakeCell(AColumn, ARow)) then
  begin
    ARowData.Data.TryGetValue(AColumn, c);
    if not Assigned(c) then
      Result := CreateCellDataForRow(ARowData, AColumn, ARow, AExtended)
    else
      Result := c;

    if Assigned(c) and not IsExtendedCell(c) and (AExtended or NeedsItemExtended(MakeCell(AColumn, ARow))) then
    begin
      cc := TTMSFNCDataGridCellItem.Create;
      try
        cc.Assign(c);
        ARowData.Data.Remove(AColumn);
        Result := CreateCellDataForRow(ARowData, AColumn, ARow, AExtended);
        Result.Assign(cc);
      finally
        cc.Free;
      end;
    end;

    if Assigned(Result) and Result.Invalid and not GetIsLoading then
    begin
      Result.Invalid := False;
      CreateDisplayValue(Result, MakeCell(AColumn, ARow), FetchData(Result, MakeCell(AColumn, ARow)));
    end;
  end;
end;

function TTMSFNCDataGridData.GetCellKind(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellKind;
begin
  Result := gckCell;

  if IsCellFixed(ACell) or IsCellFreeze(ACell) then
  begin
    if IsCellFixedRoot(ACell) then
      Result := gckFixedRootCell
    else if IsCellFreezeRoot(ACell) then
      Result := gckFreezeRootCell
    else if (ACell.Row >= FixedRowCount) and ((ACell.Column < FixedColumnCount) or (ACell.Column >= ColumnCount - FixedRightColumnCount)) then
    begin
      if IsCellFreezeFixed(ACell) then
        Result := gckFreezeFixedColumnCell
      else
        Result := gckFixedColumnCell
    end
    else if (ACell.Column >= FixedColumnCount) and ((ACell.Row < FixedRowCount) or (ACell.Row >= RowCount - FixedBottomRowCount)) then
    begin
      if IsCellFreezeFixed(ACell) then
        Result := gckFreezeFixedRowCell
      else
        Result := gckFixedRowCell
    end
    else if (ACell.Row >= FixedRowCount) and (ACell.Row < RowCount - FixedBottomRowCount) and (ACell.Column >= FixedColumnCount) and (ACell.Column < FixedColumnCount + FreezeColumnCount) then
      Result := gckFreezeColumnCell
    else if (ACell.Column >= FixedColumnCount) and (ACell.Column < ColumnCount - FixedRightColumnCount) and (ACell.Row >= FixedRowCount) and (ACell.Row < FixedRowCount + FreezeRowCount) then
      Result := gckFreezeRowCell
  end;
end;

function TTMSFNCDataGridData.GetCells(AColumn, ARow: Integer): TTMSFNCDataGridCellValue;
var
  cd: TTMSFNCDataGridCellItem;
begin
  cd := CellData[AColumn, ARow];
  Result := FetchData(cd, MakeCell(AColumn, ARow));
end;

function TTMSFNCDataGridData.GetCellSize(ACell: TTMSFNCDataGridCellCoord): TSizeF;
var
  c: TTMSFNCDataGridCellCoord;
  cs, rs, k: Integer;
  totalw, totalh: Double;
begin
  c := BaseCell(ACell);
  cs := ColumnSpan(c);
  rs := RowSpan(c);

  totalw := 0;
  if cs > 0 then
  begin
    for K := c.Column to c.Column + cs - 1 do
      totalw := totalw + ColumnWidths[K];
  end
  else
    totalw := ColumnWidths[c.Column];

  totalh := 0;
  if rs > 0 then
  begin
    for K := c.Row to c.Row + rs - 1 do
      totalh := totalh + RowHeights[K];
  end
  else
    totalh := RowHeights[c.Row];

  Result.cx := totalw;
  Result.cy := totalh;
end;

function TTMSFNCDataGridData.GetColumnCalculationDictionary: TTMSFNCDataGridDataColumnCalculationDictionary;
begin
  if not Assigned(FColumnCalculationDictionary) then
    FColumnCalculationDictionary := TTMSFNCDataGridDataColumnCalculationDictionary.Create([doOwnsValues]);

  Result := FColumnCalculationDictionary;
end;

function TTMSFNCDataGridData.GetRowCalculationDictionary: TTMSFNCDataGridDataRowCalculationDictionary;
begin
  if not Assigned(FRowCalculationDictionary) then
    FRowCalculationDictionary := TTMSFNCDataGridDataRowCalculationDictionary.Create([doOwnsValues]);

  Result := FRowCalculationDictionary;
end;

function TTMSFNCDataGridData.GetRowCalculation(
  ARow: Integer; AName: string): TTMSFNCDataGridDataRowCalculationArray;
var
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
begin
  Result := nil;
  if not IsRowValid(ARow) then
    Exit;

  if RowCalculationDictionary.ContainsKey(ARow) then
  begin
    l := RowCalculationDictionary[ARow];
    if Assigned(l) and l.ContainsKey(AName) then
      Result := l[AName];
  end;
end;

function TTMSFNCDataGridData.GetColumnCalculation(
  AColumn: Integer; AName: string): TTMSFNCDataGridDataColumnCalculationArray;
var
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
begin
  Result := nil;
  if not IsColumnValid(AColumn) then
    Exit;

  if ColumnCalculationDictionary.ContainsKey(AColumn) then
  begin
    l := ColumnCalculationDictionary[AColumn];
    if Assigned(l) and l.ContainsKey(AName) then
      Result := l[AName];
  end;
end;

function TTMSFNCDataGridData.GetControls(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControl;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).Control;
end;

function TTMSFNCDataGridData.GetCellMergeType(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridDataCellMergeType;
begin
  Result := gcmtInvalid;
  if IsCellValid(ACell) then
  begin
    if IsCellFreezeFixed(ACell) or IsCellFixedRoot(ACell) then
      Result := gcmtFixedRoot
    else if IsCellFreezeRoot(ACell) then
      Result := gcmtFreezeRoot
    else if IsCellFixed(ACell) then
      Result := gcmtFixed
    else if IsCellFreeze(ACell) then
      Result := gcmtFreeze
    else
      Result := gcmtNormal;
  end;
end;

function TTMSFNCDataGridData.GetFilter: TTMSFNCDataGridDataFilter;
begin
  if not Assigned(FFilter) then
    FFilter := TTMSFNCDataGridDataFilter.Create(nil, TTMSFNCDataGridDataFilterData);

  Result := FFilter;
end;

function TTMSFNCDataGridData.GetFloats(AColumn, ARow: Integer): Double;
var
  fmt: TFormatSettings;
  f: TTMSFNCDataGridDataFormatting;
  v: TTMSFNCDataGridCellValue;
  cl: TTMSFNCDataGridCellCoord;
  a: Boolean;
begin
  Result := 0.0;
  cl := MakeCell(AColumn, ARow);
  if IsCellValid(cl) then
  begin
    v := Cells[AColumn, ARow];
    f := DefaultFormatting;
    fmt := FormatSettings;
    a := True;
    DoGetCellFormatting(cl, v, f, fmt, a);
    if a then
      Result := ValueToFloat(v, fmt)
    else
      Result := ValueToFloat(v, FormatSettings);
  end;
end;

function TTMSFNCDataGridData.GetGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
begin
  if not Assigned(FGroupDictionary) then
    FGroupDictionary := TTMSFNCDataGridDataGroupDictionary.Create([doOwnsValues]);

  Result := FGroupDictionary;
end;

function TTMSFNCDataGridData.GetHasCellData(AColumn, ARow: Integer): Boolean;
var
  c: TTMSFNCDataGridCellCoord;
  d: TTMSFNCDataGridCellItem;
  r: TTMSFNCDataGridRowItem;
begin
  c.Column := AColumn;
  c.Row := ARow;
  Result := False;
  if IsCellValid(c) then
  begin
    if CellDataDictionary.TryGetValue(c.Row, r) then
    begin
      if Assigned(r) and r.Data.TryGetValue(c.Column, d) then
        Result := Assigned(d);
    end;
  end;
end;

function TTMSFNCDataGridData.GetHasColumnCalculation(AColumn: Integer;
  AName: string): Boolean;
var
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
  cma: TTMSFNCDataGridDataColumnCalculationArray;
begin
  Result := False;
  if ColumnCalculationDictionary.TryGetValue(AColumn, l) then
    if Assigned(l) and l.TryGetValue(AName, cma) then
      Result := Length(cma) > 0;
end;

function TTMSFNCDataGridData.GetHasRowCalculation(ARow: Integer;
  AName: string): Boolean;
var
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
  cma: TTMSFNCDataGridDataRowCalculationArray;
begin
  Result := False;
  if RowCalculationDictionary.TryGetValue(ARow, l) then
    if Assigned(l) and l.TryGetValue(AName, cma) then
      Result := Length(cma) > 0;
end;

function TTMSFNCDataGridData.GetHasMergeInfo(AColumn, ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := False;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).HasMergeInfo;
end;

function TTMSFNCDataGridData.GetInts(AColumn, ARow: Integer): Int64;
var
  fmt: TFormatSettings;
  f: TTMSFNCDataGridDataFormatting;
  v: TTMSFNCDataGridCellValue;
  cl: TTMSFNCDataGridCellCoord;
  a: Boolean;
begin
  Result := 0;
  cl := MakeCell(AColumn, ARow);
  if IsCellValid(cl) then
  begin
    v := Cells[AColumn, ARow];
    f := DefaultFormatting;
    fmt := FormatSettings;
    a := True;
    DoGetCellFormatting(cl, v, f, fmt, a);
    if a then
      Result := ValueToInt64(v, fmt)
    else
      Result := ValueToInt64(v, FormatSettings);
  end;
end;

function TTMSFNCDataGridData.GetManagedObjects(AColumn, ARow: Integer): TObject;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).ManagedObject;
end;

function TTMSFNCDataGridData.GetMaxLevel: Integer;
var
  K: Integer;
begin
  Result := 0;
  for K in CellDataDictionary.Keys do
    if Assigned(CellDataDictionary[K]) then
      Result := Max(Result, CellDataDictionary[K].NodeLevel);
end;

function TTMSFNCDataGridData.GetMergeInfo(AColumn,
  ARow: Integer): TTMSFNCDataGridCellMergeInfo;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := DefaultMergeInfo;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).MergeInfo;
end;

function TTMSFNCDataGridData.GetObjects(AColumn, ARow: Integer): TObject;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).&Object;
end;

function TTMSFNCDataGridData.GetParentRow(ARow: Integer): Integer;
var
  R, lvl: Integer;
begin
  Result := -1;
  if not IsRowValid(ARow) then
    Exit;

  lvl := RowNodeLevel[ARow];

  for R := ARow - 1 downto FixedRowCount do
  begin
    if IsRowNode(R) and (RowNodeLevel[R] + 1 = lvl) then
    begin
      Result := R;
      Exit;
    end;
  end;
end;

function TTMSFNCDataGridData.GetStoredCells(AColumn,
  ARow: Integer): TTMSFNCDataGridCellValue;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) then
    Result := cd.Value;
end;

function TTMSFNCDataGridData.GetStrings(AColumn, ARow: Integer): string;
begin
  Result := FormatDisplayValue(MakeCell(AColumn, ARow), Cells[AColumn, ARow]);
end;

function TTMSFNCDataGridData.GetStrippedCells(AColumn, ARow: Integer): string;
begin
  Result := TTMSFNCUtils.HTMLStrip(Strings[AColumn, ARow]);
end;

function TTMSFNCDataGridData.GetTempDataList: TTMSFNCDataGridDataTempDataList;
begin
  if not Assigned(FTempDataList) then
    FTempDataList := TTMSFNCDataGridDataTempDataList.Create;

  Result := FTempDataList;
end;

procedure TTMSFNCDataGridData.GoToCell(ACell: TTMSFNCDataGridCellCoord;
  ASelect: Boolean);
begin

end;

procedure TTMSFNCDataGridData.Group(AColumns: TArray<Integer>; ASortColumns: TArray<Integer> = nil;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil);
var
  grpi: TTMSFNCDataGridDataGroupInfo;
begin
  grpi.Columns := AColumns;
  grpi.SortColumns := ASortColumns;
  Group(grpi, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
end;

procedure TTMSFNCDataGridData.Group(AGroupInfo: TTMSFNCDataGridDataGroupInfo;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback = nil;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback = nil;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil);
var
  c: TArray<Integer>;
  I: Integer;
begin
  if GroupDictionary.Count > 0 then
  begin
    LogMessage('Grouping already applied, please ungroup first.');
    Exit;
  end;

  SetLength(c, ColumnCount);
  for I := FixedColumnCount to ColumnCount - 1 - FixedRightColumnCount do
    c[I] := I;

  if HasMergedCells(c, FixedRowCount, RowCount - 1 - FixedBottomRowCount) then
  begin
    LogMessage('Grouping cannot be executed, please split merged cells first.');
    Exit;
  end;

  InternalGroup(GroupDictionary, AGroupInfo, 0, FixedRowCount, RowCount - 1 - FixedBottomRowCount, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallBack, ASortOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupAverage(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupAverage([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.Group(AColumn: Integer);
begin
  Group([AColumn]);
end;

function TTMSFNCDataGridData.IsCellBase(ACell: TTMSFNCDataGridCellCoord): Boolean;
var
  c: TTMSFNCDataGridCellCoord;
begin
  c := BaseCell(ACell);
  Result := (c.Column = ACell.Column) and (c.Row = ACell.Row);
end;

function TTMSFNCDataGridData.IsCellHidden(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := not IsCellVisible(ACell);
end;

function TTMSFNCDataGridData.IsCellMerged(ACell: TTMSFNCDataGridCellCoord): Boolean;
var
  c: TTMSFNCDataGridCellCoord;
begin
  c := BaseCell(ACell);
  Result := HasMergeInfo[c.Column, c.Row];
end;

function TTMSFNCDataGridData.IsGrouped: Boolean;
begin
  Result := GroupDictionary.Count > 0;
end;

function TTMSFNCDataGridData.IsCellVisible(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
var
  c: TTMSFNCDataGridCellCoord;
  cs, rs, k: Integer;
  ch, rh: Boolean;
begin
  c := BaseCell(ACell);
  cs := ColumnSpan(c);
  rs := RowSpan(c);

  ch := not IsColumnHidden(c.Column);

  if (cs > 0) and not ch then
  begin
    for K := c.Column + 1 to c.Column + cs - 1 do
    begin
      if not IsColumnHidden(K) then
      begin
        ch := True;
        Break;
      end;
    end;
  end;

  rh := not IsRowHidden(c.Row);

  if (rs > 0) and not rh then
  begin
    for K := c.Row + 1 to c.Row + rs - 1 do
    begin
      if not IsRowHidden(K) then
      begin
        rh := True;
        Break;
      end;
    end;
  end;

  Result := ch and rh;
end;

function TTMSFNCDataGridData.MatchCell(AColumn, ARow: Integer): Boolean;
var
  res1,res2: Boolean;
  ct, cs: string;
  ic: Integer;
  s: string;
begin
  s := FFindCache;

  res2 := True;
  cs := Strings[AColumn, ARow];

  if not (gfnMatchCase in FFindParameters) then
    ct := UpperCase(cs)
  else
    ct := cs;

  if gfnIgnoreHTMLTags in FFindParameters then
  begin
    ct := TTMSFNCUtils.HTMLStrip(ct);
    ct := TTMSFNCUtils.UnFixMarkup(ct);
  end;

  if (s = '""') or (s = '') then
  begin
    Result := ct = '';
    Exit;
  end;

  ic := Pos(s, ct);

  if gfnMatchStart in FFindParameters then
    res1 := ic = 1
  else
    res1 := ic > 0;

  if gfnMatchFull in FFindParameters then
    res2 := s = ct;

  if gfnMatchRegular in FFindParameters then
    Result := TTMSFNCUtils.MatchStrEx(s, ct, (gfnMatchCase in FFindParameters))
  else
    Result := res1 and res2;
end;

function TTMSFNCDataGridData.MatchFilter(ARow: Integer): Boolean;
var
  i,vp: Integer;
  s,su,cond:string;
  temp: Boolean;
  APrefix, ASuffix, prfx: string;
  f: TTMSFNCDataGridDataFilterData;
  c: TTMSFNCDataGridCellCoord;
begin
  Result := True;

  for i := 1 to FFilter.Count do
  begin
    f := FFilter.Items[i - 1];
    if f.Enabled then
    begin
      prfx := '';

      APrefix := f.Prefix;
      ASuffix := f.Suffix;

      c := MakeCell(f.Column, ARow);
      c := BaseCell(c);

      case FFilter.Items[i - 1].Data of
        gfcNormal: s := Strings[c.Column, c.Row];
        gfcStripHTML:
        begin
          s := Strippedcells[c.Column, c.Row];
          s := TTMSFNCUtils.UnFixMarkup(s);
        end;
        gfcRow: s := RowText[ARow];
      end;

      s := Trim(s);
      cond := f.Condition;

      if f.Data = gfcRow then
        temp := Pos(Uppercase(cond), Uppercase(s)) > 0
      else
      begin

        if APrefix <> '' then
          if Pos(APrefix,s) = 1 then
            Delete(s, 1, Length(f.Prefix));

        if ASuffix <> '' then
          if Pos(ASuffix, s) = 1 + Length(s) - Length(ASuffix) then
            Delete(s, 1 + Length(s) - Length(ASuffix), Length(s));

        temp := true;
        try
          case f.Method of
          gfmExpression: temp := TTMSFNCUtils.MatchStrEx(cond, s, f.CaseSensitive);
          gfmLiteral:
            begin
              if (pos('|',cond) > 0) then
              begin
                temp := false;
                while (TTMSFNCUtils.VarPos('|',cond,vp) > 0) do
                begin
                  su := copy(cond,1,vp - 1);
                  temp := temp or (su = s);
                  delete(cond,1,vp);
                  su := cond;
                end;
                temp := temp or (su = s);
              end
              else
              if cond = '""' then
                temp := s = ''
              else
                temp := cond = s;
            end;
          end;
        except
          temp := false;
        end;
      end;

      case FFilter.Items[i - 1].Operation of
      gfoSHORT:
        begin
          Result := temp;
          if not Result then
            Break;
        end;
      gfoNONE: Result := temp;
      gfoAND: Result := Result AND temp;
      gfoOR:  Result := Result OR temp;
      gfoXOR: Result := Result XOR temp;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridData.MergeCells(AColumn, ARow, AColumnCount, ARowCount: integer);
var
  i, j: Integer;
  hasExistingMerge: Boolean;
  mi: TTMSFNCDataGridCellMergeInfo;
  minColumn, maxColumn, minRow, maxRow: Integer;
begin
  hasExistingMerge := False;
  minColumn := AColumn;
  maxColumn := AColumn + AColumnCount - 1;
  minRow := ARow;
  maxRow := ARow + ARowCount - 1;

  for i := AColumn to maxColumn do
  begin
    for j := ARow to maxRow do
    begin
      if HasMergeInfo[i, j] then
      begin
        hasExistingMerge := True;
        mi := MergeInfo[i, j];
        minColumn := Min(minColumn, i - mi.ColumnOffset);
        maxColumn := Max(maxColumn, i - mi.ColumnOffset + mi.ColumnSpan - 1);
        minRow := Min(minRow, j - mi.RowOffset);
        maxRow := Max(maxRow, j - mi.RowOffset + mi.RowSpan - 1);
      end;
    end;
  end;

  if hasExistingMerge or CanMergeCells(AColumn, ARow, AColumnCount, ARowCount) then
  begin
    BeginUpdate;
    try
      for i := minColumn to maxColumn do
      begin
        for j := minRow to maxRow do
        begin
          MergeInfo[i, j] := Default(TTMSFNCDataGridCellMergeInfo);
          MergedColumns[i] := False;
          MergedRows[j] := False;
        end;
      end;

      for i := minColumn to maxColumn do
      begin
        MergedColumns[i] := True;
        for j := minRow to maxRow do
        begin
          MergedRows[j] := True;
          MergeInfo[i, j] := CreateMergeInfo(i - minColumn, j - minRow, maxColumn - minColumn + 1, maxRow - minRow + 1);
        end;
      end;
    finally
      EndUpdate;
    end;
  end
  else
  begin
    // Log a warning if merging is not possible
    LogMessage('Cells with Columns [' + FormatNumberRange(AColumn, AColumnCount) + '] and Rows [' + FormatNumberRange(ARow, ARowCount) + '] cannot be merged.', glmWarning);
  end;
end;

procedure TTMSFNCDataGridData.MergeColumn(AColumn: Integer);
begin
  MergeColumns(AColumn, 1);
end;

procedure TTMSFNCDataGridData.MergeColumns(AColumn, ACount: Integer);
begin
  MergeCells(AColumn, FixedRowCount, ACount, RowCount - FixedBottomRowCount);
end;

procedure TTMSFNCDataGridData.MergeRange(ARange: TTMSFNCDataGridCellCoordRange);
begin
  MergeCells(ARange.StartCell.Column, ARange.StartCell.Row, ARange.StopCell.Column - ARange.StartCell.Column + 1, ARange.StopCell.Row - ARange.StartCell.Row + 1);
end;

procedure TTMSFNCDataGridData.MergeRow(ARow: Integer);
begin
  MergeRows(ARow, 1);
end;

procedure TTMSFNCDataGridData.MergeRows(ARow, ACount: Integer);
begin
  MergeCells(FixedColumnCount, ARow, ColumnCount - FixedRightColumnCount, ACount);
end;

procedure TTMSFNCDataGridData.SwapCellData(AFromCell,
  AToCell: TTMSFNCDataGridCellCoord; ASwapDirection: TTMSFNCDataGridDataSwapDirection);
var
  p, pt: TPair<Integer, TTMSFNCDataGridCellItem>;
  r, rt: TTMSFNCDataGridRowItem;
  v, vt: TTMSFNCDataGridCellItem;
begin
  v := nil;
  r := nil;

  if CellDataDictionary.TryGetValue(AFromCell.Row, r) then
  begin
    if Assigned(r) then
    begin
      p := r.Data.ExtractPair(AFromCell.Column);
      v := p.Value;
    end;
  end;

  if not Assigned(r) then
    r := GetRowData(AFromCell.Row);

  vt := nil;
  rt := nil;
  if CellDataDictionary.TryGetValue(AToCell.Row, rt) then
  begin
    if Assigned(rt) then
    begin
      pt := rt.Data.ExtractPair(AToCell.Column);
      vt := pt.Value;
    end;
  end;

  if not Assigned(rt) then
    rt := GetRowData(AToCell.Row);

  if Assigned(rt) and (ASwapDirection in [gswdBoth, gswdToOnly]) then
    rt.Data.AddOrSetValue(AToCell.Column, v)
  else if Assigned(v) then
    v.Free;

  if Assigned(r) and (ASwapDirection in [gswdBoth, gswdFromOnly]) then
    r.Data.AddOrSetValue(AFromCell.Column, vt)
  else if Assigned(vt) then
    vt.Free;
end;

procedure TTMSFNCDataGridData.SwapCells(AFromCell,
  AToCell: TTMSFNCDataGridCellCoord);
begin
  SwapCellData(AFromCell, AToCell, gswdBoth);
end;

procedure TTMSFNCDataGridData.SwapColumnData(AFromColumn, AToColumn: Integer);
var
  cw, cwt: Double;
  hc, hct, mc, mct: Boolean;
  co, cot, cov, cotv: Integer;
  cm, cmt: TPair<Integer, TTMSFNCDataGridDataColumnCalculationItemDictionary>;
  sd, sdt: TPair<Integer, TTMSFNCDataGridSortDirection>;
  r: Integer;
  f, ft: TTMSFNCDataGridDataFilterData;
begin
  cw := HiddenColumnWidths[AFromColumn];
  cwt := HiddenColumnWidths[AToColumn];

  ColumnWidths[AFromColumn] := cwt;
  ColumnWidths[AToColumn] := cw;

  hc := HiddenColumns[AFromColumn];
  hct := HiddenColumns[AToColumn];

  HiddenColumns[AFromColumn] := hct;
  HiddenColumns[AToColumn] := hc;

  cm := ColumnCalculationDictionary.ExtractPair(AFromColumn);
  cmt := ColumnCalculationDictionary.ExtractPair(AToColumn);

  ColumnCalculationDictionary.AddOrSetValue(AToColumn, cm.Value);
  ColumnCalculationDictionary.AddOrSetValue(AFromColumn, cmt.Value);

  mc := MergedColumns[AFromColumn];
  mct := MergedColumns[AToColumn];

  MergedColumns[AFromColumn] := mct;
  MergedColumns[AToColumn] := mc;

  sd := SortIndexDictionary.ExtractPair(AFromColumn);
  sdt := SortIndexDictionary.ExtractPair(AToColumn);

  SortIndexDictionary.AddOrSetValue(AToColumn, sd.Value);
  SortIndexDictionary.AddOrSetValue(AFromColumn, sdt.Value);

  f := nil;
  if Filter.HasFilter(AFromColumn) then
    f := Filter.ColumnFilter[AFromColumn];

  ft := nil;
  if Filter.HasFilter(AToColumn) then
    ft := Filter.ColumnFilter[AToColumn];

  if Assigned(f) then
    f.Column := AToColumn;

  if Assigned(ft) then
    ft.Column := AFromColumn;

  for r := 0 to RowCount - 1 do
    SwapCellData(MakeCell(AFromColumn, r), MakeCell(AToColumn, r), gswdBoth);

  co := ColumnReverseOrders[AFromColumn];
  cot := ColumnReverseOrders[AToColumn];

  cov := ColumnOrders[co];
  cotv := ColumnOrders[cot];

  SetColumnOrder(co, cotv);
  SetColumnOrder(cot, cov);
end;

procedure TTMSFNCDataGridData.SwapColumn(AFromColumn, AToColumn: Integer);
begin
  if CanSwapColumn(AFromColumn, AToColumn) then
  begin
    BeginUpdate;
    try
      SwapColumnData(AFromColumn, AToColumn);
    finally
     EndUpdate;
    end;
  end
  else
    LogMessage('Columns [' + AFromColumn.ToString + ', ' + AToColumn.ToString + '] cannot be swapped.', glmWarning);
end;

procedure TTMSFNCDataGridData.SwapRowData(AFromRow, AToRow: Integer);
var
  rh, rht: Double;
  hr, hrt, mr, mrt: Boolean;
  ro, rot, rov, rotv: Integer;
  r, rt: TPair<Integer, TTMSFNCDataGridRowItem>;
  rm, rmt: TPair<Integer, TTMSFNCDataGridDataRowCalculationItemDictionary>;
  fr, frt: Boolean;
begin
  rh := HiddenRowHeights[AFromRow];
  rht := HiddenRowHeights[AToRow];

  RowHeights[AFromRow] := rht;
  RowHeights[AToRow] := rh;

  hr := HiddenRows[AFromRow];
  hrt := HiddenRows[AToRow];

  HiddenRows[AFromRow] := hrt;
  HiddenRows[AToRow] := hr;

  fr := FilteredRows[AFromRow];
  frt := FilteredRows[AToRow];

  FilteredRows[AFromRow] := frt;
  FilteredRows[AToRow] := fr;

  rm := RowCalculationDictionary.ExtractPair(AFromRow);
  rmt := RowCalculationDictionary.ExtractPair(AToRow);

  RowCalculationDictionary.AddOrSetValue(AToRow, rm.Value);
  RowCalculationDictionary.AddOrSetValue(AFromRow, rmt.Value);

  mr := MergedRows[AFromRow];
  mrt := MergedRows[AToRow];

  MergedRows[AFromRow] := mrt;
  MergedRows[AToRow] := mr;

  r := CellDataDictionary.ExtractPair(AFromRow);
  rt := CellDataDictionary.ExtractPair(AToRow);

  CellDataDictionary.AddOrSetValue(AToRow, r.Value);
  CellDataDictionary.AddOrSetValue(AFromRow, rt.Value);

  ro := RowReverseOrders[AFromRow];
  rot := RowReverseOrders[AToRow];

  rov := RowOrders[ro];
  rotv := RowOrders[rot];

  SetRowOrder(ro, rotv);
  SetRowOrder(rot, rov);
end;

procedure TTMSFNCDataGridData.SwapRow(AFromRow, AToRow: Integer);
begin
  if CanSwapRow(AFromRow, AToRow) then
  begin
    BeginUpdate;
    try
      SwapRowData(AFromRow, AToRow);
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Rows [' + AFromRow.ToString + ', ' + AToRow.ToString + '] cannot be swapped.', glmWarning);
end;

procedure TTMSFNCDataGridData.MoveCellData(AFromCell,
  AToCell: TTMSFNCDataGridCellCoord);
begin
  SwapCellData(AFromCell, AToCell, gswdToOnly);
end;

procedure TTMSFNCDataGridData.MoveColumn(AFromColumn, AToColumn: Integer);
var
  I: Integer;
begin
  if CanMoveColumn(AFromColumn, AToColumn) then
  begin
    BeginUpdate;
    try
      if AToColumn > AFromColumn then
      begin
        for I := AFromColumn to AToColumn - 1 do
          SwapColumn(I, I + 1);
      end
      else
      begin
        for I := AFromColumn downto AToColumn + 1 do
          SwapColumn(I - 1, I);
      end;
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Columns [' + AFromColumn.ToString + ', ' + AToColumn.ToString + '] cannot be moved.', glmWarning);
end;

procedure TTMSFNCDataGridData.MoveColumnData(AFromColumn, AToColumn: Integer);
var
  pb: TPair<Integer, Boolean>;
  pd: TPair<Integer, Double>;
  ps: TPair<Integer, TTMSFNCDataGridSortDirection>;
  pc: TPair<Integer, TTMSFNCDataGridDataColumnCalculationItemDictionary>;
  R: Integer;
  f: TTMSFNCDataGridDataFilterData;
begin
  if ColumnWidthDictionary.ContainsKey(AFromColumn) then
  begin
    pd := ColumnWidthDictionary.ExtractPair(AFromColumn);
    ColumnWidths[AToColumn] := pd.Value;
    ColumnPositionDictionary.Remove(AFromColumn);
    ColumnWidthDictionary.Remove(AFromColumn);
  end;

  if HiddenColumnDictionary.ContainsKey(AFromColumn) then
  begin
    pb := HiddenColumnDictionary.ExtractPair(AFromColumn);
    HiddenColumns[AToColumn] := pb.Value;
    HiddenColumnDictionary.Remove(AFromColumn);
  end;

  if ColumnCalculationDictionary.ContainsKey(AFromColumn) then
  begin
    pc := ColumnCalculationDictionary.ExtractPair(AFromColumn);
    ColumnCalculationDictionary.AddOrSetValue(AToColumn, pc.Value);
    ColumnCalculationDictionary.Remove(AFromColumn);
  end;

  if MergedColumnDictionary.ContainsKey(AFromColumn) then
  begin
    pb := MergedColumnDictionary.ExtractPair(AFromColumn);
    MergedColumns[AToColumn] := pb.Value;
    MergedColumnDictionary.Remove(AFromColumn);
  end;

  if SortIndexDictionary.ContainsKey(AFromColumn) then
  begin
    ps := SortIndexDictionary.ExtractPair(AFromColumn);
    SortIndexDictionary.AddOrSetValue(AToColumn, ps.Value);
    SortIndexDictionary.Remove(AFromColumn);
  end;

  if Filter.HasFilter(AFromColumn) then
  begin
    f := Filter.ColumnFilter[AFromColumn];
    f.Column := AToColumn;
  end;

  for R := RowCount - 1 downto 0 do
    MoveCellData(MakeCell(AFromColumn, R), MakeCell(AToColumn, R));

  if not ColumnOrderDictionary.ContainsKey(AFromColumn) then
    SetColumnOrder(AFromColumn, AFromColumn);
end;

procedure TTMSFNCDataGridData.MoveRow(AFromRow, AToRow: Integer);
var
  I: Integer;
begin
  if CanMoveRow(AFromRow, AToRow) then
  begin
    BeginUpdate;
    try
      if AToRow > AFromRow then
      begin
        for I := AFromRow to AToRow - 1 do
          SwapRow(I, I + 1);
      end
      else
      begin
        for I := AFromRow downto AToRow + 1 do
          SwapRow(I - 1, I);
      end;
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Rows [' + AFromRow.ToString + ', ' + AToRow.ToString + '] cannot be moved.', glmWarning);
end;

procedure TTMSFNCDataGridData.MoveRowData(AFromRow, AToRow: Integer);
var
  pb, fr: TPair<Integer, Boolean>;
  pd: TPair<Integer, Double>;
  pr: TPair<Integer, TTMSFNCDataGridRowItem>;
begin
  if RowHeightDictionary.ContainsKey(AFromRow) then
  begin
    pd := RowHeightDictionary.ExtractPair(AFromRow);
    RowHeights[AToRow] := pd.Value;
    RowPositionDictionary.Remove(AFromRow);
    RowHeightDictionary.Remove(AFromRow);
  end;

  if HiddenRowDictionary.ContainsKey(AFromRow) then
  begin
    pb := HiddenRowDictionary.ExtractPair(AFromRow);
    HiddenRows[AToRow] := pb.Value;
    HiddenRowDictionary.Remove(AFromRow);
  end;

  if FilteredRowDictionary.ContainsKey(AFromRow) then
  begin
    fr := FilteredRowDictionary.ExtractPair(AFromRow);
    FilteredRows[AToRow] := fr.Value;
    FilteredRowDictionary.Remove(AFromRow);
  end;

  if MergedRowDictionary.ContainsKey(AFromRow) then
  begin
    pb := MergedRowDictionary.ExtractPair(AFromRow);
    MergedRows[AToRow] := pb.Value;
    MergedRowDictionary.Remove(AFromRow);
  end;

  if CellDataDictionary.ContainsKey(AFromRow) then
  begin
    pr := CellDataDictionary.ExtractPair(AFromRow);
    CellDataDictionary.AddOrSetValue(AToRow, pr.Value);
    CellDataDictionary.Remove(AFromRow);
  end;

  if not RowOrderDictionary.ContainsKey(AFromRow) then
    SetRowOrder(AFromRow, AFromRow);
end;

function TTMSFNCDataGridData.NeedsItemExtended(ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if Assigned(Adapter) then
    Result := (Adapter as TTMSFNCDataGridDataAdapter).NeedsItemExtended(ACell);
end;

procedure TTMSFNCDataGridData.RandomFill(ADoFixed: Boolean = False; ARandomNumber: Integer = 100);
begin
  FillGridData(ADoFixed, True, ARandomNumber);
end;

procedure TTMSFNCDataGridData.RefreshData(AUpdate: Boolean = True);
begin
  RefreshData(MakeCellRange(0, 0, ColumnCount - 1, RowCount - 1), AUpdate);
end;

procedure TTMSFNCDataGridData.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClasses([TTMSFNCDataGridCellItem, TTMSFNCDataGridCellItemExtended,
    TTMSFNCDataGridDataGroup, TTMSFNCDataGridDataFilterData]);
end;

procedure TTMSFNCDataGridData.RefreshData(
  ACellRange: TTMSFNCDataGridCellCoordRange; AUpdate: Boolean = True);
var
  c, r: Integer;
  ri: TTMSFNCDataGridRowItem;
  ci: TTMSFNCDataGridCellItem;
begin
  if AUpdate then
    BeginUpdate;

  try
    for R := ACellRange.StartCell.Row to ACellRange.StopCell.Row do
    begin
      if CellDataDictionary.TryGetValue(R, ri) then
      begin
        if Assigned(ri) then
        begin
          for C := ACellRange.StartCell.Column to ACellRange.StopCell.Column do
          begin
            if ri.Data.TryGetValue(C, ci) then
            begin
              if Assigned(ci) then
                ci.Invalid := True;
            end;
          end;
        end;
      end;
    end;
  finally
    if AUpdate then
      EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.RefreshData(ACell: TTMSFNCDataGridCellCoord; AUpdate: Boolean = True);
begin
  RefreshData(MakeCellRange(ACell.Column, ACell.Row, ACell.Column, ACell.Row), AUpdate);
end;

procedure TTMSFNCDataGridData.RemoveFilter;
begin
  BeginUpdate;
  try
    FilteredRowDictionary.Clear;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.RemoveNodeRow(ARow: Integer);
begin
  ClearNodeRow(ARow);
end;

procedure TTMSFNCDataGridData.ClearNodeRow(ARow: Integer);
var
  R, C: Integer;
begin
  BeginUpdate;
  try
    RowTypes[ARow] := grtDefault;
    RowNodeLevel[ARow] := 0;
    c := RowNodeChildCount[ARow];
    RowNodeChildCount[ARow] := 0;
    RowNodeState[ARow] := grnsExpanded;

    for R := ARow + 1 to ARow + c do
      RowNodeLevel[R] := 0;

    SplitCell(MakeCell(FixedColumnCount, ARow));
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridData.Replace(AOriginalValue,
  ANewValue: string): Integer;
begin
  Result := Replace(AOriginalValue, ANewValue, []);
end;

function TTMSFNCDataGridData.Replace(AOriginalValue, ANewValue: string;
  AParameters: TTMSFNCDataGridDataFindParameters): integer;
var
  fs: boolean;
  c, prc: TTMSFNCDataGridCellCoord;
  ov, nv: string;
  nr: Integer;
  p: TTMSFNCDataGridDataFindParameters;
begin
  fs := True;
  c := MakeCell(-1, -1);
  nr := 0;

  BeginUpdate;

  try

    while fs or ((c.Column <> -1) and (c.Row <> -1)) do
    begin
      p := AParameters;
      p := p - [gfnAutoGoto];
      prc := c;
      c := Find(AOriginalValue, p, c);

      if (c.Column <> -1) and (c.Row <> -1) then
      begin
        ov := Strings[c.Column, c.Row];
        nv := StringReplace(ov, AOriginalValue, ANewValue, [rfReplaceAll, rfIgnoreCase]);
        Cells[c.Column, c.Row] := nv;

        Inc(nr);
      end;

      fs := False;
    end;

    if gfnAutoGoTo in AParameters then
      GoToCell(prc);

    Result := nr;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridData.RowSpan(ACell: TTMSFNCDataGridCellCoord): Integer;
begin
  Result := 0;
  if HasMergeInfo[ACell.Column, ACell.Row] then
    Result := MergeInfo[ACell.Column, ACell.Row].RowSpan;
end;

procedure TTMSFNCDataGridData.SetOptions(const Value: TTMSFNCDataGridDataOptions);
begin
  FOptions.Assign(Value);
end;

procedure TTMSFNCDataGridData.SetAdapter(
  const Value: TTMSFNCDataGridDataAdapter);
begin
  if FAdapter <> Value then
  begin
    FAdapter := Value;
    if FBlockAdd then
      Exit;

    FBlockAdd := True;
    if Assigned(FAdapter) then
    begin
      FAdapter.DataInstance := Self;
      FAdapter.Initialize;
    end;
    FBlockAdd := False;
  end;
end;

procedure TTMSFNCDataGridData.SetBooleans(AColumn, ARow: Integer;
  const Value: Boolean);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.BooleanValue := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridData.SetCells(AColumn, ARow: Integer; const Value: TTMSFNCDataGridCellValue);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellData[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.Value := Value;
    if Assigned(Adapter) and (Adapter is TTMSFNCDataGridDataAdapter) then
      (Adapter as TTMSFNCDataGridDataAdapter).SetCellData(MakeCell(AColumn, ARow), Value);
    DoUpdate;

    {$IFDEF SUPPORTABSTRACT}
    NotifyObjectDataRefresh;
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridData.SetColumnCalculation(AColumn: Integer; AName: string; const Value: TTMSFNCDataGridDataColumnCalculationArray);
var
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if ColumnCalculationDictionary.ContainsKey(AColumn) then
    l := ColumnCalculationDictionary[AColumn]
  else
  begin
    l := TTMSFNCDataGridDataColumnCalculationItemDictionary.Create;
    ColumnCalculationDictionary.Add(AColumn, l);
  end;

  if Assigned(l) then
  begin
    if l.ContainsKey(AName) then
    begin
      if Length(Value) = 0 then
      begin
        l.Remove(AName);
        if l.Count = 0 then
          ColumnCalculationDictionary.Remove(AColumn);
      end
      else
        l[AName] := Value;
    end
    else if (Length(Value) > 0) then
      l.Add(AName, Value);      
  end;

  UpdateColumnCalculation(AColumn, AName);
end;

procedure TTMSFNCDataGridData.SetRowCalculation(ARow: Integer; AName: string; const Value: TTMSFNCDataGridDataRowCalculationArray);
var
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
begin
  if not IsRowValid(ARow) then
    Exit;

  if RowCalculationDictionary.ContainsKey(ARow) then
    l := RowCalculationDictionary[ARow]
  else
  begin
    l := TTMSFNCDataGridDataRowCalculationItemDictionary.Create;
    RowCalculationDictionary.Add(ARow, l);
  end;

  if Assigned(l) then
  begin
    if l.ContainsKey(AName) then
    begin
      if Length(Value) = 0 then
      begin
        l.Remove(AName);
        if l.Count = 0 then
          RowCalculationDictionary.Remove(ARow);
      end
      else
        l[AName] := Value;
    end
    else if Length(Value) > 0 then
      l.Add(AName, Value);
  end;

  UpdateRowCalculation(ARow, AName);
end;

procedure TTMSFNCDataGridData.SetControls(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControl);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    RemoveCellControlFromCache(AColumn, ARow);
    cd.Control := Value;
    if Assigned(Value) then
      Value.Parent := nil;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridData.SetFloats(AColumn, ARow: Integer;
  const Value: Double);
begin
  Cells[AColumn, ARow] := Value;
end;

procedure TTMSFNCDataGridData.SetInts(AColumn, ARow: Integer;
  const Value: Int64);
begin
  Cells[AColumn, ARow] := Value;
end;

procedure TTMSFNCDataGridData.SetManagedObjects(AColumn, ARow: Integer;
  const Value: TObject);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.ManagedObject := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridData.SetMergeInfo(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellMergeInfo);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.MergeInfo := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridData.SetNodeRow(ARow, AChildCount: Integer; ALevel: Integer = 0; AMerge: Boolean = True);
var
  R: Integer;
begin
  if RowTypes[ARow] = grtNode then
  begin
    LogMessage('Row is already configured as node');
    Exit;
  end;

  for R := ARow + 1 to ARow + AChildCount do
  begin
    if RowTypes[R] <> grtDefault then
    begin
      LogMessage('Row cannot be configured as node, one or more rows already contain node information');
      Exit;
    end;
  end;

  BeginUpdate;
  try
    RowTypes[ARow] := grtNode;
    RowNodeLevel[ARow] := ALevel;
    RowNodeChildCount[ARow] := AChildCount;

    for R := ARow + 1 to ARow + AChildCount do
      RowNodeLevel[R] := ALevel + 1;

    RowNodeState[ARow] := grnsExpanded;

    if AMerge then
      MergeCells(FixedColumnCount, ARow, ColumnCount - FixedRightColumnCount - FixedColumnCount, 1);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.SetObjects(AColumn, ARow: Integer;
  const Value: TObject);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.&Object := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ShiftRowOrder(AStartRow, AShift: Integer);
var
  k, v: Integer;

  procedure ShiftGroups(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary);
  var
    ks: string;
    grp: TTMSFNCDataGridDataGroup;
  begin
    if Assigned(AGroupDictionary) then
    begin
      for ks in AGroupDictionary.Keys do
      begin
        grp := AGroupDictionary[ks];
        if Assigned(grp) then
        begin
          if grp.Row >= AStartRow then
            grp.Row := grp.Row + AShift;

          if grp.SummaryRow >= AStartRow then
            grp.SummaryRow := grp.SummaryRow + AShift;

          ShiftGroups(grp.GroupDictionary);
        end;
      end;
    end;
  end;

begin
  for k in RowOrderDictionary.Keys do
  begin
    if (RowOrderDictionary[k] <> -1) and (RowOrderDictionary[k] >= AStartRow) then
    begin
      v := RowOrderDictionary[k];
      RowOrderDictionary[k] := v + AShift;
      RowReverseOrderDictionary.AddOrSetValue(v + AShift, k);
    end;
  end;

  ShiftGroups(GroupDictionary);
end;

procedure TTMSFNCDataGridData.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; AFromRow,
  AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
var
  I: Integer;
begin
  SortIndexDictionary.Clear;
  for I := 0 to Length(AColumns) - 1 do
  begin
    if I <= Length(ADirections) - 1 then
      SortIndexDictionary.AddOrSetValue(AColumns[I], ADirections[I])
    else
      SortIndexDictionary.AddOrSetValue(AColumns[I], Options.Sorting.Direction);
  end;

  InternalSort(AColumns, ADirections, 0, AFromRow, AToRow, ACustomCompareCallback, ASortOptionsCallback);
end;

procedure TTMSFNCDataGridData.SortIndexed;
var
  I: Integer;
  c: TArray<Integer>;
  d: TArray<TTMSFNCDataGridSortDirection>;
begin
  for I in SortIndexDictionary.Keys do
  begin
    SetLength(c, Length(c) + 1);
    c[Length(c) - 1] := I;
    SetLength(d, Length(d) + 1);
    d[Length(d) - 1] := SortIndexDictionary[I];
  end;

  Sort(c, d);
end;

procedure TTMSFNCDataGridData.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection; AFromRow, AToRow: Integer);
begin
  SortIndexDictionary.Clear;
  SortIndexDictionary.AddOrSetValue(AColumn, ADirection);
  InternalSort([AColumn], [ADirection], 0, AFromRow, AToRow, nil, nil);
end;

procedure TTMSFNCDataGridData.Sort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  Sort([AColumn], [ADirection]);
end;

procedure TTMSFNCDataGridData.Sort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback = nil; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback = nil);
var
  I: Integer;
begin
  SortIndexDictionary.Clear;
  for I := 0 to Length(AColumns) - 1 do
  begin
    if I <= Length(ADirections) - 1 then
      SortIndexDictionary.AddOrSetValue(AColumns[I], ADirections[I])
    else
      SortIndexDictionary.AddOrSetValue(AColumns[I], Options.Sorting.Direction);
  end;

  InternalSort(AColumns, ADirections, 0, FixedRowCount, RowCount - 1 - FixedBottomRowCount, ACustomCompareCallback, ASortOptionsCallback);
end;

function TTMSFNCDataGridData.InternalGroup(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary; AGroupInfo: TTMSFNCDataGridDataGroupInfo;
  ALevel, AFromRow, AToRow: Integer;
  ACustomGroupCallback: TTMSFNCDataGridDataGetCustomGroupCallback;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  AGroupOptionsCallback: TTMSFNCDataGridDataGetGroupOptionsCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback): Integer;
var
  I: Integer;
  k, vh: string;
  grp, agrp: TTMSFNCDataGridDataGroup;
  grpi: TTMSFNCDataGridDataGroupInfo;
  opt: TTMSFNCDataGridDataGroupOptions;

  function GetGroupMergedValue(ARow: Integer): string;
  var
    K: Integer;
    v: string;
    c: Integer;
    d: TTMSFNCDataGridCellValue;
  begin
    Result := '';
    for K := 0 to Length(AGroupInfo.Columns) - 1 do
    begin
      c := AGroupInfo.Columns[K];
      v := Strings[c, ARow];
      d := Cells[c, ARow];

      if opt.UseFirstRowAsHeader then
      begin
        vh := Strings[c, 0];
        if vh <> '' then
          v := Strings[c, 0] + ': ' + v;
      end;

      if Assigned(ACustomGroupCallback) then
        ACustomGroupCallback(MakeCell(c, ARow), d, ALevel, v)
      else
        DoGetCustomGroup(MakeCell(c, ARow), d, ALevel, v);

      Result := Result + v;

      if K < Length(AGroupInfo.Columns) - 1 then
        Result := Result + opt.MergeDelimiter;
    end;
  end;

  procedure FinishGroup;
  begin
    if Assigned(agrp) then
    begin
      if opt.Summary and not opt.SummaryInHeader and Assigned(agrp) then
      begin
        if CanInsertRow(i) then
          InsertRow(i)
        else
          AddRow;

        RowTypes[i] := grtSummary;
        RowHeights[i] := opt.SummaryHeight;
        RowNodeChildCount[agrp.Row] := i - agrp.Row - 1;
        RowNodeLevel[i] := ALevel;

        agrp.FSummaryRow := i;
        if opt.MergeSummary then
          MergeCells(FixedColumnCount, i, ColumnCount - FixedRightColumnCount - FixedColumnCount, 1);

        Inc(i);
        Inc(AToRow);
        Inc(Result);
      end
      else
        RowNodeChildCount[agrp.Row] := i - agrp.Row - 1;
    end;
  end;

  procedure SubGroup;
  var
    rt: Integer;
  begin
    if (Length(AGroupInfo.SubColumns) > 0) and Assigned(agrp) then
    begin
      grpi.Columns := AGroupInfo.SubColumns[0].Columns;
      grpi.SubColumns := AGroupInfo.SubColumns[0].SubColumns;
      grpi.SortColumns := AGroupInfo.SubColumns[0].SortColumns;
      rt := InternalGroup(agrp.GroupDictionary, grpi, ALevel + 1, agrp.Row + 1, i - 1, ACustomGroupCallback, ACustomCompareCallback, AGroupOptionsCallback, ASortOptionsCallback);
      i := i + rt;
      AToRow := AToRow + rt;
      Result := Result + rt;
    end;
  end;

begin
  Result := 0;

  if Length(AGroupInfo.Columns) = 0 then
  begin
    LogMessage('No columns available for grouping.');
    Exit;
  end;

  for I := 0 to Length(AGroupInfo.Columns) - 1 do
  begin
    if not IsColumnValid(AGroupInfo.Columns[I]) then
    begin
      LogMessage('Column(s) [' + FormatArray(AGroupInfo.Columns) + '] cannot be grouped.');
      Exit;
    end;
  end;

  if Assigned(Adapter) then
  begin
    case Adapter.GroupMode of
      agmAdapter, agmNone: raise Exception.Create('Grouping is only possible when LoadMode = almAllRecords.');
    end;
  end;

  BeginUpdate;
  Inc(FUpdateGrouping);

  try
    opt := GetGroupOptions;

    if Assigned(AGroupOptionsCallback) then
      AGroupOptionsCallback(AGroupInfo.Columns, ALevel, opt)
    else
      DoGetGroupOptions(AGroupInfo.Columns, ALevel, opt);

    agrp := nil;

    SortIndexDictionary.Clear;
    for I := 0 to Length(AGroupInfo.SortColumns) - 1 do
    begin
      if Assigned(AGroupInfo.SortColumns) then
      begin
        if I <= Length(AGroupInfo.SortDirections) - 1 then
          SortIndexDictionary.AddOrSetValue(AGroupInfo.SortColumns[I], AGroupInfo.SortDirections[I])
        else
          SortIndexDictionary.AddOrSetValue(AGroupInfo.SortColumns[I], Options.Sorting.Direction);
      end
      else
      begin
        if I <= Length(AGroupInfo.SortDirections) - 1 then
          SortIndexDictionary.AddOrSetValue(AGroupInfo.Columns[I], AGroupInfo.SortDirections[I])
        else
          SortIndexDictionary.AddOrSetValue(AGroupInfo.Columns[I], Options.Sorting.Direction);
      end;
    end;

    if (AToRow - AFromRow > 1) then
    begin
      if Assigned(AGroupInfo.SortColumns) then
        InternalSort(AGroupInfo.SortColumns, AGroupInfo.SortDirections, ALevel, AFromRow, AToRow, ACustomCompareCallback, ASortOptionsCallback)
      else if opt.AutoSort then
        InternalSort(AGroupInfo.Columns, AGroupInfo.SortDirections, ALevel, AFromRow, AToRow, ACustomCompareCallback, ASortOptionsCallback);
    end;

    i := AFromRow;
    while i <= AToRow do
    begin
      k := GetGroupMergedValue(i);

      if not AGroupDictionary.TryGetValue(k, grp) then
      begin
        SubGroup;
        FinishGroup;

        InsertRow(i);
        RowHeights[i] := opt.HeaderHeight;
        Inc(AToRow);
        Inc(Result);

        if opt.MergeHeader and ((not opt.SummaryInHeader and opt.Summary) or not opt.Summary) then
          MergeCells(FixedColumnCount, i, ColumnCount - FixedRightColumnCount - FixedColumnCount, 1);

        Cells[FixedColumnCount, i] := k;

        RowTypes[i] := grtNode;
        RowNodeLevel[i] := ALevel;

        grp := TTMSFNCDataGridDataGroup.Create;
        grp.FSummaryRow := -1;
        grp.FRow := i;

        AGroupDictionary.Add(k, grp);
        agrp := grp;
      end
      else
        RowNodeLevel[i] := ALevel + 1;

      Inc(i);
    end;

    SubGroup;
    FinishGroup;

    if opt.HideColumns then
    begin
      for I := 0 to Length(AGroupInfo.Columns) - 1 do
        HideColumn(AGroupInfo.Columns[I]);
    end;

    if FFilterApplied then
      InternalFilterGroups(AGroupDictionary);
  finally
    FGroupInfo := AGroupInfo;
    Dec(FUpdateGrouping);
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalGroupCalculation(
  AColumns: TArray<Integer>; AOutputRow, AFromRow, AToRow: Integer; AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
var
  d: TTMSFNCDataGridDataColumnCalculationDictionary;
begin
  d := GetColumnCalculations(AColumns, AOutputRow, AFromRow, AToRow, AMethod, gctGroup, ACalculationOptionsCallback, ACustomCalculationCallback);
  try
    InternalGroupCalculation(GroupDictionary, d, 0);
  finally
    d.Free;
  end;
end;

procedure TTMSFNCDataGridData.InternalGroupCalculation(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary; ACalculations: TTMSFNCDataGridDataColumnCalculationDictionary;
  ALevel: Integer);
var
  m: Integer;
  grp: TTMSFNCDataGridDataGroup;
  k, dck: string;
  d: TTMSFNCDataGridDataColumnCalculationDictionary;
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
  cm: TTMSFNCDataGridDataColumnCalculation;
  cmau, cma: TTMSFNCDataGridDataColumnCalculationArray;
  I: Integer;
  cc: integer;
begin
  if (AGroupDictionary.Count = 0) then
    Exit;

  BeginUpdate;
  try
    for k in AGroupDictionary.Keys do
    begin
      grp := AGroupDictionary[k];

      if Assigned(grp) then
      begin
        cc := RowNodeChildCount[grp.Row];

        d := TTMSFNCDataGridDataColumnCalculationDictionary.Create([doOwnsValues]);
        try
          for I in ACalculations.Keys do
          begin
            l := ACalculations[I];
            for dck in l.Keys do
            begin
              cma := l[dck];

              SetLength(cmau, 0);
              for m := 0 to Length(cma) - 1 do
              begin
                cm := cma[m];
                if cm.&Type = gctGroup then
                begin
                  SetLength(cmau, Length(cmau) + 1);
                  if (cm.FromRow = -1) or (cm.ToRow = -1) then
                  begin
                    cm.FromRow := grp.Row + 1;
                    cm.ToRow := grp.Row + cc;
                  end;

                  cmau[Length(cmau) - 1] := cm;
                end;
              end;
            end;

            if Length(cmau) > 0 then
            begin
              l := TTMSFNCDataGridDataColumnCalculationItemDictionary.Create;
              l.AddOrSetValue(dck, cmau);
              d.AddOrSetValue(I, l);
            end;
          end;

          InternalColumnCalculation(d, grp, ALevel);
        finally
          d.Free;
        end;

        InternalGroupCalculation(grp.GroupDictionary, ACalculations, ALevel + 1);
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>; ALevel, AFromRow, AToRow: Integer; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback; ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
var
  acol, R, K, minr, maxr, I: Integer;
  c: Integer;
  a: TArray<TTMSFNCDataGridDataSortInfo>;
  ac: TArray<TTMSFNCDataGridDataSortCellInfo>;
  d: TTMSFNCDataGridDataSortInfo;
  cd: TTMSFNCDataGridDataSortCellInfo;
  soa: TArray<TTMSFNCDataGridDataSortOptions>;
  so: TTMSFNCDataGridDataSortOptions;
begin
  if Length(AColumns) = 0 then
  begin
    LogMessage('No columns available for sorting.');
    Exit;
  end;

  for I := 0 to Length(AColumns) - 1 do
  begin
    if not IsColumnValid(AColumns[I]) then
    begin
      LogMessage('Column(s) [' + FormatArray(AColumns) + '] cannot be sorted.');
      Exit;
    end;
  end;

  minr := AFromRow;
  maxr := AToRow;

  if maxr <= minr then
  begin
    LogMessage('Rows cannot be sorted, please make sure that there is more than 1 sortable row');
    Exit;
  end;

  if Assigned(Adapter) then
  begin
    case Adapter.SortMode of
      asmNone: Exit;
      asmAdapter:
      begin
        Adapter.SortData;
        Exit;
      end;
    end;
  end;

  if HasMergedCells(AColumns, AFromRow, AToRow) then
  begin
    LogMessage('Column(s) [' + FormatArray(AColumns) + '] cannot be sorted, please split merged cells first.');
    Exit;
  end;

  acol := 0;
  c := AColumns[acol];

  BeginUpdate;
  try
    SetLength(a, maxr - minr + 1);
    SetLength(soa, Length(AColumns));

    for I := 0 to Length(AColumns) - 1 do
    begin
      so := GetSortOptions;

      if I <= Length(ADirections) - 1 then
        so.Direction := ADirections[I];

      if Assigned(ASortOptionsCallback) then
        ASortOptionsCallback(AColumns[I], ALevel, so)
      else
        DoGetSortOptions(AColumns[I], ALevel, so);

      soa[I] := so;
    end;

    R := 0;
    for I := minr to maxr do
    begin
      SetLength(ac, Length(AColumns));
      for K := 0 to Length(AColumns) - 1 do
      begin
        cd.Data := Cells[AColumns[K], I];
        cd.Column := AColumns[K];

        cd.Options := soa[K];

        ac[K] := cd;
      end;

      d.Data := ac;
      d.Row := I;

      a[R] := d;
      Inc(R);
    end;

    if Length(a) > 0 then
    begin
      QuickSort<TTMSFNCDataGridDataSortInfo>(a,
        TComparer<TTMSFNCDataGridDataSortInfo>.Construct(
        function (const V1, V2: TTMSFNCDataGridDataSortInfo): Integer
        begin
          Result := CompareRows(c, ALevel, V1, V2, ACustomCompareCallback);
        end)
        , Low(a), High(a),
        procedure (var Values: TArray<TTMSFNCDataGridDataSortInfo>; L, R: Integer)
        var
          tl, tr: TTMSFNCDataGridDataSortInfo;
          ll, rr: Integer;
        begin
          tl := Values[L];
          ll := tl.Row;
          tr := Values[R];
          rr := tr.Row;

          tr.Row := ll;
          tl.Row := rr;
          Values[L] := tr;
          Values[R] := tl;

          SwapRow(tl.Row, tr.Row);
        end
      );
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ShiftColumnOrder(AStartColumn, AShift: Integer);
var
  k, v: Integer;
begin
  for k in ColumnOrderDictionary.Keys do
  begin
    if (ColumnOrderDictionary[k] <> -1) and (ColumnOrderDictionary[k] >= AStartColumn) then
    begin
      v := ColumnOrderDictionary[k];
      ColumnOrderDictionary[k] := v + AShift;
      ColumnReverseOrderDictionary.AddOrSetValue(v + AShift, k);
    end;
  end;
end;

procedure TTMSFNCDataGridData.SplitCell(ACell: TTMSFNCDataGridCellCoord);
var
  fc, fr, tc, tr: Integer;
  mi: TTMSFNCDataGridCellMergeInfo;
  i, j: Integer;
begin
  if HasMergeInfo[ACell.Column, ACell.Row] then
  begin
    BeginUpdate;
    try
      mi := MergeInfo[ACell.Column, ACell.Row];
      fc := ACell.Column - mi.ColumnOffset;
      fr := ACell.Row - mi.RowOffset;
      tc := fc + mi.ColumnSpan;
      tr := fr + mi.RowSpan;

      for i := fc to tc - 1 do
      begin
        MergedColumns[i] := False;
        for j := fr to tr - 1 do
        begin
          MergedRows[j] := False;
          MergeInfo[i, j] := DefaultMergeInfo;
        end;
      end;
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Cell ' + ACell.Column.ToString + ':' + ACell.Row.ToString + ' cannot be split.', glmWarning);
end;

class function TTMSFNCDataGridData.ValueToInteger(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Integer;
var
  v: Int64;
begin
  Result := 0;
  v := ValueToInt64(AValue, AFormatSettings);
  if (v <= MaxInt) and (v >= -MaxInt) then
    Result := v;
end;

class function TTMSFNCDataGridData.ValueToInt64(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Int64;
var
  I64: Int64;
  I: Integer;
  D: Double;
  v: TValue;
  B: Boolean;
begin
  Result := 0;

  v := CellValueToTValue(AValue);
  if v.IsEmpty then
    Exit;

  case v.Kind of
    tkFloat, tkEnumeration, tkInteger{$IFNDEF WEBLIB}, tkInt64{$ENDIF}:
    begin
      if v.TryAsType<Double>(D) then
        Result := Round(D)
      else if v.TryAsType<Int64>(I64) then
        Result := I64
      else if v.TryAsType<Integer>(I) then
        Result := I
      else if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$IFDEF WEBLIB}
    tkBool:
    begin
      if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$ENDIF}
    tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}:
    begin
      if TryStrToFloat(v.AsString, D, AFormatSettings) then
        Result := Round(D)
      else if TryStrToInt64(v.AsString, I64) then
        Result := I64
      else if TryStrToBool(v.AsString, B) then
        Result := Integer(B);
    end;
  end;
end;

class function TTMSFNCDataGridData.ValueToBoolean(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Boolean;
begin
  Result := ValueToInteger(AValue, AFormatSettings) <> 0;
end;

class function TTMSFNCDataGridData.ValueToFloat(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): Double;
var
  I64: Int64;
  I: Integer;
  D: Double;
  v: TValue;
  B: Boolean;
begin
  Result := 0.0;

  v := CellValueToTValue(AValue);
  if v.IsEmpty then
    Exit;

  case v.Kind of
    tkFloat, tkEnumeration, tkInteger{$IFNDEF WEBLIB}, tkInt64{$ENDIF}:
    begin
      if v.TryAsType<Double>(D) then
        Result := D
      else if v.TryAsType<Int64>(I64) then
        Result := I64
      else if v.TryAsType<Integer>(I) then
        Result := I
      else if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$IFDEF WEBLIB}
    tkBool:
    begin
      if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$ENDIF}
    tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}:
    begin
      if TryStrToFloat(v.AsString, D, AFormatSettings) then
        Result := D
      else if TryStrToInt64(v.AsString, I64) then
        Result := I64
      else if TryStrToInt(v.AsString, I) then
        Result := I
      else if TryStrToBool(v.AsString, B) then
        Result := Integer(B);
    end;
  end;
end;

class function TTMSFNCDataGridData.ValueToDateTime(AValue: TTMSFNCDataGridCellValue; AFormatSettings: TFormatSettings): TDateTime;
var
  I64: Int64;
  I: Integer;
  D: Double;
  v: TValue;
  B: Boolean;
  dt: TDateTime;
begin
  Result := 0.0;

  v := CellValueToTValue(AValue);
  if v.IsEmpty then
    Exit;

  case v.Kind of
    tkFloat, tkEnumeration, tkInteger{$IFNDEF WEBLIB}, tkInt64{$ENDIF}:
    begin
      if v.TryAsType<Double>(D) then
        Result := D
      else if v.TryAsType<Int64>(I64) then
        Result := I64
      else if v.TryAsType<Integer>(I) then
        Result := I
      else if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$IFDEF WEBLIB}
    tkBool:
    begin
      if v.TryAsType<Boolean>(B) then
        Result := Integer(B);
    end;
    {$ENDIF}
    tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}:
    begin
      if TryStrToDateTime(v.AsString, dt, AFormatSettings) then
        Result := dt
      else if TryStrToFloat(v.AsString, D, AFormatSettings) then
        Result := D
      else if TryStrToInt64(v.AsString, I64) then
        Result := I64
      else if TryStrToInt(v.AsString, I) then
        Result := I
      else if TryStrToBool(v.AsString, B) then
        Result := Integer(B);
    end;
  end;
end;

class function TTMSFNCDataGridData.ValueIsString(AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := (TypeOfValue(AValue) in [tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}]);
end;

class function TTMSFNCDataGridData.ValueIsInteger(AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := (TypeOfValue(AValue) in [tkEnumeration, tkInteger{$IFNDEF WEBLIB},tkInt64{$ENDIF}{$IFDEF WEBLIB}, tkBool{$ENDIF}]);
end;

class function TTMSFNCDataGridData.ValueIsEmpty(
  AValue: TTMSFNCDataGridCellValue): Boolean;
var
  v: TValue;
begin
  v := CellValueToTValue(AValue);
  Result := v.IsEmpty;
end;

class function TTMSFNCDataGridData.ValueIsFloat(AValue: TTMSFNCDataGridCellValue): Boolean;
begin
  Result := (TypeOfValue(AValue) in [tkFloat]);
end;

procedure TTMSFNCDataGridData.ToggleNode(ARow: Integer);
begin
  if not IsRowNode(ARow) then
  begin
    LogMessage('Row ' + ARow.ToString + ' cannot be toggled.');
    Exit;
  end;

  if RowNodeState[ARow] = grnsCollapsed then
    ExpandNode(ARow)
  else
    CollapseNode(ARow);
end;

class function TTMSFNCDataGridData.TypeOfValue(AValue: TTMSFNCDataGridCellValue): TTypeKind;
var
  v: TValue;
begin
  Result := tkUnknown;

  v := CellValueToTValue(AValue);
  if v.IsEmpty then
    Exit;

  Result := v.Kind;
end;

procedure TTMSFNCDataGridData.GroupAverage(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmAverage, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  GroupCalculation([AColumn], AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.GroupCount(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmCount, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupCustomCalculation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmCustom, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.GroupDistinct(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmDistinct, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupMax(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmMax, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupMin(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmMin, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupStandardDeviation(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmStandardDeviation, ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupSum(AColumns: TArray<Integer>; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, gcmSum, ACalculationOptionsCallback);
end;

function TTMSFNCDataGridData.ColumnAverage(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  c: Double;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  c := ColumnCounting(AColumn, AFromRow, AToRow);
  if c > 0 then
    Result := ColumnSum(AColumn, AFromRow, AToRow) / c
  else
    Result := 0;
end;

function TTMSFNCDataGridData.ColumnCustomCalculation(AColumn: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
begin
  Result := InternalColumnCustomCalculation(AColumn, 0, ACustomCalculationCallback, AFromRow, AToRow);
end;

function TTMSFNCDataGridData.ColumnDistinct(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  sl: TStringList;
  i: integer;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  sl := TStringList.Create;

  try
    sl.Duplicates := dupIgnore;
    sl.Sorted := true;
    sl.CaseSensitive := false;

    for i := AFromRow to AToRow do
    begin
      if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
        and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
          sl.Add(Strings[AColumn, i]);
    end;

    Result := sl.Count;

  finally
    sl.Free;
  end;
end;

procedure TTMSFNCDataGridData.ColumnCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  InternalColumnCalculation(AColumns, -1, -1, -1, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.ColumnCalculation(AColumn: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  ColumnCalculation([AColumn], AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCDataGridData.ColumnCounting(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  i: Integer;
begin
  Result := 0;
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  for i := AFromRow to AToRow do
  begin
    if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
      and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
        Result := Result + 1;
  end;
end;

function TTMSFNCDataGridData.ColumnMax(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  m, f: Double;
  i: Integer;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  m := Floats[AColumn, AFromRow];

  for i := AFromRow to AToRow do
  begin
    if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
      and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
    begin
      try
        f := Floats[AColumn, i];
        if m < f then
          m := f;
      except
      end;
    end;
  end;

  Result := m;
end;

function TTMSFNCDataGridData.ColumnMin(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  m, f: Double;
  i: Integer;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  m := Floats[AColumn, AFromRow];

  for i := AFromRow to AToRow do
  begin
    if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
      and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
    begin
      try
        f := Floats[AColumn, i];
        if m > f then
          m := f;
      except
      end;
    end;
  end;

  Result := m;
end;

function TTMSFNCDataGridData.ColumnStandardDeviation(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  m,d: Double;
  i: Integer;
  rr: Integer;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  m := ColumnAverage(AColumn, AFromRow, AToRow);

  d := 0;

  rr := 0;
  for i := AFromRow to AToRow do
  begin
    if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
      and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
    begin
      d := d + sqr(Floats[AColumn, i] - m);
      Inc(rr);
    end;
  end;

  Result := sqrt(d / (rr + 1));
end;

function TTMSFNCDataGridData.ColumnSum(AColumn: Integer; AFromRow: Integer = -1; AToRow: Integer = -1): Double;
var
  i: Integer;
  sum: Double;
begin
  if (AFromRow = -1) or (AToRow = -1) then
  begin
    AFromRow := FixedRowCount;
    AToRow := RowCount - 1 - FixedBottomRowCount;
  end;

  sum := 0;

  for i := AFromRow to AToRow do
  begin
    if IsCellBase(MakeCell(AColumn, i)) and (RowTypes[i] = grtDefault)
      and ((not Options.Calculations.IncludeHiddenRows and not IsRowHidden(i)) or Options.Calculations.IncludeHiddenRows) then
    begin
      try
        Sum := Sum + Floats[AColumn, i]
      except
      end;
    end;
  end;

  Result := sum;
end;

function TTMSFNCDataGridData.RowAverage(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  c: Double;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  c := RowCounting(ARow, AFromColumn, AToColumn);
  if c > 0 then
    Result := RowSum(ARow, AFromColumn, AToColumn) / c
  else
    Result := 0;
end;

function TTMSFNCDataGridData.RowCustomCalculation(ARow: Integer; ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback = nil; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  if Assigned(ACustomCalculationCallback) then
    Result := ACustomCalculationCallback(ARow, AFromColumn, AToColumn)
  else
    Result := DoCustomRowCalculation(ARow, AFromColumn, AToColumn);
end;

function TTMSFNCDataGridData.RowDistinct(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  sl: TStringList;
  i: integer;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  sl := TStringList.Create;

  try
    sl.Duplicates := dupIgnore;
    sl.Sorted := true;
    sl.CaseSensitive := false;

    for i := AFromColumn to AToColumn do
    begin
      if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
        or Options.Calculations.IncludeHiddenColumns) then
          sl.Add(Strings[i, ARow]);
    end;

    Result := sl.Count;

  finally
    sl.Free;
  end;
end;

procedure TTMSFNCDataGridData.RowCalculation(ARows: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  InternalRowCalculation(ARows, -1, -1, -1, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.RowCalculation(ARow: Integer;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetRowCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomRowCalculationCallback);
begin
  RowCalculation([ARow], AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

function TTMSFNCDataGridData.RowCounting(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  i: Integer;
begin
  Result := 0;
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  for i := AFromColumn to AToColumn do
  begin
    if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
      or Options.Calculations.IncludeHiddenColumns) then
        Result := Result + 1;
  end;
end;

function TTMSFNCDataGridData.RowMax(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  m, f: Double;
  i: Integer;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  m := Floats[AFromColumn, ARow];

  for i := AFromColumn to AToColumn do
  begin
    if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
      or Options.Calculations.IncludeHiddenColumns) then
    begin
      try
        f := Floats[i, ARow];
        if m < f then
          m := f;
      except
      end;
    end;
  end;

  Result := m;
end;

function TTMSFNCDataGridData.RowMin(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  m, f: Double;
  i: Integer;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  m := Floats[AFromColumn, ARow];

  for i := AFromColumn to AToColumn do
  begin
    if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
      or Options.Calculations.IncludeHiddenColumns) then
    begin
      try
        f := Floats[i, ARow];
        if m > f then
          m := f;
      except
      end;
    end;
  end;

  Result := m;
end;

function TTMSFNCDataGridData.RowStandardDeviation(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  m,d: Double;
  i: Integer;
  rr: Integer;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  m := RowAverage(ARow, AFromColumn, AToColumn);

  d := 0;

  rr := 0;
  for i := AFromColumn to AToColumn do
  begin
    if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
      or Options.Calculations.IncludeHiddenColumns) then
    begin
      d := d + sqr(Floats[i, ARow] - m);
      Inc(rr);
    end;
  end;

  Result := sqrt(d / (rr + 1));
end;

function TTMSFNCDataGridData.RowSum(ARow: Integer; AFromColumn: Integer = -1; AToColumn: Integer = -1): Double;
var
  i: Integer;
  sum: Double;
begin
  if (AFromColumn = -1) or (AToColumn = -1) then
  begin
    AFromColumn := FixedColumnCount;
    AToColumn := ColumnCount - 1 - FixedRightColumnCount;
  end;

  sum := 0;

  for i := AFromColumn to AToColumn do
  begin
    if IsCellBase(MakeCell(i, ARow)) and ((not Options.Calculations.IncludeHiddenColumns and not IsColumnHidden(i))
      or Options.Calculations.IncludeHiddenColumns) then
    begin
      try
        Sum := Sum + Floats[i, ARow]
      except
      end;
    end;
  end;

  Result := sum;
end;

procedure TTMSFNCDataGridData.Ungroup;
var
  I: Integer;
  l: TList<Integer>;
  cl: TTMSFNCDataGridCellCoord;
  R: Integer;

  procedure AddGroups(AGroupDictionary: TTMSFNCDataGridDataGroupDictionary);
  var
    k: string;
  begin
    for k in AGroupDictionary.Keys do
    begin
      l.Add(AGroupDictionary[k].Row);
      if AGroupDictionary[k].SummaryRow <> -1 then
        l.Add(AGroupDictionary[k].SummaryRow);

      AddGroups(AGroupDictionary[k].GroupDictionary);
    end;
  end;

begin
  if GroupDictionary.Count = 0 then
  begin
    LogMessage('No Grouping active, please group first.');
    Exit;
  end;

  BeginUpdate;

  Inc(FUpdateGrouping);

  l := TList<Integer>.Create;
  try
    AddGroups(GroupDictionary);

    l.Sort(
      TComparer<Integer>.Construct(
      function (const V1, V2: Integer): Integer
      begin
        Result := CompareValue(V1, V2) * -1;
      end
    ));

    for I in L do
    begin
      cl := MakeCell(FixedColumnCount, I);
      if IsCellMerged(cl) then
        SplitCell(cl);

      for R := I + 1 to I + RowNodeChildCount[I] do
      begin
        RowNodeLevel[R] := 0;
        RowNodeState[R] := grnsExpanded;
        UnhideRow(R);
      end;

      InternalDeleteRows(I, 1);
    end;

    UnhideAllColumns;

    GroupDictionary.Clear;

  finally
    FGroupInfo := DefaultGroupInfo;
    Dec(FUpdateGrouping);
    l.Free;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.UpdateColumnCalculation(AColumn: Integer; AName: string);
var
  cma, cmau: TTMSFNCDataGridDataColumnCalculationArray;
  I: Integer;
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
  d: TTMSFNCDataGridDataColumnCalculationDictionary;
begin
  if HasColumnCalculations[AColumn, AName] then
  begin
    cma := ColumnCalculations[AColumn, AName];

    d := TTMSFNCDataGridDataColumnCalculationDictionary.Create([doOwnsValues]);
    try
      SetLength(cmau, 0);
      for I := 0 to Length(cma) - 1 do
      begin
        if cma[I].&Type = gctNormal then
        begin
          SetLength(cmau, Length(cmau) + 1);
          cmau[Length(cmau) - 1] := cma[I];
        end;
      end;

      if Length(cmau) > 0 then
      begin
        l := TTMSFNCDataGridDataColumnCalculationItemDictionary.Create;
        l.AddOrSetValue(AName, cmau);
        d.AddOrSetValue(AColumn, l);
        InternalColumnCalculation(d, nil, 0);
      end;

      d.Clear;

      SetLength(cmau, 0);
      for I := 0 to Length(cma) - 1 do
      begin
        if cma[I].&Type = gctGroup then
        begin
          SetLength(cmau, Length(cmau) + 1);
          cmau[Length(cmau) - 1] := cma[I];
        end;
      end;

      if Length(cmau) > 0 then
      begin
        l := TTMSFNCDataGridDataColumnCalculationItemDictionary.Create;
        l.AddOrSetValue(AName, cmau);
        d.AddOrSetValue(AColumn, l);
        InternalGroupCalculation(GroupDictionary, d, 0);
      end;
    finally
      d.Free;
    end;
  end;
end;

procedure TTMSFNCDataGridData.UpdateColumnCalculations;
var
  k: Integer;
  I: string;
  l: TTMSFNCDataGridDataColumnCalculationItemDictionary;
begin
  BeginUpdate;
  try
    for k in ColumnCalculationDictionary.Keys do
    begin
      l := ColumnCalculationDictionary[k];
      if Assigned(l) then
      begin
        for I in l.Keys do
          UpdateColumnCalculation(k, I);
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.UpdateRowCalculation(ARow: Integer; AName: string);
var
  cma: TTMSFNCDataGridDataRowCalculationArray;
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
  d: TTMSFNCDataGridDataRowCalculationDictionary;
begin
  if HasRowCalculations[ARow, AName] then
  begin
    cma := RowCalculations[ARow, AName];

    d := TTMSFNCDataGridDataRowCalculationDictionary.Create([doOwnsValues]);
    l := TTMSFNCDataGridDataRowCalculationItemDictionary.Create;
    try
      l.AddOrSetValue(AName, cma);
      d.AddOrSetValue(ARow, l);

      InternalRowCalculation(d);
    finally
      d.Free;
    end;
  end;
end;

procedure TTMSFNCDataGridData.UpdateRowCalculations;
var
  k: Integer;
  I: string;
  l: TTMSFNCDataGridDataRowCalculationItemDictionary;
begin
  BeginUpdate;
  try
    for k in RowCalculationDictionary.Keys do
    begin
      l := RowCalculationDictionary[k];
      if Assigned(l) then
      begin
        for I in l.Keys do
          UpdateRowCalculation(k, I);
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.UpdateCalculations;
begin
  BeginUpdate;
  try
    UpdateColumnCalculations;
    UpdateRowCalculations;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.RemoveCellControlFromCache(AColumn, ARow: Integer);
begin

end;

class function TTMSFNCDataGridData.ValueToString(AValue: TTMSFNCDataGridCellValue): string;
var
  I64: Int64;
  D: Double;
  v: TValue;
  I: Integer;
  B: Boolean;
  l: TStrings;
begin
  Result := '';

  v := CellValueToTValue(AValue);
  if v.IsEmpty then
    Exit;

  case v.Kind of
    tkFloat, tkEnumeration, tkInteger{$IFNDEF WEBLIB}, tkInt64{$ENDIF}:
    begin
      if v.TryAsType<Double>(D) then
        Result := FloatToStr(D)
      else if v.TryAsType<Int64>(I64) then
        Result := IntToStr(I64)
      else if v.TryAsType<Integer>(I) then
        Result := IntToStr(I)
      else if v.TryAsType<Boolean>(B) then
        Result := BoolToStr(B, True);
    end;
    {$IFDEF WEBLIB}
    tkBool:
    begin
      if v.TryAsType<Boolean>(B) then
        Result := BoolToStr(B, True);
    end;
    {$ENDIF}
    tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}: Result := v.AsString;
    tkClass:
    begin
      if v.TryAsType<TStrings>(l) then
        Result := l.Text
    end
    else
      Result := v.ToString;
  end;
end;

procedure TTMSFNCDataGridData.GroupCalculation(AColumns: TArray<Integer>;
  AMethod: TTMSFNCDataGridDataCalculationMethod;
  ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback);
begin
  InternalGroupCalculation(AColumns, -1, -1, -1, AMethod, ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.GroupCount(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupCount([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupCustomCalculation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil;
  ACustomCalculationCallback: TTMSFNCDataGridDataCustomColumnCalculationCallback = nil);
begin
  GroupCustomCalculation([AColumn], ACalculationOptionsCallback, ACustomCalculationCallback);
end;

procedure TTMSFNCDataGridData.GroupDistinct(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupDistinct([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupMax(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupMax([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupMin(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupMin([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupSort(AColumns: TArray<Integer>; ADirections: TArray<TTMSFNCDataGridSortDirection>;
  ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback;
  ASortOptionsCallback: TTMSFNCDataGridDataGetSortOptionsCallback);
var
  I: Integer;

  procedure InternalGroupSort(d: TTMSFNCDataGridDataGroupDictionary);
  var
    k: string;
    grp: TTMSFNCDataGridDataGroup;
    rst, lvl, rstp: Integer;
  begin
    for k in d.Keys do
    begin
      grp := d[k];

      if Assigned(grp) then
      begin
        if Assigned(grp.GroupDictionary) and (grp.GroupDictionary.Count > 0) then
          InternalGroupSort(grp.GroupDictionary)
        else if IsRowNode(grp.Row) then
        begin
          rst := grp.Row + 1;
          rstp := grp.Row + RowNodeChildCount[grp.Row];
          lvl := RowNodeLevel[grp.row];

          if rstp - rst > 1 then
            InternalSort(AColumns, ADirections, lvl, rst, rstp, ACustomCompareCallback, ASortOptionsCallback);
        end;
      end;
    end;
  end;
begin
  SortIndexDictionary.Clear;
  for I := 0 to Length(AColumns) - 1 do
  begin
    if I <= Length(ADirections) - 1 then
      SortIndexDictionary.AddOrSetValue(AColumns[I], ADirections[I])
    else
      SortIndexDictionary.AddOrSetValue(AColumns[I], Options.Sorting.Direction);
  end;

  InternalGroupSort(GroupDictionary);
end;

procedure TTMSFNCDataGridData.GroupSortIndexed;
var
  I: Integer;
  c: TArray<Integer>;
  d: TArray<TTMSFNCDataGridSortDirection>;
begin
  for I in SortIndexDictionary.Keys do
  begin
    SetLength(c, Length(c) + 1);
    c[Length(c) - 1] := I;
    SetLength(d, Length(d) + 1);
    d[Length(d) - 1] := SortIndexDictionary[I];
  end;

  GroupSort(c, d);
end;

procedure TTMSFNCDataGridData.GroupSort(AColumn: Integer; ADirection: TTMSFNCDataGridSortDirection);
begin
  GroupSort([AColumn], [ADirection]);
end;

procedure TTMSFNCDataGridData.GroupStandardDeviation(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupStandardDeviation([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.GroupSum(AColumn: Integer; ACalculationOptionsCallback: TTMSFNCDataGridDataGetColumnCalculationOptionsCallback = nil);
begin
  GroupSum([AColumn], ACalculationOptionsCallback);
end;

procedure TTMSFNCDataGridData.LoadFromXMLData(AFileName: string; AEncoding: TEncoding);
begin
  InternalLoadFromXML(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadFromXMLStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalLoadFromXMLStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadSampleData;
var
  I: Integer;
  J: Integer;
  s, p: string;
  d: TDate;
begin
  BeginUpdate;
  try
    for I := 0 to FixedRowCount - 1 do
    begin
      if I > 0 then
        p := 'Sub '
      else
        p := '';

      for J := FixedColumnCount to ColumnCount - 1 do
      begin
        case J of
          0: s := p + 'ID';
          1: s := p + 'Name';
          2: s := p + 'Joined';
          3: s := p + 'Status';
          else
            s := p + 'Number';
        end;

        Cells[J, I] := s;
      end;
    end;

    for I := FixedRowCount to RowCount - 1 do
    begin
      for J := FixedColumnCount to ColumnCount - 1 do
      begin
        s := '';

        case J of
          0: s := '00' + I.ToString;
          1: begin
            case Random(120) mod 5 of
              0: s := 'David';
              1: s := 'Laura';
              2: s := 'Isaac';
              3: s := 'Marie';
              else
                s := 'Leon';
            end;

            case Random(120) mod 5 of
              0: s := s + ' Kleermakers';
              1: s := s + ' Tailor';
              2: s := s + ' Terzi';
              3: s := s + ' Sastre';
              else
                s := s + ' Schneider';
            end;
          end;
          2: begin
            d := Round(Now) - Random(5475);
            s := DateToStr(d);
          end;
          3: begin
            case Random(120) mod 5 of
              0: s := 'Flight';
              1: s := 'On Leave';
              2: s := 'Abroad';
              else
                s := 'Office';
            end;
          end;
          else
          begin
            s := Random(100).ToString;
          end;
        end;

        Cells[J, I] := s;
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.LoadFromXMLData(AFileName: string);
begin
  InternalLoadFromXML(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LoadFromXMLStreamData(AStream: TStream);
begin
  InternalLoadFromXMLStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LoadFromFileData(AFileName: string; AEncoding: TEncoding);
begin
  InternalLoadFromFile(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadFromFileStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalLoadFromFileStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadFromJSONData(AFileName: string;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  InternalLoadFromJSON(AFileName, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCDataGridData.LoadFromJSONData(AFileName, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  InternalLoadFromJSON(AFileName, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCDataGridData.LoadFromJSONStreamData(AStream: TStream;
  ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  InternalLoadFromJSONStream(AStream, TEncoding.Default, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCDataGridData.LoadFromJSONStreamData(AStream: TStream;
  AEncoding: TEncoding; ARowDataName, AColumnDataName: string;
  AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  InternalLoadFromJSONStream(AStream, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCDataGridData.LoadFromJSONTextData(AText, ARowDataName,
  AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
begin
  InternalLoadFromJSONText(AText, ARowDataName, AColumnDataName, AColumnNames);
end;

procedure TTMSFNCDataGridData.LoadFromFileData(AFileName: string);
begin
  InternalLoadFromFile(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LoadFromFileStreamData(AStream: TStream);
begin
  InternalLoadFromFileStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LoadFromCSVData(AFileName: string; AEncoding: TEncoding);
begin
  InternalLoadFromCSV(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadFromCSVStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalLoadFromCSVStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridData.LoadFromCSVData(AFileName: string);
begin
  InternalLoadFromCSV(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LoadFromCSVStreamData(AStream: TStream);
begin
  InternalLoadFromCSVStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.SaveToFileData(AFileName: string; AEncoding: TEncoding);
begin
  InternalSaveToFile(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridData.SaveToFileStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalSaveToFileStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridData.SaveToFileData(AFileName: string);
begin
  InternalSaveToFile(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.SaveToFileStreamData(AStream: TStream);
begin
  InternalSaveToFileStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.SaveToCSVData(AFileName: string; AEncoding: TEncoding);
begin
  InternalSaveToCSV(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridData.SaveToCSVStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalSaveToCSVStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridData.SaveToCSVData(AFileName: string);
begin
  InternalSaveToCSV(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.SaveToCSVStreamData(AStream: TStream);
begin
  InternalSaveToCSVStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridData.LinearFill(ADoFixed: Boolean = False);
begin
  FillGridData(ADoFixed, False);
end;

procedure TTMSFNCDataGridData.InsertColumn(AColumn: Integer);
begin
  if (FixedRightColumnCount = 0) and (AColumn = ColumnCount) then
    AddColumn
  else
    InsertColumns(AColumn, 1);
end;

procedure TTMSFNCDataGridData.InsertColumns(AColumn, ACount: Integer);
var
  c, cc, i: Integer;
begin
  if CanInsertColumn(AColumn) then
  begin
    BeginUpdate;
    try
      cc := ColumnCount;

      ColumnCount := ColumnCount + ACount;

      for c := cc - 1 downto AColumn do
        MoveColumnData(c, c + ACount);

      ShiftColumnOrder(AColumn, ACount);

      I := 0;
      for c := AColumn to AColumn + ACount - 1 do
      begin
        SetColumnOrder(cc + I, c);
        Inc(I);
      end;
    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Columns [' + FormatNumberRange(AColumn, ACount) + '] cannot be inserted.', glmWarning);
end;

procedure TTMSFNCDataGridData.InsertRow(ARow: integer);
begin
  if (FixedBottomRowCount = 0) and (ARow = RowCount) then
    AddRow
  else
    InsertRows(ARow, 1);
end;

procedure TTMSFNCDataGridData.InsertRows(ARow, ACount: Integer);
var
  r, pr, rr, I: Integer;
  lvl: Integer;
begin
  if CanInsertRow(ARow) then
  begin
    BeginUpdate;
    try
      pr := -1;
      if FUpdateGrouping = 0 then
        pr := GetParentRow(ARow);

      rr := RowCount;

      RowCount := RowCount + ACount;

      for r := rr - 1 downto ARow do
        MoveRowData(r, r + ACount);

      ShiftRowOrder(ARow, ACount);

      I := 0;
      for r := ARow to ARow + ACount - 1 do
      begin
        SetRowOrder(rr + I, r);
        Inc(I);
      end;

      if (FUpdateGrouping = 0) then
      begin
        if pr <> -1 then
        begin
          lvl := RowNodeLevel[pr];
          for rr := ARow to ARow + ACount - 1 do
            RowNodeLevel[rr] := lvl + 1;

          while pr <> -1 do
          begin
            RowNodeChildCount[pr] := RowNodeChildCount[pr] + ACount;
            pr := GetParentRow(pr);
          end;
        end;
      end;

    finally
      EndUpdate;
    end;
  end
  else
    LogMessage('Rows [' + FormatNumberRange(ARow, ACount) + '] cannot be inserted.', glmWarning);
end;

function TTMSFNCDataGridData.CompareRows(AColumn: Integer; ALevel: Integer; V1, V2: TTMSFNCDataGridDataSortInfo; ACustomCompareCallback: TTMSFNCDataGridDataCustomCompareCallback): Integer;
var
  f: TTMSFNCDataGridSortFormat;
  fmt: TFormatSettings;
  sf: TTMSFNCDataGridInternalSortFormat;
  sl, sr: string;
  fl, fr: Double;
  dtl, dtr: TDateTime;
  il, ir: Int64;
  vc1, vc2: TTMSFNCDataGridCellValue;
  vc1v, vc2v: TValue;
  csi1, csi2, cs1, I: Integer;
  cs: Boolean;
  dr: TTMSFNCDataGridSortDirection;
  h: Boolean;
  r: Integer;

  I64val: Int64;
  Ival: Integer;
  Dval: Double;
  dt: TDateTime;
begin
  Result := 0;
  csi1 := -1;
  for I := 0 to Length(V1.Data) - 1 do
  begin
    if V1.Data[I].Column = AColumn then
    begin
      csi1 := I;
      Break;
    end;
  end;

  csi2 := -1;
  for I := 0 to Length(V2.Data) - 1 do
  begin
    if V2.Data[I].Column = AColumn then
    begin
      csi2 := I;
      Break;
    end;
  end;

  if (csi1 >= 0) and (csi1 <= Length(V1.Data) - 1) and (csi2 >= 0) and (csi2 <= Length(V2.Data) - 1) then
  begin
    f := V1.Data[csi1].Options.Format;
    fmt := V1.Data[csi1].Options.FormatSettings;
    cs := V1.Data[csi1].Options.CaseSensitive;
    dr := V1.Data[csi1].Options.Direction;

    vc1 := V1.Data[csi1].Data;
    vc2 := V2.Data[csi2].Data;

    h := False;
    if Assigned(ACustomCompareCallback) then
    begin
      r := ACustomCompareCallback(AColumn, V1.Row, V2.Row, vc1, vc2, ALevel, h);
      if h then
        Result := r;
    end;

    if not h then
    begin
      sf := gisfCustom;
      case f of
        gsfAutomatic:
        begin
          if ValueIsFloat(vc1) and ValueIsFloat(vc2) then
            sf := gisfNumericFloat
          else if ValueIsInteger(vc1) and ValueIsInteger(vc2) then
            sf := gisfNumericInteger
          else if ValueIsString(vc1) and ValueIsString(vc2) then
          begin
            vc1v := CellValueToTValue(vc1);
            vc2v := CellValueToTValue(vc2);
            if TryStrToFloat(vc1v.AsString, Dval, fmt) and TryStrToFloat(vc2v.AsString, Dval, fmt) then
              sf := gisfNumericFloat
            else if TryStrToInt64(vc1v.AsString, I64val) and TryStrToInt64(vc2v.AsString, I64val) then
              sf := gisfNumericInteger
            else if TryStrToInt(vc1v.AsString, Ival) and TryStrToInt(vc2v.AsString, Ival) then
              sf := gisfNumericInteger
            else if TryStrToDateTime(vc1v.AsString, dt) and TryStrToDateTime(vc2v.AsString, dt) then
              sf := gisfDateTime
            else
              sf := gisfAlphabetic
          end
          else
          begin
//            raise Exception.Create('TODO: HANDLE COMPARE OTHER VALUE TYPES?');
          end;
        end;
        gsfAlphabetic: sf := gisfAlphabetic;
        gsfNumeric: sf := gisfNumericFloat;
        gsfDateTime: sf := gisfDateTime;
        gsfCustom: sf := gisfCustom;
      end;

      case sf of
        gisfAlphabetic:
        begin
          if cs then
          begin
            sl := ValueToString(vc1);
            sr := ValueToString(vc2);
          end
          else
          begin
            sl := UpperCase(ValueToString(vc1));
            sr := UpperCase(ValueToString(vc2));
          end;

          Result := CompareStr(sl, sr);
        end;
        gisfDateTime:
        begin
          dtl := ValueToDateTime(vc1, fmt);
          dtr := ValueToDateTime(vc2, fmt);
          Result := CompareDateTime(dtl, dtr);
        end;
        gisfNumericFloat:
        begin
          fl := ValueToFloat(vc1, fmt);
          fr := ValueToFloat(vc2, fmt);

          if sf = gisfNumericFloat then
            Result := CompareValue(fl, fr)
          else
            Result := CompareDateTime(fl, fr);
        end;
        gisfNumericInteger:
        begin
          il := ValueToInt64(vc1, fmt);
          ir := ValueToInt64(vc2, fmt);
          Result := CompareValue(il, ir);
        end;
        gisfCustom: Result := DoCustomCompare(AColumn, V1.Row, V2.Row, vc1, vc2, ALevel);
      end;
    end;

    if (Result = 0) then
    begin
      Inc(csi1);
      Inc(csi2);
      if (csi1 > 0) and (csi1 <= Length(V1.Data) - 1) and (csi2 > 0) and (csi2 <= Length(V2.Data) - 1) then
      begin
        cs1 := V1.Data[csi1].Column;
        Result := CompareRows(cs1, ALevel, V1, V2, ACustomCompareCallback);
      end;
    end
    else
    begin
      if dr = gsdDescending then
        Result := Result * -1;
    end;
  end;
end;

procedure TTMSFNCDataGridData.ClearCells(
  ACellRange: TTMSFNCDataGridCellCoordRange);
var
  i, j: Integer;
  c: TTMSFNCDataGridCellCoord;
begin
  BeginUpdate;
  try
    for i := ACellRange.StartCell.Row to ACellRange.StopCell.Row do
    begin
      for j := ACellRange.StartCell.Column to ACellRange.StopCell.Column do
      begin
        c := MakeCell(j, i);

        if IsCellMerged(c) then
          SplitCell(c);

        DeleteCellData(c);
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ClearDataForColumn(AColumn: Integer);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := 0 to RowCount - 1 do
    begin
      if HasCellData[AColumn, I] then
        DeleteCellData(MakeCell(AColumn, I));
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ClearDataForRow(ARow: Integer);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := 0 to ColumnCount - 1 do
    begin
      if HasCellData[I, ARow] then
        DeleteCellData(MakeCell(I, ARow));
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.ClearData;
begin
  BeginUpdate;
  try
    CellDataDictionary.Clear;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromCSV(AFileName: string; AEncoding: TEncoding);
var
  ms: TStream;

  procedure InternalInputFromCSV;
  begin
    ms.Position := 0;
    InternalLoadFromCSVStream(ms, AEncoding);
  end;
begin
  {$IFDEF WEBLIB}
  ms := TMemoryStream.Create;
  TMemoryStream(ms).LoadFromFile(AFileName,
  procedure
  begin
    InternalInputFromCSV;
    ms.Free;
  end
  );
  {$ENDIF}
  {$IFNDEF WEBLIB}
  ms := TFileStream.Create(AFileName, fmShareDenyNone, 0);
  try
    InternalInputFromCSV;
  finally
    ms.Free;
  end;
  {$ENDIF}
end;

procedure TTMSFNCDataGridData.InternalLoadFromFile(AFileName: string; AEncoding: TEncoding);
var
  ms: TStream;

  procedure InternalInputFromFile;
  begin
    ms.Position := 0;
    InternalLoadFromFileStream(ms, AEncoding);
  end;
begin
  {$IFDEF WEBLIB}
  ms := TMemoryStream.Create;
  TMemoryStream(ms).LoadFromFile(AFileName,
  procedure
  begin
    InternalInputFromFile;
    ms.Free;
  end
  );
  {$ENDIF}
  {$IFNDEF WEBLIB}
  ms := TFileStream.Create(AFileName, fmShareDenyNone, 0);
  try
    InternalInputFromFile;
  finally
    ms.Free;
  end;
  {$ENDIF}
end;

procedure TTMSFNCDataGridData.InternalLoadFromXML(AFileName: string; AEncoding: TEncoding);
var
  ms: TStream;

  procedure InternalInputFromXML;
  begin
    ms.Position := 0;
    InternalLoadFromXMLStream(ms, AEncoding);
  end;
begin
  {$IFDEF WEBLIB}
  ms := TMemoryStream.Create;
  TMemoryStream(ms).LoadFromFile(AFileName,
  procedure
  begin
    InternalInputFromXML;
    ms.Free;
  end
  );
  {$ENDIF}
  {$IFNDEF WEBLIB}
  ms := TFileStream.Create(AFileName, fmShareDenyNone, 0);
  try
    InternalInputFromXML;
  finally
    ms.Free;
  end;
  {$ENDIF}
end;

procedure TTMSFNCDataGridData.InternalSaveToCSV(AFileName: string; AEncoding: TEncoding);
var
  ms: TMemoryStream;
  procedure InternalOutputToCSV;
  begin
    ms.Position := 0;
    InternalSaveToCSVStream(ms, AEncoding);
  end;
begin
  ms := TMemoryStream.Create;
  try
    InternalOutputToCSV;
    ms.SaveToFile(AFileName);
    if Options.IO.OpenAfterCreation then
      TTMSFNCUtils.OpenFile(AFileName);
  finally
    ms.Free;
  end;
end;

procedure TTMSFNCDataGridData.InternalSaveToFileStream(AStream: TStream; AEncoding: TEncoding);
var
  i, j: Integer;
  ss, CellText: string;
  sl: TStringList;
  io: TTMSFNCDataGridDataInputOutputOptions;
  v: TTMSFNCDataGridCellValue;
begin
  sl := TStringList.Create;

  io := GetIOOptions;
  ExportNotification(gesExportStart, io.StartRow);

  try
    ss := IntToStr(ColumnCount) + ',' + IntToStr(RowCount);

    sl.Add(ss);

    for i := io.StartColumn to ColumnCount - 1 do
    begin
      ss := 'cw '+ IntToStr(i) + ',' + FloatToStr(ColumnWidths[i]);
      sl.Add(ss);
    end;

    for i := io.StartRow to RowCount - 1 do
    begin
      ExportNotification(gesExportNewRow, i);

      for j := io.StartColumn to ColumnCount - 1 do
      begin
        v := Cells[j, i];

        DoSaveCellData(j, i, v);

        CellText := ValueToString(v);

        if CellText <> '' then
        begin
          ss := IntToStr(j) + ',' + IntToStr(i) + ',' + LFToFile(CellText);
          sl.Add(ss);
        end;
      end;
    end;

    sl.SaveToStream(AStream, AEncoding);

  finally
    sl.Free;
    ExportNotification(gesExportDone, -1);
  end;
end;

procedure TTMSFNCDataGridData.InternalSaveToFile(AFileName: string; AEncoding: TEncoding);
var
  ms: TMemoryStream;
  procedure InternalOutputToFile;
  begin
    ms.Position := 0;
    InternalSaveToFileStream(ms, AEncoding);
  end;
begin
  ms := TMemoryStream.Create;
  try
    InternalOutputToFile;
    ms.SaveToFile(AFileName);
    if Options.IO.OpenAfterCreation then
      TTMSFNCUtils.OpenFile(AFileName);
  finally
    ms.Free;
  end;
end;

procedure TTMSFNCDataGridData.InternalSaveToCSVStream(AStream: TStream; AEncoding: TEncoding);
var
  z, s, rs: Integer;
  CellText: String;
  Delim: Char;
  dblquotes: Boolean;
  sl: TTMSFNCDataGridDataFileStringList;
  io: TTMSFNCDataGridDataInputOutputOptions;
  v: TTMSFNCDataGridCellValue;
begin
  sl := TTMSFNCDataGridDataFileStringList.Create;
  io := GetIOOptions;
  ExportNotification(gesExportStart, io.StartRow);

  try

    if io.Delimiter = #0 then
      Delim := ','
    else
      Delim := io.Delimiter;

    sl.LineBreak := #13#10;

    for z := io.StartRow to RowCount - 1 do
    begin
      if IsRowVisible(z) then
      begin
        ExportNotification(gesExportNextRow, z);

        for s := io.StartColumn to ColumnCount - 1 do
        begin
          if s > io.StartColumn then
            sl.Write(Delim);

          rs := s;

          v := Cells[rs, z];

          DoSaveCellData(rs, z, v);

          CellText := ValueToString(v);
          CellText := CSVQuotes(CellText);

          dblquotes := false;

          if io.AlwaysQuotes or ((Pos(Delim,CellText) = 0) and (Pos('"',CellText) > 0)) then
          begin
            CellText := '"' + CellText + '"';
            dblquotes := true;
          end;

          if CellText = '' then
          begin
            if io.QuoteEmptyCells then
              CellText := '""';
          end;

          if (Pos(Delim,CellText) > 0) or (LinesInText(CellText {,FMultiLineCells}) > 1) then
          begin
            if not dblquotes then
              LinefeedsToCSV(CellText)
            else
              LinefeedsToCSVNQ(CellText);
          end;

          sl.Write(CellText);
        end;

        sl.Writeln('');
      end;
    end;

    sl.SaveToStream(AStream, AEncoding);

  finally
    sl.Free;
    ExportNotification(gesExportDone, -1);
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromCSVStream(AStream: TStream; AEncoding: TEncoding);
var
  buffer, celltext: string;
  s, z: Integer;
  strtCol, strtRow: Integer;
  del: Char;
  delimiterpos, quotepos: Integer;
  lr: TStringList;
  NewRows: Integer;
  sl: TTMSFNCDataGridDataFileStringList;
  hasquote: boolean;
  io: TTMSFNCDataGridDataInputOutputOptions;
  v: TTMSFNCDataGridCellValue;
begin
  sl := TTMSFNCDataGridDataFileStringList.Create;
  sl.LoadFromStream(AStream, AEncoding);

  if sl.Eof then
  begin
    raise Exception.Create('File is empty');
    sl.Free;
    Exit;
  end;

  io := GetIOOptions;

  StrtCol := io.StartColumn;
  StrtRow := io.StartRow;

  z := StrtRow;

  lr := TStringList.Create;

  del := io.Delimiter;

  BeginUpdate;

  try
    if (del = #0) then
    begin
      if sl.Count > 1 then
        Del := TTMSFNCUtils.GetBestDelimiter(sl[0], sl[1], False)
      else
        Del := TTMSFNCUtils.GetBestDelimiter(sl[0], '', False);
    end;

    sl.Reset;

    NewRows := 0;

    while (not sl.Eof) and ((io.MaxRows <= 0) or (NewRows < io.MaxRows)) do
    begin
      sl.ReadLn(buffer);

      inc(NewRows);

      s := StrtCol;

      if z >= RowCount - FixedBottomRowCount then
        RowCount := z + 1000;

      DelimiterPos := -1;
      while VarCharPos(del, Buffer, DelimiterPos) > 0 do
      begin
        if io.TrimSpaces then
        begin
          while (Length(Buffer) > 1) and (Buffer[1] = ' ') do
          begin
            Delete(Buffer, 1, 1);
            Dec(DelimiterPos);
          end;
        end;

        {$IFDEF ZEROSTRINGINDEX}
        DelimiterPos := DelimiterPos + 1;
        hasquote := Buffer[0] = '"';
        {$ELSE}
        hasquote := Buffer[1] = '"';
        {$ENDIF}
        if hasquote then
        begin
          Delete(buffer,1,1);

          QuotePos := -1;
          if SinglePos('"',Buffer,QuotePos) > 0 then
          begin
            CellText := Copy(buffer,1,QuotePos - 1);
            CellText := DoubleToSingleChar('"',CellText);
            Delete(buffer,1,QuotePos);
          end
          else
            CellText := '';

          VarCharPos(del, buffer, DelimiterPos);
          {$IFDEF ZEROSTRINGINDEX}
          DelimiterPos := DelimiterPos + 1;
          {$ENDIF}
        end
        else
          CellText := Copy(buffer,1,DelimiterPos - 1);

        CSVToLineFeeds(CellText);

        Inc(s);
        if (s >= ColumnCount) then
          ColumnCount := s;

        v := {$IFNDEF WEBLIB}TValueToCellValue{$ENDIF}(CellText);

        DoLoadCellData(s - 1, z, v);

        Cells[s - 1,z] := v;

        Delete(buffer,1,DelimiterPos);
      end;

      if Length(Buffer) > 0 then
      begin
        if io.TrimSpaces then
        begin
          while (Length(Buffer) > 1) and (Buffer[1] = ' ') do
            Delete(Buffer, 1, 1);
        end;

        {$IFDEF ZEROSTRINGINDEX}
        hasquote := Buffer[0] = '"';
        {$ELSE}
        hasquote := Buffer[1] = '"';
        {$ENDIF}
        if hasquote then
          Delete(buffer,1,1);

        if Length(Buffer) > 0 then
        begin
          {$IFDEF ZEROSTRINGINDEX}
          if hasquote and (Buffer[Length(Buffer) - 1] = '"') then
          {$ELSE}
          if hasquote and (Buffer[Length(Buffer)] = '"') then
          {$ENDIF}
            Delete(Buffer,Length(Buffer),1);
        end;

        if hasquote then
          CellText := DoubleToSingleChar('"',Buffer)
        else
          CellText := Buffer;

        CSVToLineFeeds(CellText);

        Inc(s);

        if (s > ColumnCount) then
          ColumnCount := s;

        v := {$IFNDEF WEBLIB}TValueToCellValue{$ENDIF}(CellText);

        DoLoadCellData(s - 1, z, v);

        Cells[s - 1,z] := v;
      end;

      Inc(z);
    end;

    RowCount := z + FixedBottomRowCount;

  finally
    EndUpdate;
    sl.Free;
    lr.Free;
  end;
end;

function GetNextLine(var s:string;multiline:boolean = True):string;
var
  vp: Integer;
begin
  vp := -1;
  if TTMSFNCUtils.VarPos(#$A,s,vp) > 0 then
  begin
    Result := Copy(s,1,vp-1);
    Delete(s,1,vp);
    if s<>'' then
      if s[1] = #10 then
        Delete(s,1,1);
    if not Multiline then
      s := '';
  end
  else
  begin
    Result := s;
    s := '';
  end;
end;

function FileToLF(s:string;multiline:boolean = true):string;
var
  i:Integer;
begin
  if Pos(#8,s)>0 then
  {$IFDEF ZEROSTRINGINDEX}
  for i := 0 to length(s) - 1 do
  {$ELSE}
  for i := 1 to length(s) do
  {$ENDIF}
    begin
      if s[i] = #9 then s[i] := #13;
      if s[i] = #8 then s[i] := #10;
      if s[i] = #1 then s[i] := #0;
    end;
  if not MultiLine then
    Result := GetNextLine(s,multiline)
  else
    Result := s;
end;

function MStrToInt(s: string): Integer;
var
  {%H-}code,i: Integer;
begin
  val(s, i, code);
  Result := i;
end;

function GetToken(var s:string; separator: string): string;
var
  sp:Integer;
begin
  Result := '';
  sp := Pos(separator,s);
  if sp > 0 then
  begin
    Result := Copy(s,1,sp - 1);
    Delete(s,1,sp);
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromFileStream(AStream: TStream; AEncoding: TEncoding);
var
  X, Y, CW: Integer;
  ss, ss1:string;
  strtCol, strtRow: Integer;
  seppos: Integer;
  sl: TTMSFNCDataGridDataFileStringList;
  io: TTMSFNCDataGridDataInputOutputOptions;
  v: TTMSFNCDataGridCellValue;
begin
  sl := TTMSFNCDataGridDataFileStringList.Create;
  sl.LoadFromStream(AStream, AEncoding);

  if sl.Eof then
  begin
    raise Exception.Create('File is empty');
    sl.Free;
    Exit;
  end;

  io := GetIOOptions;

  StrtCol := io.StartColumn;
  StrtRow := io.StartRow;

  ss := '';
  sl.ReadLn(ss);

  if ss <> '' then
  begin
    {$IFDEF ZEROSTRINGINDEX}
    ss1 := Copy(ss,0,CharPos(',',ss));
    {$ELSE}
    ss1 := Copy(ss,1,CharPos(',',ss) - 1);
    {$ENDIF}
    ColumnCount := MStrToInt(ss1) + StrtCol;
    {$IFDEF ZEROSTRINGINDEX}
    ss1 := Copy(ss,CharPos(',',ss) + 2,Length(ss));
    {$ELSE}
    ss1 := Copy(ss,CharPos(',',ss) + 1,Length(ss));
    {$ENDIF}
    RowCount := MStrToInt(ss1) + StrtRow;
  end;

  if (ColumnCount = 0) or (RowCount = 0) then
  begin
    sl.Free;
    raise Exception.Create('File contains no data or corrupt file ');
  end;

  BeginUpdate;

  try
    Cells[ColumnCount - 1, RowCount - 1] := '';

    while not sl.Eof do
    begin
      sl.Readln(ss);
      if Pos('cw', ss) = 1 then
      begin
        seppos := CharPos(',',ss);
        ss1 := Copy(ss,4,seppos - 4);
        ss := Copy(ss,seppos + 1,255);
        CW := MStrToInt(ss1);
        if (cw >= 0) and (cw < ColumnCount) then
          ColumnWidths[cw] := mstrtoint(ss);
      end
      else
      begin
        ss1 := GetToken(ss,',');
        X := mStrToInt(ss1);
        ss1 := GetToken(ss,',');
        Y := mStrToInt(ss1);

        if (X < ColumnCount) and (Y < RowCount) then
        begin
          ss := FileToLF(ss);

          v := {$IFNDEF WEBLIB}TValueToCellValue{$ENDIF}(ss);

          DoLoadCellData(X, Y, v);

          Cells[X, Y] := v;
        end;
      end;
    end;
  finally
    sl.Free;

    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromJSON(AFileName: string; AEncoding: TEncoding;
  ARowDataName: string; AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
var
  ms: TMemoryStream;
  procedure InternalInputFromJSON;
  begin
    ms.Position := 0;
    InternalLoadFromJSONStream(ms, AEncoding, ARowDataName, AColumnDataName, AColumnNames);
  end;
begin
  ms := TMemoryStream.Create;
  try
    {$IFDEF WEBLIB}
    ms.LoadFromFile(AFileName,
    procedure
    begin
      InternalInputFromJSON;
      ms.Free;
    end
    );
    {$ENDIF}
    {$IFNDEF WEBLIB}
    ms.LoadFromFile(AFileName);
    InternalInputFromJSON;
    {$ENDIF}
  finally
    {$IFNDEF WEBLIB}
    ms.Free;
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromJSONStream(AStream: TStream; AEncoding: TEncoding;
  ARowDataName: string; AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
var
  SL: TStringList;
begin
  SL := TStringList.Create;
  try
    AStream.Position := 0;
    SL.LoadFromStream(AStream, AEncoding);
    InternalLoadFromJSONText(SL.Text, ARowDataName, AColumnDataName, AColumnNames);
  finally
    SL.Free;
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromJSONText(AText,
  ARowDataName: string; AColumnDataName: string; AColumnNames: TTMSFNCDataGridDataJSONNamesArray);
var
  j, jc, jo : TJSONValue;
  ja: TJSONArray;
  I: Integer;
  io: TTMSFNCDataGridDataInputOutputOptions;
  z, s: Integer;
  NewRows: Integer;
  strtCol, strtRow: Integer;

  procedure ParseValue(pv: TJSONValue; AColumnName: string);
  var
    v, vc: TTMSFNCDataGridCellValue;
  begin
    Inc(s);
    if (s >= ColumnCount) then
      ColumnCount := s;

    if not Assigned(pv) then
      Exit;

    if pv is TJSONObject then
      v := pv.ToString
    else
    begin
      if pv is TJSONString then
        v := pv.Value
      else
        v := pv.ToString
    end;

    if (AColumnName <> '') and (strtRow > 0) then
    begin
      vc := AColumnName;
      DoLoadCellData(s - 1, 0, vc);
      Cells[s - 1, 0] := vc;
    end;

    DoLoadCellData(s - 1, z, v);
    Cells[s - 1,z] := v;
  end;

  procedure ParseCell(pj: TJSONValue);
  var
    pjc: TJSONValue;
    K: Integer;
    nm: string;
  begin
    s := strtCol;
    if (Length(AColumnNames) = 0) then
    begin
      if pj is TJSONObject then
      begin
        for K := 0 to TTMSFNCUtils.GetJSONObjectSize(pj as TJSONObject) - 1 do
        begin
          pjc := TTMSFNCUtils.GetJSONObjectItem(pj as TJSONObject, K);
          nm := TTMSFNCUtils.GetJSONObjectName(pj as TJSONObject, K);
          ParseValue(pjc, nm);
        end;
      end
      else
        ParseValue(pj, '')
    end
    else
    begin
      for K := 0 to Length(AColumnNames) - 1 do
      begin
        pjc := TTMSFNCUtils.FindJSONValue(pj, AColumnNames[K]);
        if Assigned(pjc) then
          ParseValue(pjc, AColumnNames[K])
        else
          ParseValue(pj, AColumnNames[K]);
      end;
    end;
  end;

  procedure ParseRow(sj: TJSONValue);
  var
    sjc, sjo: TJSONValue;
    sja: TJSONArray;
    K: Integer;
  begin
    if ((io.MaxRows <= 0) or (NewRows < io.MaxRows)) then
    begin
      inc(NewRows);

      if z >= RowCount - FixedBottomRowCount then
        RowCount := z + 1000;

      sjc := TTMSFNCUtils.FindJSONValue(sj, AColumnDataName);
      if Assigned(sjc) then
      begin
        if sjc is TJSONArray then
        begin
          sja := (sjc as TJSONArray);
          for K := 0 to TTMSFNCUtils.GetJSONArraySize(sja) - 1 do
          begin
            sjo := TTMSFNCUtils.GetJSONArrayItem(sja, K);
            ParseCell(sjo);
          end
        end
        else
          ParseCell(sjc);
      end
      else
      begin
        if sj is TJSONArray then
        begin
          sja := (sj as TJSONArray);
          for K := 0 to TTMSFNCUtils.GetJSONArraySize(sja) - 1 do
          begin
            sjo := TTMSFNCUtils.GetJSONArrayItem(sja, K);
            ParseCell(sjo);
          end
        end
        else
          ParseCell(sj);
      end;

      inc(z);
    end;
  end;
begin
  BeginUpdate;

  try
    NewRows := 0;

    io := GetIOOptions;
    StrtCol := io.StartColumn;
    StrtRow := io.StartRow;
    z := StrtRow;

    j := TTMSFNCUtils.ParseJSON(AText);
    if Assigned(j) then
    try
      if j is TJSONArray then
        jc := j as TJSONArray
      else
        jc := TTMSFNCUtils.FindJSONValue(j, ARowDataName);

      if Assigned(jc) then
      begin
        if jc is TJSONArray then
        begin
          ja := (jc as TJSONArray);
          for I := 0 to TTMSFNCUtils.GetJSONArraySize(ja) - 1 do
          begin
            jo := TTMSFNCUtils.GetJSONArrayItem(ja, i);
            ParseRow(jo);
          end
        end
        else
          ParseRow(jc);
      end
      else
      begin
        ParseRow(j);
      end;
    finally
      j.Free;
    end;
  finally
    RowCount := z + FixedBottomRowCount;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridData.InternalLoadFromXMLStream(AStream: TStream; AEncoding: TEncoding);
var
  xmldoc: TTMSFNCDataGridDataXMLDocument;
  r, c, fc, fr: Integer;
  parentNode, eNode: TTMSFNCDataGridDataXMLNode;
  I: Integer;
  sl, slt: TStringList;
  txt: string;
  io: TTMSFNCDataGridDataInputOutputOptions;

  procedure LoadChildNodes(ANode: TTMSFNCDataGridDataXMLNode);
  var
    J, idx: Integer;
    v: TTMSFNCDataGridCellValue;
    n: string;
  begin
    for J:= 0 to ANode.ChildNodes.Count - 1 do
    begin
      if ANode.ChildNodes[J].ChildNodes.Count > 1 then
      begin
        LoadChildNodes(ANode.ChildNodes[J]);
      end
      {$IFNDEF WEBLIB}
      else if ANode.ChildNodes[J].NodeType <> ntComment then
      {$ENDIF}
      {$IFDEF WEBLIB}
      else if (ANode.ChildNodes[J].ChildNodes.Count = 1) then
      {$ENDIF}
      begin
        eNode := ANode.ChildNodes[J];

        idx := sl.IndexOf(eNode.NodeName);
        if idx = -1 then
        begin
          sl.Add(eNode.NodeName);
          idx := sl.Count - 1;

          n := eNode.NodeName;

          v := {$IFNDEF WEBLIB}TValueToCellValue{$ENDIF}(n);
          DoLoadCellData(idx + fc, fr - 1, v);
          Cells[idx + fc, fr - 1] := n;
        end;

        {$IFNDEF WEBLIB}
        v := TValue.FromVariant(eNode.NodeValue);
        {$ENDIF}
        {$IFDEF WEBLIB}
        v := eNode.ChildNodes[0].NodeValue;
        {$ENDIF}

        DoLoadCellData(idx + fc, r, v);
        Cells[idx + fc, r] := v;
      end;
    end;

    if ANode.ChildNodes.Count > 0 then
      r := r + 1;
  end;

begin
  slt := TStringList.Create;
  slt.LoadFromStream(AStream, AEncoding);

  if slt.Count = 0 then
  begin
    raise Exception.Create('File is empty');
    slt.Free;
    Exit;
  end;

  txt := slt.Text;

  BeginUpdate;
  try
    {$IFDEF WEBLIB}
    asm
      var parser = new DOMParser();
      xmldoc = parser.parseFromString(txt, 'text/xml');
    end;
    {$ELSE}
    xmldoc := TXmlDocument.Create(nil);
    xmldoc.LoadFromXML(txt);
    {$ENDIF}

    parentNode := xmldoc.DocumentElement;

    io := GetIOOptions;

    fc := io.StartColumn;
    fr := io.StartRow;

    r := fr;

    sl := TStringList.Create;
    try
      for I := 0 to parentNode.ChildNodes.Count - 1 do
        LoadChildNodes(parentNode.ChildNodes[I]);

    finally
      c := sl.Count + fc;
      sl.Free;
    end;

    ColumnCount := c;
    RowCount := r;
  finally
    EndUpdate;
    slt.Free;
  end;
end;

{ TTMSFNCDataGridDataFileStringList }

procedure TTMSFNCDataGridDataFileStringList.Reset;
begin
  fp := 0;
  cache := '';
end;

function TTMSFNCDataGridDataFileStringList.GetEOF: Boolean;
begin
  Result := fp >= Count;
end;

procedure TTMSFNCDataGridDataFileStringList.ReadLn(var s: string);
begin
  s := Strings[fp];
  inc(fp);
end;

procedure TTMSFNCDataGridDataFileStringList.Write(s: string);
begin
  cache := cache + s;
end;

procedure TTMSFNCDataGridDataFileStringList.WriteLn(s: string);
begin
  Add(cache + s);
  cache := '';
end;

{ TTMSFNCDataGridDataGroup }

destructor TTMSFNCDataGridDataGroup.Destroy;
begin
  FreeAndNil(FGroupDictionary);
  inherited;
end;

function TTMSFNCDataGridDataGroup.GetGroupDictionary: TTMSFNCDataGridDataGroupDictionary;
begin
  if not Assigned(FGroupDictionary) then
    FGroupDictionary := TTMSFNCDataGridDataGroupDictionary.Create([doOwnsValues]);

  Result := FGroupDictionary;
end;

{ TTMSFNCDataGridDataFilterData }

procedure TTMSFNCDataGridDataFilterData.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataFilterData then
  begin
    FCaseSensitive := (Source as TTMSFNCDataGridDataFilterData).CaseSensitive;
    FColumn := (Source as TTMSFNCDataGridDataFilterData).Column;
    FCondition := (Source as TTMSFNCDataGridDataFilterData).Condition;
    FType := (Source as TTMSFNCDataGridDataFilterData).&Type;
    FData := (Source as TTMSFNCDataGridDataFilterData).Data;
    FEnabled := (Source as TTMSFNCDataGridDataFilterData).Enabled;
    FMethod := (Source as TTMSFNCDataGridDataFilterData).Method;
    FOperation := (Source as TTMSFNCDataGridDataFilterData).Operation;
    FPrefix := (Source as TTMSFNCDataGridDataFilterData).Prefix;
    FSuffix := (Source as TTMSFNCDataGridDataFilterData).Suffix;
  end;
end;

procedure TTMSFNCDataGridDataFilterData.BuildCondition(s: string);
begin
  if Pos(' ', s) > 0 then
    s := '"' + s + '"';

  case &Type of
    gftEqual: Condition := s;
    gftEmpty: Condition := '';
    gftNotEmpty: Condition := '!';
    gftStartsWith: Condition := s + '*';
    gftEndsWith: Condition := '*' + s;
    gftContains: Condition := '*' + s + '*';
    gftNotContains: Condition := '!*' + s + '*';
    gftNotEqual: Condition := '!' + s;
    gftLargerThan: Condition := '>' + s;
    gftSmallerThan: Condition := '<' + s;
    gftLargerOrEqualThan: Condition := '>=' + s;
    gftSmallerOrEqualThan: Condition := '<=' + s;
  end;
end;

constructor TTMSFNCDataGridDataFilterData.Create(ACollection: TCollection);
begin
  inherited;
  FCaseSensitive := False;
  FMethod := gfmExpression;
  FData := gfcNormal;
  FType := gftEqual;
  FEnabled := True;
end;

procedure StripFilterCondition(ACondition: string; var AValue: string; var AType: TTMSFNCDataGridDataFilterType);
begin
  AValue := ACondition;
  if Pos('"', AValue) > 0 then
    AValue := StringReplace(AValue, '"', '', [rfReplaceAll]);

  if AValue = '' then
    AType := gftEmpty
  else if AValue = '!' then
    AType := gftNotEmpty
  else if AValue.StartsWith('*') and AValue.EndsWith('*') then
    AType := gftContains
  else if AValue.StartsWith('!*') and AValue.EndsWith('*') then
    AType := gftNotContains
  else if AValue.StartsWith('*') then
    AType := gftEndsWith
  else if AValue.EndsWith('*') then
    AType := gftStartsWith
  else if AValue.StartsWith('!') then
    AType := gftNotEqual
  else if AValue.StartsWith('>=') then
    AType := gftLargerOrEqualThan
  else if AValue.StartsWith('<=') then
    AType := gftSmallerOrEqualThan
  else if AValue.StartsWith('>') then
    AType := gftLargerThan
  else if AValue.StartsWith('<') then
    AType := gftSmallerThan
  else
    AType := gftEqual;

  AValue := StringReplace(AValue, '!', '', [rfReplaceAll]);
  AValue := StringReplace(AValue, '>', '', [rfReplaceAll]);
  AValue := StringReplace(AValue, '<', '', [rfReplaceAll]);
  AValue := StringReplace(AValue, '*', '', [rfReplaceAll]);
  AValue := StringReplace(AValue, '=', '', [rfReplaceAll]);
  AValue := Trim(AValue);
end;

function GetFilterTypeName(AType: TTMSFNCDataGridDataFilterType): string;
begin
  case AType of
    gftEmpty: Result := sTMSFNCDataGridFilterTypeEmpty;
    gftNotEmpty: Result := sTMSFNCDataGridFilterTypeNotEmpty;
    gftStartsWith: Result := sTMSFNCDataGridFilterTypeStartsWith;
    gftEndsWith: Result := sTMSFNCDataGridFilterTypeEndsWith;
    gftContains: Result := sTMSFNCDataGridFilterTypeContains;
    gftNotContains: Result := sTMSFNCDataGridFilterTypeNotContains;
    gftEqual: Result := sTMSFNCDataGridFilterTypeEqual;
    gftNotEqual: Result := sTMSFNCDataGridFilterTypeNotEqual;
    gftLargerThan: Result := sTMSFNCDataGridFilterTypeLargerThan;
    gftSmallerThan: Result := sTMSFNCDataGridFilterTypeSmallerThan;
    gftLargerOrEqualThan: Result := sTMSFNCDataGridFilterTypeLargerOrEqualThan;
    gftSmallerOrEqualThan: Result := sTMSFNCDataGridFilterTypeSmallerOrEqualThan;
  end;
end;

function TTMSFNCDataGridDataFilterData.StripCondition: string;
begin
  StripFilterCondition(Condition, Result, FType);
end;

{ TTMSFNCDataGridDataFilter }

function TTMSFNCDataGridDataFilterData.&And(AColumn: Integer;
  ACondition: string): TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Collection is TTMSFNCDataGridDataFilter then
    Result := (Collection as TTMSFNCDataGridDataFilter).Add(AColumn, ACondition, gfoAND);
end;

function TTMSFNCDataGridDataFilterData.&And(AColumn: Integer;
  AType: TTMSFNCDataGridDataFilterType;
  ACondition: string): TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Collection is TTMSFNCDataGridDataFilter then
    Result := (Collection as TTMSFNCDataGridDataFilter).Add(AColumn, AType, ACondition, gfoAnd);
end;

function TTMSFNCDataGridDataFilterData.&Or(AColumn: Integer;
  ACondition: string): TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Collection is TTMSFNCDataGridDataFilter then
    Result := (Collection as TTMSFNCDataGridDataFilter).Add(AColumn, ACondition, gfoOR);
end;

function TTMSFNCDataGridDataFilterData.&Or(AColumn: Integer;
  AType: TTMSFNCDataGridDataFilterType;
  ACondition: string): TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Collection is TTMSFNCDataGridDataFilter then
    Result := (Collection as TTMSFNCDataGridDataFilter).Add(AColumn, AType, ACondition, gfoOR);
end;

function TTMSFNCDataGridDataFilter.Add: TTMSFNCDataGridDataFilterData;
begin
  Result := TTMSFNCDataGridDataFilterData(inherited Add);
  if Count = 1 then
    Result.Operation := gfoNONE
  else
    Result.Operation := gfoAND;
end;

destructor TTMSFNCDataGridDataFilter.Destroy;
begin
  FreeAndNil(FCommonItem);
  FreeAndNil(FUniqueItem);
  FreeAndNil(FCommonDoubleQuotedItem);
  FreeAndNil(FUniqueDoubleQuotedItem);
  inherited;
end;

function TTMSFNCDataGridDataFilter.First: TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Count > 0 then
    Result := Items[0];
end;

function TTMSFNCDataGridDataFilter.Add(AColumn: Integer; ACondition: string;
  AOperation: TTMSFNCDataGridDataFilterOperation = gfoNone): TTMSFNCDataGridDataFilterData;
begin
  Result := Add;
  Result.Column := AColumn;
  Result.Condition := ACondition;
  Result.Operation := AOperation;
end;

function TTMSFNCDataGridDataFilter.Add(AColumn: Integer; AType: TTMSFNCDataGridDataFilterType; ACondition: string;
  AOperation: TTMSFNCDataGridDataFilterOperation = gfoNone): TTMSFNCDataGridDataFilterData;
begin
  Result := Add;
  Result.Column := AColumn;
  Result.Operation := AOperation;
  Result.&Type := AType;
  Result.BuildCondition(ACondition);
end;

function TTMSFNCDataGridDataFilter.GetColumnFilter(AColumn: Integer): TTMSFNCDataGridDataFilterData;
var
  i: Integer;
begin
  for i := 1 to Count do
  begin
    if Items[i - 1].Column = AColumn then
    begin
      Result := Items[i - 1];
      Exit;
    end;
  end;
  Result := Add;
  Result.Column := AColumn;
end;

function TTMSFNCDataGridDataFilter.GetCommonDoubleQuotedItem: TTMSFNCDataGridDataFilterCommonDoubleQuotedItem;
begin
  if not Assigned(FCommonDoubleQuotedItem) then
    FCommonDoubleQuotedItem := TTMSFNCDataGridDataFilterCommonDoubleQuotedItem.Create;

  Result := FCommonDoubleQuotedItem;
end;

function TTMSFNCDataGridDataFilter.GetCommonItem: TTMSFNCDataGridDataFilterCommonItem;
begin
  if not Assigned(FCommonItem) then
    FCommonItem := TTMSFNCDataGridDataFilterCommonItem.Create;

  Result := FCommonItem;
end;

function TTMSFNCDataGridDataFilter.GetItem(Index: Integer): TTMSFNCDataGridDataFilterData;
begin
  Result := TTMSFNCDataGridDataFilterData(inherited GetItem(Index));
end;

function TTMSFNCDataGridDataFilter.GetUniqueDoubleQuotedItem: TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem;
begin
  if not Assigned(FUniqueDoubleQuotedItem) then
    FUniqueDoubleQuotedItem := TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem.Create;

  Result := FUniqueDoubleQuotedItem;
end;

function TTMSFNCDataGridDataFilter.GetUniqueItem: TTMSFNCDataGridDataFilterUniqueItem;
begin
  if not Assigned(FUniqueItem) then
    FUniqueItem := TTMSFNCDataGridDataFilterUniqueItem.Create;

  Result := FUniqueItem;
end;

procedure TTMSFNCDataGridDataFilter.RemoveColumnFilter(AColumn: integer);
var
  i: Integer;
begin
  i := Count;
  while i > 0 do
  begin
    Dec(i);
    if Items[i].Column = AColumn then
      Delete(i);
  end;
end;

function TTMSFNCDataGridDataFilter.HasFilter(AColumn: integer): Boolean;
var
  i: Integer;
begin
  Result := False;
  for i := 1 to Count do
  begin
    if Items[i - 1].Column = AColumn then
    begin
      Result := True;
      Break;
    end;
  end;
end;

function TTMSFNCDataGridDataFilter.Insert(AIndex: Integer): TTMSFNCDataGridDataFilterData;
begin
  Result := TTMSFNCDataGridDataFilterData(inherited Insert(AIndex));
end;

function TTMSFNCDataGridDataFilter.Last: TTMSFNCDataGridDataFilterData;
begin
  Result := nil;
  if Count > 0 then
    Result := Items[Count - 1];
end;

procedure TTMSFNCDataGridDataFilter.Parse(AColumn: Integer; AFilterText: string);
var
  sl, sls: TStringList;
  I: Integer;
  f, fc: string;
  K: Integer;
  t: TTMSFNCDataGridDataFilterType;
begin
  Clear;
  sl := TStringList.Create;
  try
    TTMSFNCUtils.Split('|', AFilterText, sl);

    for I := 0 to sl.Count - 1 do
    begin
      f := sl[I];
      f := Trim(f);
      if f <> '' then
      begin
        sls := TStringList.Create;
        try
          TTMSFNCUtils.Split('&', f, sls);

          if sls.Count = 1 then
          begin
            StripFilterCondition(f, f, t);
            if (sl.Count > 0) then
              Add(AColumn, t, f, gfoOR)
            else
              Add(AColumn, t, f)
          end
          else
          begin
            for K := 0 to sls.Count - 1 do
            begin
              fc := sls[K];
              fc := Trim(fc);
              if fc <> '' then
              begin
                StripFilterCondition(fc, fc, t);
                if (sls.Count > 0) then
                  Add(AColumn, t, fc, gfoAND)
                else
                  Add(AColumn, t, fc)
              end;
            end;
          end;

        finally
          sls.Free;
        end;
      end;
    end;
  finally
    sl.Free;
  end;
end;

procedure TTMSFNCDataGridDataFilter.SetItem(Index: Integer; const Value: TTMSFNCDataGridDataFilterData);
begin
  inherited SetItem(Index, Value);
end;

{ TTMSFNCDataGridDataOptions }

procedure TTMSFNCDataGridDataOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataOptions then
  begin
    FSorting.Assign((Source as TTMSFNCDataGridDataOptions).Sorting);
    FIO.Assign((Source as TTMSFNCDataGridDataOptions).IO);
    FFiltering.Assign((Source as TTMSFNCDataGridDataOptions).Filtering);
    FGrouping.Assign((Source as TTMSFNCDataGridDataOptions).Grouping);
    FFind.Assign((Source as TTMSFNCDataGridDataOptions).Find);
  end;
end;

constructor TTMSFNCDataGridDataOptions.Create;
begin
  FIO := GetIOOptionsClass.Create;
  FSorting := TTMSFNCDataGridDataSortingOptions.Create;
  FSorting.OnChangeRebuild := DoSortingOptionsChange;
  FGrouping := TTMSFNCDataGridDataGroupingOptions.Create;
  FFiltering := TTMSFNCDataGridDataFilteringOptions.Create;
  FFiltering.OnChangeRebuild := DoFilteringOptionsChange;
  FFind := TTMSFNCDataGridDataFindOptions.Create;
  FCalculations := TTMSFNCDataGridDataCalculationOptions.Create;
  FURL := TTMSFNCDataGridDataURLOptions.Create;
  FURL.OnChangeRebuild := DoURLOptionsChange;
end;

function TTMSFNCDataGridDataOptions.GetIOOptionsClass: TTMSFNCDataGridDataIOOptionsClass;
begin
  Result := TTMSFNCDataGridDataIOOptions;
end;

destructor TTMSFNCDataGridDataOptions.Destroy;
begin
  FFind.Free;
  FIO.Free;
  FGrouping.Free;
  FFiltering.Free;
  FCalculations.Free;
  FSorting.Free;
  FURL.Free;
  inherited;
end;

procedure TTMSFNCDataGridDataOptions.DoFilteringOptionsChange(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridDataOptions.DoSortingOptionsChange(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridDataOptions.DoURLOptionsChange(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridDataOptions.SetCalculations(
  const Value: TTMSFNCDataGridDataCalculationOptions);
begin
  if FCalculations <> Value then
    FCalculations.Assign(Value);
end;

procedure TTMSFNCDataGridDataOptions.SetFiltering(
  const Value: TTMSFNCDataGridDataFilteringOptions);
begin
  if FFiltering <> Value then
    FFiltering.Assign(Value);
end;

procedure TTMSFNCDataGridDataOptions.SetFind(
  const Value: TTMSFNCDataGridDataFindOptions);
begin
  if FFind <> Value then
    FFind.Assign(Value);
end;

procedure TTMSFNCDataGridDataOptions.SetGrouping(
  const Value: TTMSFNCDataGridDataGroupingOptions);
begin
  if FGrouping <> Value then
    FGrouping.Assign(Value);
end;

procedure TTMSFNCDataGridDataOptions.SetIO(
  const Value: TTMSFNCDataGridDataIOOptions);
begin
  if FIO <> Value then
    FIO.Assign(Value)
end;

procedure TTMSFNCDataGridDataOptions.SetSorting(
  const Value: TTMSFNCDataGridDataSortingOptions);
begin
  if FSorting <> Value then
    FSorting.Assign(Value);
end;

procedure TTMSFNCDataGridDataOptions.SetURL(
  const Value: TTMSFNCDataGridDataURLOptions);
begin
  if FURL <> Value then
    FURL.Assign(Value);
end;

{ TTMSFNCDataGridDataIOOptions }

procedure TTMSFNCDataGridDataIOOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataIOOptions then
  begin
    FDelimiter := (Source as TTMSFNCDataGridDataIOOptions).FDelimiter;
    FMaxRows := (Source as TTMSFNCDataGridDataIOOptions).MaxRows;
    FStartColumn := (Source as TTMSFNCDataGridDataIOOptions).StartColumn;
    FStartRow := (Source as TTMSFNCDataGridDataIOOptions).StartRow;
    FAlwaysQuotes := (Source as TTMSFNCDataGridDataIOOptions).AlwaysQuotes;
    FQuoteEmptyCells := (Source as TTMSFNCDataGridDataIOOptions).QuoteEmptyCells;
    FOpenAfterCreation := (Source as TTMSFNCDataGridDataIOOptions).OpenAfterCreation;
    FTrimSpaces := (Source as TTMSFNCDataGridDataIOOptions).TrimSpaces;
  end;
end;

constructor TTMSFNCDataGridDataIOOptions.Create;
begin
  FDelimiter := #0;
  FMaxRows := -1;
  FStartRow := 0;
  FStartColumn := 0;
  FAlwaysQuotes := False;
  FQuoteEmptyCells := True;
  FOpenAfterCreation := True;
  FTrimSpaces := False;
end;

{ TTMSFNCDataGridDataSortingOptions }

procedure TTMSFNCDataGridDataSortingOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataSortingOptions then
  begin
    FDirection := (Source as TTMSFNCDataGridDataSortingOptions).Direction;
    FCaseSensitive := (Source as TTMSFNCDataGridDataSortingOptions).CaseSensitive;
    FFormat := (Source as TTMSFNCDataGridDataSortingOptions).Format;
    FRow := (Source as TTMSFNCDataGridDataSortingOptions).Row;
    FEnabled := (Source as TTMSFNCDataGridDataSortingOptions).Enabled;
  end;
end;

constructor TTMSFNCDataGridDataSortingOptions.Create;
begin
  FDirection := gsdAscending;
  FCaseSensitive := True;
  FFormat := gsfAutomatic;
  FRow := 0;
  FEnabled := False;
end;

procedure TTMSFNCDataGridDataSortingOptions.SetEnabled(const Value: Boolean);
begin
  if FEnabled <> Value then
  begin
    FEnabled := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridDataFilteringOptions }

procedure TTMSFNCDataGridDataFilteringOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataFilteringOptions then
  begin
    FRows := (Source as TTMSFNCDataGridDataFilteringOptions).Rows;
    FEnabled := (Source as TTMSFNCDataGridDataFilteringOptions).Enabled;
    FRow := (Source as TTMSFNCDataGridDataFilteringOptions).Row;
    FDropDownWidth := (Source as TTMSFNCDataGridDataFilteringOptions).DropDownWidth;
    FDropDownHeight := (Source as TTMSFNCDataGridDataFilteringOptions).DropDownHeight;
    FMultiColumn := (Source as TTMSFNCDataGridDataFilteringOptions).MultiColumn;
    FHideEmptyGroups := (Source as TTMSFNCDataGridDataFilteringOptions).HideEmptyGroups;
    FControls := (Source as TTMSFNCDataGridDataFilteringOptions).Controls;
  end;
end;

constructor TTMSFNCDataGridDataFilteringOptions.Create;
begin
  FRows := gfrNormal;
  FControls := [gfcButton];
  FEnabled := False;
  FRow := 0;
  FDropDownWidth := 200;
  FDropDownHeight := 280;
  FMultiColumn := False;
  FHideEmptyGroups := False;
end;

procedure TTMSFNCDataGridDataFilteringOptions.SetControls(
  const Value: TTMSFNCDataGridDataFilterControls);
begin
  if FControls <> Value then
  begin
    FControls := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridDataFilteringOptions.SetEnabled(const Value: Boolean);
begin
  if FEnabled <> Value then
  begin
    FEnabled := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridDataFilteringOptions.SetRow(const Value: Integer);
begin
  if FRow <> Value then
  begin
    FRow := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridDataGroupingOptions }

procedure TTMSFNCDataGridDataGroupingOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataGroupingOptions then
  begin
    FSummary := (Source as TTMSFNCDataGridDataGroupingOptions).Summary;
    FSummaryInHeader := (Source as TTMSFNCDataGridDataGroupingOptions).SummaryInHeader;
    FMergeSummary := (Source as TTMSFNCDataGridDataGroupingOptions).MergeSummary;
    FMergeHeader := (Source as TTMSFNCDataGridDataGroupingOptions).MergeHeader;
    FHideColumns := (Source as TTMSFNCDataGridDataGroupingOptions).HideColumns;
    FCalculationFormat := (Source as TTMSFNCDataGridDataGroupingOptions).CalculationFormat;
    FAutoSort := (Source as TTMSFNCDataGridDataGroupingOptions).AutoSort;
    FUseFirstRowAsHeader := (Source as TTMSFNCDataGridDataGroupingOptions).UseFirstRowAsHeader;
    FMergeDelimiter := (Source as TTMSFNCDataGridDataGroupingOptions).MergeDelimiter;
  end;
end;

constructor TTMSFNCDataGridDataGroupingOptions.Create;
begin
  FSummary := True;
  FMergeHeader := True;
  FMergeSummary := False;
  FSummaryInHeader := False;
  FHideColumns := False;
  FCalculationFormat := '%g';
  FAutoSort := True;
  FUseFirstRowAsHeader := True;
  FMergeDelimiter := ' ; ';
  FHeaderHeight := 30;
  FSummaryHeight := 30;
end;

function TTMSFNCDataGridDataGroupingOptions.IsCalculationFormatStored: Boolean;
begin
  Result := CalculationFormat <> '%g';
end;

function TTMSFNCDataGridDataGroupingOptions.IsHeaderHeightStored: Boolean;
begin
  Result := HeaderHeight <> 30;
end;

function TTMSFNCDataGridDataGroupingOptions.IsMergeDelimiterStored: Boolean;
begin
  Result := MergeDelimiter <> ' ; ';
end;

function TTMSFNCDataGridDataGroupingOptions.IsSummaryHeightStored: Boolean;
begin
  Result := SummaryHeight <> 30;
end;

{ TTMSFNCDataGridDataFindOptions }

procedure TTMSFNCDataGridDataFindOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataFindOptions then
  begin
    FRow := (Source as TTMSFNCDataGridDataFindOptions).Row;
    FColumn := (Source as TTMSFNCDataGridDataFindOptions).Column;
  end;
end;

constructor TTMSFNCDataGridDataFindOptions.Create;
begin
  FRow := 0;
  FColumn := 0;
end;

{$IFDEF WEBLIB}
function TJSNodeListHelper.GetCount: NativeInt;
begin
  Result := length;
end;

function TJSNodeListHelper.FindNode(ANodeName: string): TJSNode;
var
  Index: Integer;
begin
  Index := IndexOf(ANodeName);
  if Index >= 0 then
    Result := Nodes[Index]
  else
    Result := nil;
end;

function TJSNodeListHelper.IndexOf(const Name: string): NativeInt;
var
  n: TJSNode;
begin
  for Result := 0 to Count - 1 do
  begin
    n := Nodes[Result];
    if (n.nodeName = Name) then
      Exit;
  end;
  Result := -1;
end;

{$ENDIF}

{ TTMSFNCDataGridDataAdapter }

constructor TTMSFNCDataGridDataAdapter.Create(AOwner: TComponent);
begin
  inherited;
  FActive := True;
end;

constructor TTMSFNCDataGridDataAdapter.Create;
begin
  Create(nil);
end;

destructor TTMSFNCDataGridDataAdapter.Destroy;
begin
  if Assigned(FDataInstance) then
    FDataInstance.FAdapter := nil;

  FDataInstance := nil;
  inherited;
end;

function TTMSFNCDataGridDataAdapter.GetDataInstance: TTMSFNCDataGridData;
begin
  Result := FDataInstance;
end;

function TTMSFNCDataGridDataAdapter.GetInstance: NativeUInt;
begin
  Result := HInstance;
end;

procedure TTMSFNCDataGridDataAdapter.SetActive(const Value: boolean);
begin
  FActive := Value;
  if not Assigned(DataInstance) then
    Exit;
  Initialize;
end;

procedure TTMSFNCDataGridDataAdapter.SetDataInstance(const Value: TTMSFNCDataGridData);
begin
  if FDataInstance <> Value then
  begin
    FDataInstance := Value;
    if FBlockAdd then
      Exit;

    FBlockAdd := True;
    if Assigned(FDataInstance) then
    begin
      FDataInstance.Adapter := Self;
      Initialize;
    end;
    FBlockAdd := False;
  end;
end;

{ TTMSFNCDataGridDataCalculationOptions }

procedure TTMSFNCDataGridDataCalculationOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataCalculationOptions then
  begin
    FIncludeHiddenRows := (Source as TTMSFNCDataGridDataCalculationOptions).IncludeHiddenRows;
    FIncludeHiddenColumns := (Source as TTMSFNCDataGridDataCalculationOptions).IncludeHiddenColumns;
  end;
end;

constructor TTMSFNCDataGridDataCalculationOptions.Create;
begin
  FIncludeHiddenRows := True;
  FIncludeHiddenColumns := True;
end;

{ TTMSFNCDataGridDataFilterItem }

constructor TTMSFNCDataGridDataFilterItem.Create;
begin
  FDoubleQuotes := False;
  FPartOfCheckAll := False;
end;

{ TTMSFNCDataGridDataFilterCommonItem }

constructor TTMSFNCDataGridDataFilterCommonItem.Create;
begin
  inherited;
  FDoubleQuotes := False;
  FPartOfCheckAll := True;
end;

{ TTMSFNCDataGridDataFilterUniqueItem }

constructor TTMSFNCDataGridDataFilterUniqueItem.Create;
begin
  inherited;
  FPartOfCheckAll := False;
  FDoubleQuotes := False;
end;

{ TTMSFNCDataGridDataFilterCommonDoubleQuotedItem }

constructor TTMSFNCDataGridDataFilterCommonDoubleQuotedItem.Create;
begin
  inherited;
  FDoubleQuotes := True;
  FPartOfCheckAll := True;
end;

{ TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem }

constructor TTMSFNCDataGridDataFilterUniqueDoubleQuotedItem.Create;
begin
  inherited;
  FDoubleQuotes := True;
  FPartOfCheckAll := False;
end;

{ TTMSFNCDataGridDataURLOptions }

procedure TTMSFNCDataGridDataURLOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridDataURLOptions then
  begin
    FAutoDetect := (Source as TTMSFNCDataGridDataURLOptions).AutoDetect;
    FAutoOpen := (Source as TTMSFNCDataGridDataURLOptions).AutoOpen;
  end;
end;

constructor TTMSFNCDataGridDataURLOptions.Create;
begin
  FAutoDetect := False;
  FAutoOpen := True;
end;

procedure TTMSFNCDataGridDataURLOptions.SetAutoDetect(const Value: Boolean);
begin
  if FAutoDetect <> Value then
  begin
    FAutoDetect := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridDataTempData }

constructor TTMSFNCDataGridDataTempData.Create(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellItem);
begin
  FCell := ACell;
  FData := AData;
end;

destructor TTMSFNCDataGridDataTempData.Destroy;
begin
  FData := nil;
  FCell := MakeCell(-1, -1);
  inherited;
end;

{ TTMSFNCDataGridDataTempDataList }

function TTMSFNCDataGridDataTempDataList.Find(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellItem;
var
  I: Integer;
begin
  Result := nil;
  for I := 0 to Count - 1 do
  begin
    if Items[I].Cell.IsEqualTo(ACell) then
    begin
      Result := Items[I].Data;
      Break;
    end;
  end;
end;

end.
